import { oneFortyEighthEn, oneFortyEighthPtBr } from '@/lib/i18n/assets-one-forty-eighth';

/**
 * Assets module copy (ALC-1680, ADR 0003 ticket K).
 *
 * The norm references are deliberately in the copy rather than in the code. A controller
 * reading "36 meses (CPC 27)" beside "60 meses (IN RFB 1.700/2017)" can see why the two réguas
 * disagree without leaving the screen, which is the whole point of showing them side by side.
 */
export const assetsPtBr = {
  intangibleArt42: {
    title: 'Exclusão do art. 42 (Lei 12.973)',
    label: 'Excluir o custo de desenvolvimento na competência do gasto',
    help:
      'Ligado, o custo capitalizado de cada projeto desta empresa é excluído do lucro real no ' +
      'mês em que foi gasto, e o ativo nasce com base fiscal zero. Desligado (padrão), o custo ' +
      'é amortizado no livro fiscal como em qualquer outro bem. Empresa com prejuízo costuma ' +
      'não ganhar nada excluindo hoje: o prejuízo fiscal só compensa 30% do lucro de cada ano ' +
      'seguinte — a decisão é do contador.',
    reasonLabel: 'Motivo da mudança',
    reasonPlaceholder: 'Por que o regime desta empresa está mudando?',
    notRetroactive:
      'A mudança vale só para projetos criados a partir de agora. Projetos em andamento ' +
      'mantêm a resposta que congelaram na criação — um regime novo não reinterpreta meses que ' +
      'um projeto já fechou.',
    historyTitle: 'Histórico',
    historyEntry: 'De {{from}} para {{to}} em {{at}}',
    value: { true: 'ligado', false: 'desligado' },
    saved: 'Configuração do art. 42 salva.',
    saving: 'Salvando…',
    saveFailed: 'Não foi possível salvar a configuração do art. 42.',
  },
  oneFortyEighth: oneFortyEighthPtBr,
  common: {
    yes: 'Sim',
    no: 'Não',
    all: 'Todos',
    none: '—',
    entity: 'Entidade',
    activeFilters: 'Filtros ativos',
    book: 'Livro',
    clearFilters: 'Limpar filtros',
    search: 'Buscar',
    truncated: 'Mostrando os primeiros {{limit}} resultados. Refine os filtros para ver o resto.',
    loadError: 'Não foi possível carregar os dados.',
    loading: 'Carregando…',
    memoBook: 'Livro memo',
    memoBookHint: 'Mensura sem lançar no razão — a régua fiscal é extra-contábil.',
    postedBook: 'Livro contábil',
    warnings: 'Avisos',
    apply: 'Aplicar',
    clear: 'Limpar',
    filterColumn: 'Filtrar {{column}}',
    pagination: {
      showing: 'Mostrando {{start}}–{{end}} de {{total}}',
      noResults: 'Nenhum resultado',
      previous: 'Anterior',
      next: 'Próxima',
      page: '{{current}} de {{total}}',
    },
  },

  queue: {
    title: 'Fila de revisão de ativos',
    description:
      'Compras classificadas como candidatas a imobilizado. Cada linha mostra a regra que ' +
      'decidiu e o número que a acionou.',
    empty: 'Nenhum candidato nesta fila.',
    emptyPending:
      'Nada aguardando revisão. As classificações de contas a pagar alimentam esta fila.',
    tabs: {
      needsDecision: 'Decisão sua',
      hasVerdict: 'Temos um veredito',
      decided: 'Já decididos',
    },
    needsDecisionHint:
      'O motor não escolheu entre um ativo e vários: unitizar depois é impossível, porque um ' +
      'bem agregado não pode ser baixado individualmente.',
    hasVerdictHint:
      'O motor propôs um veredito. Confirmar mantém a proposta; mudar registra uma exceção.',
    columns: {
      source: 'Origem',
      line: 'Linha da conta a pagar',
      proposal: 'Proposta',
      drivenBy: 'O que decidiu',
      amount: 'Valor da linha',
      threshold: 'Limite aplicado',
      unitization: 'Unitização',
      // ALC-3487 — só aparece quando alguma linha da página aponta para um projeto.
      project: 'Projeto',
      status: 'Status',
      decidedAt: 'Decidido em',
    },
    source: {
      billReference: 'Conta {{reference}}',
      currentHint:
        'Identificação atual da conta de origem; os valores da decisão permanecem congelados.',
      lineUnavailable: 'Linha de origem indisponível ({{id}})',
      open: 'Ver origem',
    },
    drivenBy: {
      rule: 'Regra aprendida',
      threshold: 'Limite de capitalização',
      ruleAndThreshold: 'Regra + limite',
      unitAmount: '{{unit}} por unidade × {{quantity}}',
      overThreshold: '{{amount}} acima do limite de {{threshold}}',
      underThreshold: '{{amount}} abaixo do limite de {{threshold}}',
      thresholdSnapshot:
        'Limite congelado na proposta — mudanças de política depois não alteram esta decisão.',
      noRule: 'Nenhuma regra aprendida disparou.',
    },
    verdict: {
      CAPITALIZE: 'Capitalizar',
      EXPENSE: 'Despesa',
      // O motor também reconhece a linha como arrendamento: não é imobilizado nem despesa
      // simples, e a decisão passa pelo cadastro de contrato (CPC 06 R2).
      LEASE_SERVICE: 'Arrendamento',
      LEASE_ASSESSMENT_REQUIRED: 'Avaliar como arrendamento',
      // ADR 0017 D3: a linha é custo de um intangível construído. Não vira um bem próprio —
      // entra no projeto, que se torna um único ativo no go-live.
      ADD_TO_PROJECT: 'Custo de projeto intangível',
      UNDECIDED: 'Sem proposta',
    },
    status: {
      PENDING: 'Pendente',
      CONFIRMED: 'Confirmado',
      OVERRIDDEN: 'Alterado',
      DISCARDED: 'Descartado',
    },
    unitization: {
      PER_UNIT: 'Um ativo por unidade',
      AGGREGATE: 'Um ativo único',
      ASK: 'Requer decisão',
    },
    decide: {
      action: 'Decidir',
      title: 'Decidir candidato',
      description: 'A decisão vale para esta linha e cria os ativos na hora, quando capitalizada.',
      verdict: 'Veredito',
      class: 'Classe de ativo',
      classPlaceholder: 'Manter a classe proposta',
      unitization: 'Unitização',
      unitizationRequired: 'Escolha obrigatória: o motor não tem um padrão seguro aqui.',
      assetCount: 'Quantidade de ativos',
      project: 'Projeto de desenvolvimento',
      projectPlaceholder: 'Selecione o projeto',
      projectHint:
        'Só projetos abertos da empresa desta conta. O débito vai para "Intangível em ' +
        'andamento" se o gate de desenvolvimento já estiver aberto na competência; caso ' +
        'contrário permanece em despesa e fica vinculado ao projeto apenas para informação ' +
        '(CPC 04 §54 e §57).',
      projectNoneOpen:
        'Nenhum projeto aberto nesta empresa. Abra um projeto de desenvolvimento antes de ' +
        'encaminhar custo a ele.',
      note: 'Justificativa',
      notePlaceholder: 'Por que esta decisão (opcional).',
      promoteToRule: 'Aprender como regra',
      promoteToRuleHint: 'A regra aprendida propõe, nunca confirma sozinha.',
      submit: 'Registrar decisão',
      cancel: 'Cancelar',
      close: 'Fechar',
      successCapitalized: 'Capitalizado. {{count}} ativo(s) criado(s).',
      successExpensed: 'Linha mantida como despesa.',
      successAddedToProject: 'Linha encaminhada ao projeto de desenvolvimento.',
      error: 'Não foi possível registrar a decisão.',
      creditPolicy: {
        title: 'Apropriação dos créditos de PIS e COFINS',
        description:
          'Defina uma única forma legal para os dois tributos. Esta decisão é gravada nos ativos criados e não altera compras comuns.',
        pis: 'PIS a apropriar',
        cofins: 'COFINS a apropriar',
        billIssueDate: 'Emissão da conta (referência)',
        billCompetencyDate: 'Competência da conta (referência)',
        acquisitionDate: 'Data de aquisição ou recebimento',
        acquisitionDateHint:
          'A emissão é preenchida apenas como sugestão. Confirme a data real de aquisição ou recebimento; a competência contábil não substitui essa decisão.',
        acquisitionDateConfirm:
          'Confirmo que esta é a data real de aquisição ou recebimento do bem',
        accountingInService: {
          title: 'Disponível para uso — CPC 27',
          description:
            'Confirme separadamente quando o bem ficou disponível para o uso pretendido. Neste fluxo, a data deve permanecer na mesma competência contábil da conta.',
          date: 'Data em que o bem ficou disponível para uso',
          evidenceCode: 'Código da evidência de disponibilidade',
          evidenceDescription: 'Descrição da evidência de disponibilidade',
          confirm: 'Confirmo esta data e sua evidência contábil',
        },
        notAvailable: 'Não informada',
        method: 'Forma de apropriação',
        select: 'Selecione',
        methods: {
          DEPRECIATION_RFB: 'Conforme depreciação fiscal da RFB',
          QUOTA_1_48: 'Em 48 parcelas mensais',
          IMMEDIATE_NEW_MACHINE_EQUIPMENT: 'Imediata para máquina ou equipamento novo',
        },
        commonImmediateUnsupported:
          'Para uso comum, a parcela legalmente elegível é definida pelo rateio mensal; a apropriação imediata não está disponível.',
        fortyEightUnsupported:
          'A apropriação em 1/48 ainda não é suportada neste fluxo e não será substituída por outra opção.',
        destination: 'Destinação do bem',
        destinations: {
          PRODUCTION_GOODS: 'Produção de bens',
          SERVICE_PROVISION: 'Prestação de serviços',
          RENTAL_THIRD_PARTY: 'Locação a terceiros',
        },
        origin: 'Origem da aquisição',
        origins: {
          DOMESTIC: 'Mercado interno',
          IMPORT: 'Importação',
        },
        condition: 'Condição do bem',
        conditions: {
          NEW: 'Novo',
          USED: 'Usado',
        },
        usedUnsupported:
          'A apropriação deste crédito para bem usado ainda não é suportada. A decisão não pode ser concluída neste fluxo.',
        evidenceCode: 'Código da evidência',
        evidenceDescription: 'Descrição da evidência',
        recoverability: {
          title: 'Recuperabilidade do potencial de PIS/COFINS',
          description:
            'Registre uma conclusão contábil independente sobre a realização integral do potencial diferido.',
          commonDescription:
            'Registre uma conclusão contábil independente sobre a realização da parcela que vier a ser legalmente admitida após o rateio de uso comum.',
          conclusion: 'Confirmo que a recuperação integral do potencial de PIS e COFINS é esperada',
          conclusionHint:
            'Esta conclusão reconhece a expectativa de realização do ativo fiscal; ela não decorre apenas da elegibilidade legal do crédito.',
          commonConclusion:
            'Confirmo que a recuperação integral da parcela legalmente admitida após o rateio de uso comum é esperada',
          commonConclusionHint:
            'O rateio de uso comum e a parcela legalmente excluída serão apurados por competência. Essa exclusão de elegibilidade não é perda de recuperabilidade.',
          evidenceCode: 'Referência da projeção ou política contábil',
          evidenceDescription: 'Fundamento da expectativa de recuperação',
          evidenceHint:
            'Cite a projeção, orçamento aprovado ou política contábil que sustenta a recuperação integral. Não repita somente a base legal de elegibilidade.',
          commonEvidenceHint:
            'Cite a projeção, orçamento aprovado ou política contábil que sustenta a recuperação integral da parcela legalmente admitida após o rateio. Não trate a parcela legalmente excluída como perda de recuperabilidade.',
        },
        firstUse: {
          title: 'Primeira utilização fiscal',
          description:
            'Este fluxo capitaliza um bem já disponível para uso. Registre agora a data e a evidência fiscal somente se elas já estiverem confirmadas.',
          alreadyInUse: 'Registrar agora a data da primeira utilização fiscal',
          deferredHint:
            'Se não registrar agora, o crédito permanece diferido até a confirmação posterior no ativo; nenhuma parcela fiscal é gerada antes disso.',
          date: 'Data da primeira utilização',
          evidenceCode: 'Código da evidência da primeira utilização',
          evidenceDescription: 'Descrição da evidência da primeira utilização',
        },
      },
    },
    errors: {
      ASSET_STATUS_CONCURRENT_CHANGE:
        'Outra pessoa alterou o status deste bem enquanto você trabalhava, e a operação foi ' +
        'desfeita. Atualize a página para ver o estado atual e tente novamente.',
      FIXED_ASSET_CLASS_FRAMEWORK_MISMATCH:
        'A classe do bem pertence a um padrão contábil diferente do dos livros desta entidade. ' +
        'Escolha uma classe do mesmo padrão contábil da entidade ou ajuste o cadastro da classe.',
      FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
        'Créditos de PIS/COFINS do imobilizado estão disponíveis somente para entidades brasileiras. Revise a entidade do ativo e a origem fiscal.',
      FIXED_ASSET_PIS_COFINS_CLASS_REQUIRED:
        'Selecione uma classe de ativo ativa e compatível com o bem antes de registrar a apropriação fiscal.',
      FIXED_ASSET_PIS_COFINS_CREDIT_EVIDENCE_REQUIRED:
        'Informe a referência e a descrição da evidência que sustenta a forma de apropriação de PIS e COFINS.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_HUMAN_REQUIRED:
        'Este bem tem crédito de PIS/COFINS e precisa de revisão humana. Abra o candidato e confirme a forma de apropriação fiscal.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_NOT_APPLICABLE:
        'A decisão fiscal informada não se aplica a este candidato. Atualize a conta de origem e revise o tipo de crédito antes de continuar.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_REQUIRED:
        'Defina a forma de apropriação dos créditos de PIS e COFINS e registre a evidência no candidato do ativo. A capitalização não escolhe esse método automaticamente.',
      FIXED_ASSET_PIS_COFINS_LATE_CAPITALIZATION_UNSUPPORTED:
        'Esta conta já foi contabilizada. A definição do crédito fiscal do imobilizado após a contabilização exige uma reclassificação ainda indisponível neste fluxo. Os lançamentos existentes foram preservados.',
      FIXED_ASSET_PIS_COFINS_RECOVERABILITY_REQUIRED:
        'Confirme se a recuperação integral do potencial de PIS/COFINS é esperada e informe a projeção ou política contábil que sustenta essa conclusão.',
      FIXED_ASSET_PIS_COFINS_RECOVERABILITY_NOT_APPLICABLE:
        'A conclusão de recuperabilidade se aplica somente à apropriação conforme a depreciação fiscal da RFB. Revise a forma de apropriação selecionada.',
      FIXED_ASSET_PIS_COFINS_FIRST_USE_EVIDENCE_REQUIRED:
        'Revise a data e a evidência de primeira utilização do imobilizado. A data não pode anteceder a aquisição nem estar no futuro; não use uma data estimada como utilização já ocorrida.',
      FIXED_ASSET_PIS_COFINS_FIRST_USE_BEFORE_IN_SERVICE:
        'A primeira utilização fiscal registrada antecede a entrada em serviço do ativo. Revise a data e a decisão fiscal do bem; não altere datas silenciosamente apenas para liberar a apropriação.',
      FIXED_ASSET_PIS_COFINS_ACQUISITION_DATE_REQUIRED:
        'Confirme a data real de aquisição ou recebimento do bem. A emissão da conta é apenas uma referência e a competência contábil não substitui essa decisão.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_REQUIRED:
        'Confirme quando o bem ficou disponível para uso e informe a evidência contábil correspondente. Esta data é distinta da aquisição e da primeira utilização fiscal.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_PERIOD_UNSUPPORTED:
        'Neste fluxo, a disponibilidade para uso deve permanecer na mesma competência contábil da conta de origem. Revise a data real; não altere a evidência apenas para contornar o bloqueio.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_IMMUTABLE:
        'A disponibilidade para uso já foi confirmada com outra data ou evidência. Atualize o ativo e confira o registro existente antes de continuar.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_RACED:
        'O ativo ou sua decisão contábil mudou durante a confirmação. Atualize a página, confira o registro atual e tente novamente.',
      FIXED_ASSET_PIS_COFINS_ACQUISITION_AFTER_IN_SERVICE:
        'A aquisição ou recebimento não pode ocorrer depois de o bem ficar disponível para uso. Revise as duas datas e suas evidências; não as ajuste silenciosamente para liberar o fluxo.',
      FIXED_ASSET_PIS_COFINS_IMMEDIATE_METHOD_INELIGIBLE:
        'O bem não atende aos requisitos da opção imediata informada. Revise a natureza de máquina/equipamento novo, a aquisição e a destinação à produção ou prestação de serviços. Não selecione outro método apenas para contornar o bloqueio.',
      FIXED_ASSET_PIS_COFINS_IMPORT_EVIDENCE_REQUIRED:
        'O crédito deste bem importado exige a base e as contribuições efetivamente recolhidas na importação. Esse tratamento ainda não está disponível neste fluxo; não substitua a origem por aquisição nacional.',
      FIXED_ASSET_PIS_COFINS_OWNER_LINKAGE_REQUIRED:
        'Não foi possível vincular com segurança o crédito ao bem e à linha da compra. Revise a origem na conta a pagar e no candidato antes de continuar.',
      FIXED_ASSET_PIS_COFINS_OWNER_TYPE_MISMATCH:
        'A natureza do crédito informada na conta não corresponde ao ativo vinculado. Revise a classificação da compra e o vínculo com o ativo antes de continuar.',
      FIXED_ASSET_PIS_COFINS_REVENUE_USE_UNSUPPORTED:
        'A destinação informada para este imobilizado ainda não tem tratamento completo neste fluxo. Preserve a classificação real da compra e mantenha o candidato pendente; não altere a destinação apenas para liberar o crédito.',
      FIXED_ASSET_PIS_COFINS_RFB_RULE_REQUIRED:
        'Configure uma única regra fiscal válida para a classe no livro fiscal da entidade, com vida útil, taxa anual e referência normativa coerentes. A regra societária não substitui a fiscal.',
      FIXED_ASSET_PIS_COFINS_SOURCE_LINE_REQUIRED:
        'O crédito do imobilizado precisa de um item persistido na conta a pagar. Cadastre e classifique o item da compra antes de decidir o candidato.',
      FIXED_ASSET_PIS_COFINS_USED_ASSET_UNSUPPORTED:
        'A análise fiscal de bens usados ainda não está disponível neste fluxo. Preserve a condição real do bem; esta limitação do sistema não significa que todo bem usado seja inelegível.',
      ASSET_CANDIDATE_ALREADY_DECIDED:
        'Este candidato já foi decidido. Atualize a fila para ver a decisão registrada.',
      ASSET_CANDIDATE_SOURCE_FACTS_CHANGED:
        'A origem deste candidato mudou depois da análise. Mantenha-o pendente e atualize a classificação da compra para gerar um novo candidato com os dados atuais.',
      ASSET_CANDIDATE_ASSET_COUNT_REQUIRED:
        'Capitalizar por unidade exige um número inteiro de unidades. Informe a quantidade de ' +
        'ativos, ou escolha criar um ativo único para esta linha.',
      ASSET_CANDIDATE_AUTO_CONFIRM_ON_POSTED_BILL:
        'A conta a pagar desta linha já está contabilizada, e capitalizar agora exige um ' +
        'lançamento — que precisa de um autor. Uma regra aprendida não confirma sozinha um ' +
        'documento já lançado; decida este candidato manualmente.',
      ASSET_CANDIDATE_CLASS_REQUIRED:
        'Selecione a classe de ativo: sem ela o bem ficaria sem régua de depreciação e sem ' +
        'contas mapeadas.',
      ASSET_CANDIDATE_FX_BASIS_MISSING:
        'A conta a pagar está em {{originalCurrencyCode}} e não tem taxa de câmbio de provisão ' +
        'registrada, então não há base para medir o bem em {{functionalCurrencyCode}}. Nada foi ' +
        'gravado. Registre a taxa da nota — a mesma que provisionou o passivo — e classifique a ' +
        'conta de novo para o candidato ser levantado com o valor já convertido.',
      ASSET_CANDIDATE_LINE_ALREADY_CAPITALIZED:
        'Esta linha da conta a pagar já foi capitalizada no bem {{assetCode}}, e decidir este candidato ' +
        'agora criaria uma duplicidade. O candidato ficou obsoleto — normalmente após uma ' +
        'mudança na política de capitalização — e será removido ao reclassificar esta conta. Se ' +
        'o bem existente estiver errado, corrija-o primeiro.',
      ASSET_CANDIDATE_UNITIZATION_REQUIRED:
        'Escolha a unitização: diga se a linha vira um ativo único ou um ativo por unidade. Um ' +
        'bem agregado não pode ser baixado individualmente depois, então não há padrão seguro a ' +
        'assumir.',
    },
  },

  list: {
    title: 'Ativos imobilizados',
    description: 'Imobilizado e direito de uso, com as duas réguas de depreciação.',
    empty: 'Nenhum ativo encontrado.',
    searchPlaceholder: 'Código, descrição ou número de série',
    codePlaceholder: 'Código do ativo',
    descriptionPlaceholder: 'Descrição do ativo',
    classPlaceholder: 'Nome da classe',
    custodianPlaceholder: 'Nome do responsável',
    acquisitionPlaceholder: 'Selecione o período',
    costMin: 'Custo mínimo',
    costMax: 'Custo máximo',
    filters: {
      class: 'Classe',
      status: 'Status',
      source: 'Origem',
      custodian: 'Responsável',
      costCenter: 'Centro de custo',
    },
    columns: {
      source: 'Origem',
      code: 'Código',
      description: 'Descrição',
      class: 'Classe',
      acquisition: 'Aquisição',
      cost: 'Custo',
      custodian: 'Responsável',
      books: 'Valor contábil por livro',
    },
    rightOfUse: 'Direito de uso',
    overdueInstallments_one: '{{count}} parcela vencida sem lançar',
    overdueInstallments_other: '{{count}} parcelas vencidas sem lançar',
    openAsset: 'Abrir {{code}} — {{description}}',
  },

  status: {
    DRAFT: 'Rascunho',
    IN_SERVICE: 'Em uso',
    IN_MAINTENANCE: 'Em manutenção',
    // Ocioso continua depreciando (CPC 27 §55) e pode ser revisado (CPC 27 §51).
    IDLE: 'Ocioso',
    DISPOSED: 'Baixado',
  },

  source: {
    AP_BILL: 'Conta a pagar',
    LEASE_ROU: 'Direito de uso',
    LEASEHOLD_IMPROVEMENT: 'Benfeitoria',
    MIGRATION: 'Migração',
    MANUAL: 'Manual',
  },

  detail: {
    pisCofinsRecoverability: {
      action: 'Confirmar recuperabilidade',
      review: 'Revisar dados',
      title: 'Recuperabilidade de PIS/COFINS — {{code}}',
      description:
        'Registre a expectativa contábil de realização do potencial diferido antes de reconhecer o ativo fiscal.',
      commonDescription:
        'Registre a expectativa contábil de realização da parcela legalmente admitida após o rateio de uso comum antes de reconhecer o ativo fiscal.',
      conclusion: 'Confirmo que a recuperação integral do potencial de PIS e COFINS é esperada',
      commonConclusion:
        'Confirmo que a recuperação integral da parcela legalmente admitida após o rateio de uso comum é esperada',
      conclusionHint:
        'Esta conclusão é separada da elegibilidade legal do crédito e não contabiliza a compra.',
      commonConclusionHint:
        'O rateio e a parcela legalmente excluída são decisões de elegibilidade por competência; essa exclusão não é perda de recuperabilidade.',
      evidenceCode: 'Referência da projeção ou política contábil',
      evidenceDescription: 'Fundamento da expectativa de recuperação',
      evidenceHint:
        'Cite a projeção, orçamento aprovado ou política contábil que sustenta a recuperação integral. Não repita somente a base legal de elegibilidade.',
      commonEvidenceHint:
        'Cite a projeção, orçamento aprovado ou política contábil que sustenta a recuperação integral da parcela legalmente admitida após o rateio. Não trate a parcela legalmente excluída como perda de recuperabilidade.',
      confirm: 'Confirmar recuperabilidade',
      cancel: 'Cancelar',
      success:
        'Recuperabilidade registrada. Retorne à conta de origem e tente a aprovação novamente.',
      required: 'Confirme a conclusão contábil e informe a referência e o fundamento da evidência.',
      recordedConclusion: 'Conclusão contábil',
      fullPotentialExpected: 'Recuperação integral esperada',
      classifiedAt: 'Confirmada em',
      errors: {
        FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
          'Créditos de PIS/COFINS do imobilizado estão disponíveis somente para entidades brasileiras. Revise a entidade do ativo e a origem fiscal.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_REQUIRED:
          'Confirme a conclusão contábil e informe a projeção ou política que sustenta a recuperação integral.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_NOT_APPLICABLE:
          'Este ativo não utiliza apropriação conforme a depreciação fiscal da RFB. Atualize o ativo e revise a decisão fiscal registrada.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_IMMUTABLE:
          'A recuperabilidade já foi confirmada com outra evidência. Atualize o ativo e confira o registro existente antes de continuar.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_RACED:
          'O ativo ou sua decisão fiscal mudou durante a confirmação. Atualize a página, confira o registro atual e tente novamente.',
      },
    },
    pisCofinsAllowance: {
      action: 'Revisar recuperabilidade',
      title: 'Revisar recuperabilidade — {{code}}',
      description:
        'Defina o novo valor contábil esperado separadamente para PIS e COFINS. A revisão altera somente a perda contábil estimada; a face legal e as quotas permanecem intactas.',
      method: 'Forma de apropriação fiscal',
      effectiveDate: 'Data efetiva',
      carryingValue: 'Novo valor contábil esperado — {{tax}}',
      currentCarryingValue: 'Valor contábil atual: {{value}}',
      evidenceCode: 'Referência da projeção ou política contábil',
      evidenceDescription: 'Fundamento da nova expectativa de recuperação',
      preview: 'Visualizar prévia',
      confirm: 'Confirmar revisão',
      cancel: 'Cancelar',
      close: 'Fechar',
      previewRequired:
        'Preencha a decisão e gere a prévia contábil antes de confirmar. Nenhum lançamento foi criado.',
      previewWarning:
        'Revise as parcelas, a variação da perda e a conta contábil abaixo. A confirmação grava a perda estimada e o lançamento contábil de forma atômica.',
      success: 'Revisão registrada em {{count}} evento(s).',
      previewTitle: 'Prévia contábil por tributo',
      accountingContext: 'Livro {{book}} · período {{period}} · conta de perda {{account}}',
      tax: 'Tributo',
      available: 'Disponível',
      deferred: 'A apropriar',
      allowance: 'Perda acumulada',
      allowanceDelta: 'Variação da perda',
      newCarrying: 'Novo valor contábil',
      direction: 'Movimento',
      directions: {
        INCREASE: 'Aumento da perda',
        REVERSAL: 'Reversão',
        UNCHANGED: 'Sem alteração',
      },
      allocations: 'Alocações afetadas',
      ownerTypes: { INSTALLMENT: 'A apropriar', ENTITLEMENT: 'Disponível' },
      effectiveSummary:
        'Data efetiva: {{date}}. O servidor aplica a revisão às parcelas mais recentes primeiro.',
      resultTitle: 'Posição após a revisão',
      resultTax: 'Valor contábil de {{tax}}',
      viewJournal: 'Ver lançamento contábil {{number}}',
      position: 'Recuperabilidade — {{tax}}',
      positionValue: 'Valor contábil {{carrying}} · perda acumulada {{allowance}}',
      eligibleBasis: 'Valor bruto de origem congelado',
      nextLegalInstallment: 'Próxima quota legal — {{tax}}',
      nextLegalInstallmentValue: '{{value}} · competência {{competence}}',
      errors: {
        FA_PIS_COFINS_RECOVERABILITY_LOSS_REQUEST_INVALID:
          'Revise a data, os valores contábeis e a evidência informada.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_UNSUPPORTED:
          'Este ativo não pode receber uma revisão de recuperabilidade no estado ou método atual.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_CURRENCY_UNSUPPORTED:
          'A revisão exige fonte e livro estatutário em BRL. Preserve a moeda real e encaminhe o ativo para revisão.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_POST_2026_UNSUPPORTED:
          'A data efetiva informada é a partir de 2027 e ainda não é suportada neste fluxo. Preserve a data real; quotas futuras da cauda não são, por si só, um bloqueio.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_MAPPING_REQUIRED:
          'Configure a conta de perda de recuperabilidade de PIS/COFINS no livro estatutário antes de continuar.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_PREVIEW_STALE:
          'Os saldos mudaram após a prévia. Gere uma nova prévia e revise os valores antes de confirmar.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_EFFECTIVE_DATE_BEFORE_LATEST_MOVEMENT:
          'Escolha uma data efetiva igual ou posterior a {{latestLedgerDate}}, data da movimentação mais recente do crédito.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_PERIOD_NOT_OPEN:
          'O período da data efetiva não está aberto. Preserve a data real e regularize o período contábil.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_HISTORICAL_JOURNAL_INVALID:
          'O histórico contábil do crédito não reconcilia com a fonte. Solicite revisão antes de registrar nova perda.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_BALANCE_INVALID:
          'O novo valor excede a face legal elegível ou não reconcilia com a perda acumulada. Revise os valores.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_ALLOWANCE_REVERSAL_REQUIRED:
          'Há utilização coberta por perda acumulada. Reverta a perda correspondente antes de consumir o crédito.',
      },
    },
    pisCofinsAccountingInService: {
      action: 'Confirmar disponibilidade para uso',
      review: 'Revisar dados',
      title: 'Disponível para uso — {{code}}',
      description:
        'Registre quando o bem ficou disponível para o uso pretendido. Esta confirmação contábil é separada da aquisição e da primeira utilização fiscal.',
      confirm: 'Confirmar disponibilidade',
      cancel: 'Cancelar',
      success:
        'Disponibilidade para uso registrada. Retorne à conta de origem e tente a aprovação novamente.',
      required: 'Informe uma data válida, o código e a descrição da evidência.',
      immutable:
        'O registro é definitivo para esta capitalização e não contabiliza a conta automaticamente.',
      date: 'Data disponível para uso',
      evidenceCode: 'Código da evidência',
      evidenceDescription: 'Descrição da evidência',
    },
    pisCofinsFirstUse: {
      action: 'Registrar primeira utilização',
      review: 'Revisar dados',
      title: 'Primeira utilização — {{code}}',
      description:
        'Registre quando o equipamento começou a ser utilizado. A data e a evidência iniciam a programação fiscal dos créditos de PIS/COFINS.',
      confirm: 'Registrar utilização',
      cancel: 'Cancelar',
      success:
        'Primeira utilização registrada. Atualize as fontes da apuração do período e revise a parcela disponível antes de aprovar.',
      required:
        'Informe uma data a partir da mais recente entre aquisição e entrada em serviço, até hoje, além do código e da descrição da evidência.',
      immutable:
        'O registro é definitivo para esta decisão fiscal. Ele não antecipa parcelas futuras nem registra pagamento ao fornecedor.',
      date: 'Data da primeira utilização',
      evidenceCode: 'Código da evidência',
      evidenceDescription: 'Descrição da evidência',
      sectionTitle: 'Apropriação de PIS/COFINS',
      awaitingEvidence:
        'O crédito permanece a apropriar até que a primeira utilização e sua evidência sejam registradas.',
      awaitingProvision:
        'A primeira utilização fiscal depende de o ativo estar em serviço e de a provisão da compra estar contabilizada. Confira a conta de origem.',
      errors: {
        FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
          'Créditos de PIS/COFINS do imobilizado estão disponíveis somente para entidades brasileiras. Revise a entidade do ativo e a origem fiscal.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_NOT_APPLICABLE:
          'Esta forma de apropriação não utiliza ativação por primeira utilização. Confira a decisão fiscal registrada no ativo.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_ASSET_NOT_IN_SERVICE:
          'O ativo ainda não está em serviço. Conclua essa etapa antes de registrar a primeira utilização fiscal.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_IMMUTABLE:
          'A primeira utilização já foi registrada com outra data ou evidência. Atualize o ativo e confira o registro existente.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_SCHEDULE_MISSING:
          'Há uma primeira utilização registrada sem programação fiscal correspondente. Solicite a reconciliação desse ativo antes de continuar.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_SCHEDULE_CONFLICT:
          'Já existe programação fiscal para este ativo. Atualize e confira o histórico antes de registrar outra primeira utilização.',
        FIXED_ASSET_PIS_COFINS_SOURCE_REQUIRED:
          'A provisão fiscal da compra ainda não foi registrada. Confira e aprove a conta de origem antes de continuar.',
      },
    },
    backToList: 'Voltar para ativos',
    notFound: 'Ativo não encontrado.',
    sections: {
      identification: 'Identificação',
      dimensions: 'Dimensões gerenciais',
      provenance: 'Origem',
      books: 'Dupla régua',
      // Um único livro não é uma "dupla" de nada — o cabeçalho nomeia o próprio livro em vez de
      // prometer uma comparação que não existe (ALC-2978 bug 033).
      booksSingle: 'Régua — {{book}}',
      schedule: 'Parcelas de depreciação',
      custody: 'Histórico de custódia',
      disposal: 'Baixa',
    },
    fields: {
      serialNumber: 'Número de série',
      acquisitionDate: 'Data de aquisição',
      inServiceDate: 'Em serviço desde',
      availableForUseDate: 'Disponível para uso em',
      availableForUseDraftHint:
        'A disponibilidade foi confirmada, mas o ativo permanece em rascunho. A provisão e as réguas só são contabilizadas quando a conta de origem for aprovada.',
      acquisitionCost: 'Custo de aquisição',
      unitQuantity: 'Unidades',
      costCenter: 'Centro de custo',
      department: 'Departamento',
      managerialAccount: 'Conta gerencial',
      custodian: 'Responsável',
      location: 'Localização',
      vendor: 'Fornecedor',
      sourceBill: 'Conta a pagar de origem',
      leaseContract: 'Contrato de arrendamento',
      notes: 'Observações',
    },
    books: {
      title: 'Dupla régua',
      description:
        'Uma coluna por livro. A diferença entre a depreciação acumulada societária e a fiscal ' +
        'é exatamente a diferença temporária que a Parte A do e-Lalur reporta.',
      // Mesmo tom do formulário de cadastro (`register.temporaryDifference.singleBook`): um
      // único livro não tem o que diferenciar, e dizer isso é melhor do que silenciar a seção
      // do e-Lalur sem explicação (ALC-2978 bug 033).
      singleBookDescription:
        'Um único livro mede este ativo ({{book}}) — nenhuma diferença societária × fiscal a ' +
        'apurar.',
      // "Restante" tem que ser o restante de verdade, não o que a FixedAssetBookRule congelou no
      // último evento que fixou a régua (cadastro, migração ou revisão). Rotular sem o "restante"
      // fazia o leitor calcular uma taxa mensal maior do que a real; rotular *com* "restante"
      // sobre o valor congelado fazia o leitor calcular uma taxa menor e contradizia, três linhas
      // abaixo, o "{{settled}} de {{total}} parcelas" da mesma tela. `AssetBookLadder` agora
      // recalcula os dois números a partir de campos já presentes na view — parcelas totais menos
      // lançadas, e base depreciável menos depreciação acumulada — em vez de exibir o congelado
      // (ALC-2978 bug 050, corrige o rótulo do bug 030). O valor congelado continua no wire como
      // `usefulLifeMonths` para quem precisar da régua original (047, ainda em aberto).
      usefulLife: 'Vida útil restante',
      months: '{{count}} meses',
      method: 'Método',
      residualRate: 'Valor residual',
      depreciableBase: 'Base depreciável restante',
      depreciableBaseAtDisposal: 'Base depreciável na data da baixa',
      accumulated: 'Depreciação acumulada',
      netBookValue: 'Valor contábil líquido',
      netBookValueAtDisposal: 'Valor contábil líquido na data da baixa',
      disposalSnapshot: 'Posição histórica usada na baixa de {{date}}. O ativo já foi baixado.',
      progress: '{{settled}} de {{total}} parcelas',
      period: 'Período',
      estimatedUsefulLifePeriod: 'Vida útil estimada',
      depreciationSuspended: 'Depreciação suspensa por valor residual',
      lastSettled: 'Última competência',
      termCapped: 'Prazo limitado pelo contrato de arrendamento',
      norm: 'Norma',
      overrideReason: 'Motivo da vida útil ajustada',
      difference: 'Diferença societária × fiscal',
      differenceHint:
        'Societária menos fiscal. Positiva significa que a régua societária já consumiu mais ' +
        'custo do que a fiscal.',
      // A régua fiscal (memo) calcula a vida inteira no momento em que existe — todas as
      // parcelas nascem `COMPUTED` de uma vez — enquanto a societária (lançada) só avança uma
      // competência por vez, e só quando alguém roda a depreciação. Por isso o resumo de cada
      // livro ("última competência liquidada") não serve para decidir se os dois estão
      // comparáveis: o memo sempre aparenta estar anos à frente. A comparação de verdade lê,
      // parcela a parcela, o saldo de cada livro na competência mais recente que o livro mais
      // lento já alcançou — e recusa o número, nomeando o livro atrasado, quando um dos dois
      // ainda não liquidou nada. Melhor recusar do que publicar um sinal invertido na Parte A do
      // e-Lalur (ALC-2978 bug 049, corrigido de novo após uma primeira versão que ainda usava o
      // resumo por livro e nunca convergia para um livro memo).
      differenceMisaligned:
        'O livro {{book}} ainda não liquidou nenhuma parcela. Rode a depreciação para comparar.',
      differenceUnavailable:
        'Diferença não disponível sem as parcelas de depreciação de cada livro.',
      noBooks: 'Nenhuma régua criada para este ativo.',
      overdue: '{{count}} parcela(s) vencida(s) ainda não lançada(s).',
    },
    // A baixa recusa lançar sem isto — antes desta tela, `runDepreciation` não tinha nenhum
    // chamador em `apps/web`, e a mensagem de recusa apontava para uma ação que não existia em
    // lugar nenhum da aplicação, bloqueando toda baixa permanentemente (ALC-2978 bug 039).
    depreciation: {
      action: 'Rodar depreciação',
      dialog: {
        title: 'Rodar depreciação — {{code}}',
        titleBook: 'Rodar depreciação — {{code}} ({{book}})',
        description:
          'Lança toda parcela pendente até a competência informada, inclusive. Livros memo ' +
          'nunca são lançados — eles só medem.',
        monthLabel: 'Competência (até)',
        monthFutureError:
          'A competência não pode ser futura — nenhuma parcela vencida está numa competência ' +
          'que ainda não chegou.',
        run: 'Rodar depreciação',
        running: 'Rodando…',
        runAgain: 'Rodar outra competência',
        close: 'Fechar',
        success: '{{count}} parcela(s) lançada(s).',
        summary: {
          total: 'Parcelas',
          posted: 'Lançadas',
          failed: 'Falharam',
          empty: 'Nenhuma parcela pendente até essa competência.',
          failedSectionTitle: 'Parcelas que falharam',
          errorCodeFallback: 'Falha não mapeada ({{code}}).',
          errorCodes: {
            FIXED_ASSET_DEPRECIATION_BOOK_NOT_POSTABLE:
              'O livro não mantém razão para a entidade deste ativo — não há onde lançar.',
            FIXED_ASSET_DEPRECIATION_CURRENCY_UNSUPPORTED:
              'A moeda deste ativo diverge da moeda funcional do livro — depreciação em moeda ' +
              'estrangeira ainda não é suportada.',
            FIXED_ASSET_SCHEDULE_ALREADY_POSTED:
              'Esta parcela já foi lançada por outra execução simultânea. Nenhuma ação é ' +
              'necessária.',
            FIXED_ASSET_CLASS_NOT_FOUND:
              'A classe deste ativo não existe mais. Corrija o cadastro do ativo antes de rodar.',
            SYSTEM_ACCOUNT_SLOT_NOT_FOUND:
              'A configuração de contas do sistema está incompleta para essa contrapartida. ' +
              'Fale com a Alchemy.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_UNRESOLVED:
              'A classe deste ativo não tem uma conta de depreciação acumulada mapeada. Corrija ' +
              'o mapeamento da classe antes de rodar.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
              'A conta de depreciação acumulada mapeada para a classe não é de natureza credora. ' +
              'Corrija o mapeamento antes de rodar.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
              'A conta de depreciação acumulada mapeada para a classe não é uma conta de ativo. ' +
              'Corrija o mapeamento antes de rodar.',
            FIXED_ASSET_DEPRECIATION_EXPENSE_ACCOUNT_UNRESOLVED:
              'A classe deste ativo não tem uma conta de despesa de depreciação mapeada. ' +
              'Corrija o mapeamento da classe antes de rodar.',
            FIXED_ASSET_DEPRECIATION_EXPENSE_ACCOUNT_TYPE_INVALID:
              'A conta de despesa de depreciação mapeada para a classe não é do tipo esperado ' +
              '(custo ou despesa, devedora). Corrija o mapeamento antes de rodar.',
            NO_OPEN_PERIOD:
              'O período contábil dessa competência está fechado neste livro. Reabra o período ' +
              'ou rode uma competência anterior.',
            FIXED_ASSET_NOT_DEPRECIABLE:
              'O status deste ativo não permite depreciar. A depreciação roda somente enquanto o ' +
              'bem está em uso, em manutenção ou ocioso.',
            FIXED_ASSET_SCHEDULE_BOOK_IS_MEMO:
              'Este livro é memo: ele mede sem lançar, então as parcelas dele terminam em ' +
              'Calculada e são lidas por relatório (ADR 0003, Decisão 1).',
            FIXED_ASSET_SCHEDULE_NOT_FOUND:
              'Esta parcela não existe mais. Atualize a página para ver a régua atual.',
            FIXED_ASSET_SCHEDULE_NOT_PENDING:
              'Esta parcela não está pendente, e só uma parcela pendente pode ser lançada. Ela ' +
              'já foi lançada, cancelada ou substituída por uma revisão.',
            UNKNOWN: 'Falha inesperada ao lançar esta parcela.',
          },
        },
      },
      errors: {
        FIXED_ASSET_DEPRECIATION_BAD_MONTH:
          'A competência informada não é válida. Use o formato AAAA-MM.',
      },
    },
    schedule: {
      description:
        'Uma linha por competência, lado a lado por livro — a mesma competência pode estar ' +
        'lançada num livro e vencida no outro.',
      empty: 'Nenhuma parcela gerada.',
      columns: {
        competency: 'Competência',
        installment: 'Parcela',
        amount: 'Valor',
        accumulated: 'Acumulada',
        netBookValue: 'Saldo contábil',
        status: 'Status',
      },
      status: {
        PENDING: 'Pendente',
        POSTED: 'Lançada',
        REVERSED: 'Revertida',
        CANCELLED: 'Cancelada',
        COMPUTED: 'Calculada (memo)',
        ROUNDED_ZERO: 'Sem lançamento (arredondamento)',
      },
      roundedZeroDescription:
        'A parcela ficou zerada na precisão da moeda e não exige lançamento contábil. O valor residual de arredondamento permanece na última parcela, sem alterar a vida útil do ativo.',
      overdueTitle: 'Vencida e ainda não lançada',
      noRows: 'Nenhum livro tem parcelas para este ativo.',
      cancelledByRevision:
        '{{count}} parcela(s) do livro {{bookCode}} cancelada(s) pela revisão de {{date}} não ' +
        'aparecem nesta tabela — veja o Histórico de custódia.',
      errors: {
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'Este bem é um direito de uso cujo livro tem valor residual, e um direito de uso é ' +
          'consumido integralmente ao longo do prazo do arrendamento. Zere o valor residual da ' +
          'regra do livro na classe e tente novamente.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          'A moeda funcional deste livro não tem 2 casas decimais (ou o livro não tem moeda ' +
          'funcional para a entidade do bem), e o módulo de ativos só mede moedas com 2 casas. ' +
          'Registre este bem num livro em moeda com 2 casas decimais.',
        FIXED_ASSET_SCHEDULE_NOT_POSTED:
          'Só uma parcela lançada pode ser estornada. Esta parcela está pendente, cancelada ou ' +
          'já estornada.',
        FIXED_ASSET_SCHEDULE_REVERSAL_AMBIGUOUS:
          'Esta parcela aponta para mais de um grupo de lançamentos, ou para nenhum, e o estorno ' +
          'espera exatamente um. Fale com a Alchemy antes de tentar de novo.',
      },
    },
    custody: {
      description:
        'Trilha append-only. O ativo carrega onde o bem está hoje; esta lista carrega como ele ' +
        'chegou lá.',
      empty: 'Nenhuma movimentação registrada.',
      columns: {
        date: 'Data',
        change: 'Mudança',
        from: 'De',
        to: 'Para',
        reason: 'Motivo',
        registeredBy: 'Registrado por',
      },
      kinds: {
        custodian: 'Responsável',
        costCenter: 'Centro de custo',
        location: 'Localização',
        status: 'Status',
      },
      unassigned: 'Sem responsável',
    },
    disposal: {
      action: 'Baixar ativo',
      type: 'Tipo de baixa',
      date: 'Data',
      proceeds: 'Valor recebido',
      cost: 'Custos da baixa',
      resultPerBook: 'Resultado por livro',
      resultHint:
        'O ganho ou a perda difere entre as réguas porque a depreciação acumulada divergiu no ' +
        'caminho até aqui. No livro estatutário, o resultado também inclui a perda líquida dos créditos de PIS/COFINS indicada abaixo.',
      gain: 'Ganho',
      loss: 'Perda',
      extinguishedLiability: 'Passivo de arrendamento extinto',
      taxCreditNetLoss: 'Perda líquida dos créditos de PIS/COFINS',
      assetJournals: 'Lançamentos da baixa do ativo',
      pisCofinsJournals: 'Lançamentos dos créditos de PIS/COFINS',
      viewAssetJournal: 'Ver lançamento do livro {{book}}',
      viewPisCofinsJournal: 'Ver lançamento fiscal {{number}}',
      types: {
        SALE: 'Venda',
        WRITE_OFF: 'Baixa por obsolescência',
        LOSS: 'Perda',
        DONATION: 'Doação',
        LEASE_RETURN: 'Devolução ao arrendador',
      },
      dialog: {
        title: 'Baixar {{code}}',
        description:
          'A baixa derecognece o bem em cada livro que o mede: cada livro contábil ganha seu ' +
          'próprio lançamento, e cada livro memo só registra o resultado, sem lançar.',
        typePlaceholder: 'Selecione o tipo',
        proceedsAccount: 'Conta de recebimento',
        proceedsAccountPlaceholder: 'Necessária quando há valor recebido',
        costAccount: 'Conta de crédito dos custos',
        costAccountPlaceholder: 'Necessária quando há custo de baixa',
        documentReference: 'Documento (NF de venda, laudo, termo de doação)',
        reason: 'Motivo',
        reasonPlaceholder: 'Contexto da baixa (opcional).',
        preview: 'Visualizar prévia',
        previewTitle: 'Prévia autoritativa por livro',
        previewRequired:
          'Gere a prévia autoritativa antes de confirmar a baixa. Nenhum lançamento foi criado.',
        previewWarning:
          'Revise o resultado por livro e a cauda fiscal. A confirmação baixa o ativo e os créditos na mesma transação.',
        resultBeforeTaxCredit: 'Resultado antes do crédito fiscal',
        taxTailTitle: 'Cauda legal de PIS/COFINS',
        taxTailContext: 'Livro {{book}} · período {{period}} · resultado da baixa {{account}}',
        tax: 'Tributo',
        maturedTarget: 'Meta maturada',
        accountedMatured: 'Maturado contabilizado',
        preservedCurrentPeriod: 'Quota do período preservada',
        legalTail: 'Face legal baixada',
        allowanceApplied: 'Perda acumulada aplicada',
        netTaxLoss: 'Perda fiscal líquida',
        taxLegalFaceDerecognized: 'Face legal de PIS/COFINS baixada',
        estimateTitle: 'Estimativa por livro',
        estimateHint:
          'Calculada aqui no navegador a partir do saldo contábil atual de cada livro — ' +
          '(valor recebido − custos − saldo contábil). É uma prévia, não o resultado que o ' +
          'servidor vai gravar: o servidor também soma o passivo de arrendamento extinto quando ' +
          'a baixa é uma devolução ao arrendador.',
        submit: 'Confirmar baixa',
        cancel: 'Cancelar',
        close: 'Fechar',
        success: 'Ativo baixado.',
        resultTitle: 'Resultado registrado',
        cancelledSchedules: '{{count}} parcela(s) futura(s) cancelada(s) pela baixa.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Ativo não encontrado. Atualize a página e tente novamente.',
        FIXED_ASSET_NOT_DISPOSABLE:
          'Este ativo não pode ser baixado no status atual. A baixa derecognece um custo que ' +
          'está no balanço, então só é possível enquanto o ativo está em uso, em manutenção ou ' +
          'ocioso.',
        FIXED_ASSET_DISPOSAL_NO_BOOKS:
          'Nenhum livro mede este ativo, então não há régua para derecognecer. Verifique a ' +
          'configuração de livros do ativo.',
        FIXED_ASSET_DISPOSAL_DEPRECIATION_PENDING:
          'Um livro contábil tem parcela de depreciação vencida (competência até a data da ' +
          'baixa) que ainda não foi lançada. Rode a depreciação até essa competência antes de ' +
          'baixar.',
        FIXED_ASSET_DISPOSAL_DEPRECIATION_POSTED_AFTER:
          'Um livro já tem depreciação lançada em competências posteriores à data da baixa. ' +
          'Reverta esses lançamentos antes de baixar.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_CREDIT_WRITE_OFF_REQUIRED:
          'Este ativo ainda possui potencial diferido de PIS/COFINS. A baixa com esse saldo ainda não é suportada: mantenha o bem ativo até existir um fluxo específico de reversão fiscal.',
        FIXED_ASSET_1_48_DISPOSAL_EVENT_UNSUPPORTED:
          'Para créditos em 1/48, este fluxo de baixa atende venda e doação. O evento selecionado ainda precisa de tratamento fiscal específico.',
        FIXED_ASSET_COMMON_USE_DISPOSAL_NOT_SUPPORTED:
          'Para ativos com créditos rateados entre receitas, este fluxo atende venda ou doação até dezembro de 2026. Preserve o tipo e a data reais do evento; outros casos exigem tratamento fiscal ainda não disponível.',
        FIXED_ASSET_COMMON_USE_DISPOSAL_SCHEDULE_STATE_CHANGED:
          'As parcelas de depreciação mudaram durante a baixa. Gere uma nova prévia e confira as parcelas antes de confirmar.',
        FIXED_ASSET_1_48_DISPOSAL_INPUT_INVALID:
          'A data da baixa e a opção em 1/48 não são compatíveis. Confira o início da opção e a data do evento antes de continuar.',
        FIXED_ASSET_1_48_DISPOSAL_BALANCE_INVALID:
          'As parcelas e perdas do crédito em 1/48 não conciliam para esta baixa. Confira a apropriação das competências anteriores e seus ajustes de recuperabilidade.',
        FIXED_ASSET_1_48_DISPOSAL_FUTURE_MATURATION_CONFLICT:
          'Há parcelas apropriadas após o mês da baixa. Reconcilie essas competências antes de baixar o ativo.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_PREVIEW_REQUIRED:
          'Gere e revise a prévia fiscal antes de confirmar a baixa.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_PREVIEW_STALE:
          'A cauda fiscal mudou após a prévia. Gere uma nova prévia antes de confirmar.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_POST_2026_UNSUPPORTED:
          'A baixa com data a partir de janeiro de 2027 exige o tratamento de transição para CBS, ainda não disponível neste fluxo.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_FUTURE_MATURATION_CONFLICT:
          'Há maturação incompatível após o corte da baixa. Reconcilie as competências fiscais antes de continuar.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_HISTORICAL_JOURNAL_INVALID:
          'O histórico contábil do crédito não reconcilia com a fonte. Solicite revisão antes da baixa.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_REQUIRED:
          'Informe a conta de recebimento: há valor recebido e o lançamento precisa debitar ' +
          'caixa, banco ou contas a receber.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_UNEXPECTED:
          'Remova a conta de recebimento: não há valor recebido para lançar nela.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_REQUIRED:
          'Informe a conta de crédito dos custos da baixa: há valor de custo e o lançamento ' +
          'precisa de uma contrapartida.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_UNEXPECTED:
          'Remova a conta de custos da baixa: não há valor de custo para lançar nela.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_TYPE_INVALID:
          'A conta de recebimento selecionada não é do tipo esperado (caixa, banco ou contas a ' +
          'receber). Selecione outra conta.',
        FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
          'A conta de depreciação acumulada mapeada para a classe não é de natureza credora. ' +
          'Corrija o mapeamento da classe e tente novamente.',
        FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
          'A conta de depreciação acumulada mapeada para a classe não é uma conta de ativo. ' +
          'Corrija o mapeamento da classe e tente novamente.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_TYPE_INVALID:
          'A conta de custos selecionada não é do tipo esperado para essa contrapartida. ' +
          'Selecione outra conta.',
        FIXED_ASSET_DISPOSAL_COUNTER_ACCOUNT_IS_SUBLEDGER:
          'A conta selecionada pertence a um subledger de cliente ou fornecedor e não pode ' +
          'receber o lançamento da baixa diretamente. Selecione uma conta que não seja de ' +
          'subledger.',
        FIXED_ASSET_DISPOSAL_BOOK_NOT_POSTABLE:
          'Um dos livros que mede este ativo não mantém razão para a entidade dele. Fale com a ' +
          'Alchemy antes de baixar.',
        FIXED_ASSET_DISPOSAL_CURRENCY_UNSUPPORTED:
          'A moeda deste ativo diverge da moeda funcional de um dos livros que o mede — baixa de ' +
          'ativo em moeda estrangeira ainda não é suportada.',
        FIXED_ASSET_DISPOSAL_LEASE_RETURN_NOT_RIGHT_OF_USE:
          'Devolução ao arrendador só se aplica a um ativo de direito de uso. Escolha outro tipo ' +
          'de baixa para este ativo.',
        FIXED_ASSET_DISPOSAL_LEASE_LIABILITY_OUTSTANDING:
          'O contrato de arrendamento deste direito de uso ainda tem parcelas de passivo em ' +
          'aberto. Quite ou remensure o passivo antes de devolver o bem ao arrendador.',
        FIXED_ASSET_DISPOSAL_RESULT_ACCOUNT_UNRESOLVED:
          'A classe deste ativo aponta o resultado da baixa para uma conta que não existe mais. ' +
          'Corrija o mapeamento da classe antes de baixar.',
        FIXED_ASSET_DISPOSAL_RESULT_ACCOUNT_TYPE_INVALID:
          'A classe deste ativo aponta o resultado da baixa para uma conta que não é de ' +
          'resultado (DRE). Corrija o mapeamento da classe antes de baixar.',
        ASSET_STATUS_CONCURRENT_CHANGE:
          'Outra pessoa alterou o status deste bem enquanto você trabalhava, e a operação foi ' +
          'desfeita. Atualize a página para ver o estado atual e tente novamente.',
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'Este bem é um direito de uso cujo livro tem valor residual, e um direito de uso é ' +
          'consumido integralmente ao longo do prazo do arrendamento. Zere o valor residual da ' +
          'regra do livro na classe e tente novamente.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          'A moeda funcional deste livro não tem 2 casas decimais (ou o livro não tem moeda ' +
          'funcional para a entidade do bem), e o módulo de ativos só mede moedas com 2 casas. ' +
          'Registre este bem num livro em moeda com 2 casas decimais.',
        FIXED_ASSET_DISPOSAL_BAD_DATE: 'A data da baixa é inválida.',
        FIXED_ASSET_ALREADY_DISPOSED:
          'Este ativo já tem uma baixa registrada. Baixa parcial está fora de escopo, então um ' +
          'bem é derecognecido uma única vez — esta tentativa foi desfeita. Atualize a página ' +
          'para ver a baixa existente.',
        NO_OPEN_PERIOD:
          'O período contábil da data da baixa está fechado em algum livro. Escolha uma data dentro ' +
          'de um período aberto, ou reabra o período antes de baixar.',
      },
    },
    revision: {
      action: 'Revisar régua',
      dialog: {
        title: 'Revisar régua de {{code}}',
        description:
          'Revisão de vida útil, valor residual ou método (CPC 27 §51/§61) — uma mudança de ' +
          'estimativa contábil (CPC 23): vale só a partir da data de vigência, nunca ' +
          'retroativamente. Toda parcela já lançada — ou, num livro memo, já decorrida — ' +
          'permanece exatamente como está. O livro fiscal (TAX_BASIS) nunca é alterado aqui: ' +
          'sua vida útil segue o Anexo III da IN RFB 1.700/2017, não o julgamento da entidade.',
        effectiveDate: 'Data de vigência',
        reason: 'Motivo',
        reasonPlaceholder: 'Base da revisão — obrigatório, é a trilha de auditoria do julgamento.',
        usefulLifeMonths: 'Nova vida útil remanescente (meses)',
        usefulLifeMonthsHint: 'Contada a partir da data de vigência, não da vida útil total.',
        residualRate: 'Novo valor residual (%)',
        residualRatePlaceholder: 'Deixe em branco para manter o valor atual',
        method: 'Método',
        methodHint: 'Somente STRAIGHT_LINE está implementado.',
        submit: 'Revisar',
        cancel: 'Cancelar',
        close: 'Fechar',
        success: 'Régua revisada.',
        resultTitle: 'Revisão aplicada',
        bookSummary:
          '{{preserved}} preservada(s), {{cancelled}} substituída(s), {{regenerated}} nova(s).',
        suspendedSummary:
          '{{preserved}} preservada(s), {{cancelled}} cancelada(s). Nenhuma parcela de valor zero foi criada; a vida útil estimada segue até {{estimatedEndDate}}.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Ativo não encontrado. Atualize a página e tente novamente.',
        FIXED_ASSET_REVISION_NOT_IN_SERVICE:
          'Este ativo não pode ser revisado no status atual. A revisão altera a régua de um bem ' +
          'já em uso, então só é possível enquanto o ativo está em serviço, em manutenção ou ocioso.',
        FIXED_ASSET_HAS_NO_BOOK_RULE: 'Nenhum livro mede este ativo — não há régua para revisar.',
        FIXED_ASSET_REVISION_METHOD_NOT_IMPLEMENTED:
          'Somente o método linear (STRAIGHT_LINE) está implementado.',
        FIXED_ASSET_REVISION_RESIDUAL_INVALID:
          'O novo valor residual deve ser uma fração entre 0 e 1 (por exemplo, 0.10 para 10%).',
        FIXED_ASSET_REVISION_RESIDUAL_EXCEEDS_CARRYING_AMOUNT:
          'O valor residual estimado é igual ou superior ao saldo contábil. A depreciação deve ser zero nessa condição, ainda não suportada por este fluxo. Nenhuma revisão foi salva; preserve a estimativa real e encaminhe o caso para revisão.',
        FIXED_ASSET_COMMON_USE_REVISION_PERIOD_REQUIRED:
          'Não há período contábil para a competência da revisão em um dos livros. Crie e abra o período da entidade e do livro antes de revisar a régua.',
        FIXED_ASSET_COMMON_USE_REVISION_PERIOD_AMBIGUOUS:
          'Há períodos contábeis sobrepostos para a competência da revisão. Corrija as datas dos períodos da entidade e do livro antes de revisar a régua.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_DATE_INVALID:
          'A data de vigência da revisão ou do histórico é inválida. Confira a data informada e as revisões anteriores.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_BEFORE_OWNER:
          'A revisão não pode começar antes do ajuste de custo por rateio vigente. Confira a data do ajuste e informe uma vigência compatível.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_DATE_REGRESSION:
          'A revisão não pode começar antes de uma revisão já registrada. Confira o histórico e use uma vigência igual ou posterior à última revisão.',
        FIXED_ASSET_COMMON_USE_REVISION_ASSET_SCOPE_INVALID:
          'A revisão não identifica o ativo corretamente. Reabra o ativo e tente novamente.',
        FIXED_ASSET_COMMON_USE_REVISION_AMOUNT_INVALID:
          'Os valores do custo ou das parcelas são inválidos. Nenhuma revisão foi salva; confira o custo e as parcelas do ativo.',
        FIXED_ASSET_COMMON_USE_REVISION_OWNER_CHAIN_INVALID:
          'O histórico de ajustes do custo por rateio está incompleto ou divergente. Confira os ajustes vinculados ao ativo antes de revisar.',
        FIXED_ASSET_COMMON_USE_REVISION_OWNER_COST_DIVERGENT:
          'O custo atual difere do último ajuste de rateio aprovado. Confira o custo e seus lançamentos antes de revisar.',
        FIXED_ASSET_COMMON_USE_REVISION_COMMAND_CHAIN_INVALID:
          'O histórico das revisões de vida útil está incompleto ou divergente. Encaminhe o histórico do ativo para revisão.',
        FIXED_ASSET_COMMON_USE_REVISION_COMMAND_OWNER_INVALID:
          'Uma revisão anterior não corresponde ao histórico de ajustes do custo. Confira os vínculos entre as revisões e os ajustes do ativo.',
        FIXED_ASSET_COMMON_USE_REVISION_BOOK_SCOPE_INVALID:
          'Os livros da revisão estão ausentes ou duplicados. Confira os livros e suas réguas antes de revisar.',
        FIXED_ASSET_COMMON_USE_REVISION_SCHEDULE_LINEAGE_INVALID:
          'As parcelas da revisão não correspondem ao ativo e livro esperados. Confira o histórico das réguas; nenhuma revisão foi salva.',
        FIXED_ASSET_COMMON_USE_REVISION_SCHEDULE_STATUS_INVALID:
          'As parcelas mudaram de situação e não podem ser substituídas por esta revisão. Atualize o ativo e confira os lançamentos antes de tentar novamente.',
        FIXED_ASSET_COMMON_USE_REVISION_BOOK_RECONCILIATION_INVALID:
          'A depreciação preservada e as novas parcelas não conciliam com a base revisada. Confira os valores da régua; nenhuma revisão foi salva.',
        FIXED_ASSET_REVISION_ZERO_DEPRECIATION_UNSUPPORTED:
          'O valor residual estimado é igual ou superior ao saldo contábil. A depreciação deve ser zero nessa condição, ainda não suportada por este fluxo. Nenhuma revisão foi salva; preserve a estimativa real e encaminhe o caso para revisão.',
        FIXED_ASSET_REVISION_BEFORE_LAST_POSTED:
          'A data de vigência precisa ser posterior à última parcela já contabilizada nesse ' +
          'livro. Esta revisão de estimativa tem efeito prospectivo e preserva os lançamentos anteriores.',
        FIXED_ASSET_REVISION_BEFORE_LAST_COMPUTED:
          'A data de vigência precisa ser posterior à última competência já decorrida nesse ' +
          'livro memo. Esta revisão de estimativa tem efeito prospectivo e preserva as parcelas anteriores.',
        FIXED_ASSET_REVISION_PERIOD_CLOSED:
          'Uma competência que a revisão substituiria está fechada ou bloqueada em um dos livros. ' +
          'Confira os períodos afetados e escolha uma vigência que preserve as competências fechadas, ou siga o fluxo de reabertura antes de revisar.',
        FIXED_ASSET_REVISION_PERIOD_REQUIRED:
          'Falta um período contábil para uma competência decorrida que a revisão substituiria. Configure o período no livro correto antes de revisar; nenhuma alteração foi salva.',
        FIXED_ASSET_REVISION_PERIOD_AMBIGUOUS:
          'Há períodos contábeis sobrepostos em uma competência que a revisão substituiria. Corrija a sobreposição no livro antes de revisar; nenhuma alteração foi salva.',
        FIXED_ASSET_REVISION_SCOPE_CHANGED_RETRY:
          'A entidade ou os livros do ativo mudaram durante a revisão. Nada foi salvo. Atualize a página, confira os dados e tente novamente.',
        FIXED_ASSET_REVISION_NO_REVISABLE_BOOK:
          'Nenhum livro deste ativo pode ser revisado: todos são fiscais (TAX_BASIS) ou não têm ' +
          'régua ativa. Nada foi alterado.',
        FIXED_ASSET_REVISION_SCHEDULE_RACED:
          'Este livro foi alterado por outra operação (outra revisão ou o fechamento da ' +
          'depreciação) enquanto esta revisão era processada. Nada foi salvo; refaça a revisão.',
        FIXED_ASSET_REVISION_REVERSED_INSTALLMENT_IN_TAIL:
          'Este livro tem uma parcela estornada (REVERSED) dentro do período que a revisão ' +
          'substituiria. Reposte ou cancele essa parcela antes de revisar, ou escolha uma data ' +
          'de vigência posterior a ela.',
        FIXED_ASSET_REVISION_BAD_DATE: 'A data de vigência é inválida.',
        FIXED_ASSET_USEFUL_LIFE_INVALID:
          'A vida útil remanescente deve ser um número inteiro de meses maior que zero.',
        FIXED_ASSET_STARTING_INSTALLMENT_INVALID:
          'Não foi possível continuar a numeração das parcelas.',
        FIXED_ASSET_SCHEDULE_BAD_DATE: 'A data de vigência é inválida.',
        FIXED_ASSET_DEPRECIABLE_BASE_INVALID: 'A base depreciável resultante é inválida.',
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'Este bem é um direito de uso cujo livro tem valor residual, e um direito de uso é ' +
          'consumido integralmente ao longo do prazo do arrendamento. Zere o valor residual da ' +
          'regra do livro na classe e tente novamente.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          'A moeda funcional deste livro não tem 2 casas decimais (ou o livro não tem moeda ' +
          'funcional para a entidade do bem), e o módulo de ativos só mede moedas com 2 casas. ' +
          'Registre este bem num livro em moeda com 2 casas decimais.',
        FIXED_ASSET_REVISION_REVERSED_INSTALLMENT_BEFORE_EFFECTIVE:
          'Este livro tem uma parcela estornada (REVERSED) anterior à data de vigência, sem ' +
          'lançamento ativo que a tenha reposto. O estorno devolveu a despesa e a depreciação ' +
          'acumulada ao razão, então o valor contábil na data de vigência depende de essa ' +
          'parcela ser reposta, cancelada ou corrigida — revisar antes disso deixaria saldo no ' +
          'ativo ao fim da vida revisada. Reposte ou cancele essa parcela antes de revisar.',
      },
      warnings: {
        FIXED_ASSET_REVISION_BOOK_NOT_DEPRECIATING:
          'Um livro mede este ativo sem régua ativa de depreciação — não havia nada para revisar ' +
          'nele.',
        FIXED_ASSET_REVISION_FISCAL_BOOK_EXCLUDED:
          'O livro fiscal não foi alterado: a vida útil dele segue o Anexo III da IN RFB ' +
          '1.700/2017, e não o julgamento da entidade (CPC 27 §51). As parcelas desse livro ' +
          'permanecem exatamente como estavam.',
      },
    },
    transfer: {
      action: 'Transferir de Em Andamento',
      dialog: {
        title: 'Transferir {{code}} para a classe final',
        description:
          'Move o bem para fora de Imobilizado em Andamento (CPC 27 §55): a régua é gerada a ' +
          'partir da data em serviço informada, nunca a partir da data de aquisição.',
        targetClass: 'Classe de destino',
        targetClassPlaceholder: 'Selecione a classe final',
        inServiceDate: 'Disponível para uso em',
        reason: 'Motivo',
        reasonPlaceholder: 'Contexto da transferência (opcional).',
        submit: 'Transferir',
        cancel: 'Cancelar',
        close: 'Fechar',
        success: 'Ativo transferido.',
        resultTitle: 'Transferência concluída',
        booksGenerated: '{{count}} livro(s) com régua gerada a partir de {{date}}.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Ativo não encontrado. Atualize a página e tente novamente.',
        ASSET_STATUS_CONCURRENT_CHANGE:
          'Outra pessoa alterou o status deste bem enquanto você trabalhava, e a operação foi ' +
          'desfeita. Atualize a página para ver o estado atual e tente novamente.',
        FIXED_ASSET_TRANSFER_STATUS_INVALID:
          'Este ativo não está com o custo no razão (rascunho ou baixado) e não pode ser ' +
          'transferido para fora de Imobilizado em Andamento.',
        FIXED_ASSET_TRANSFER_NOT_IN_PROGRESS:
          'Este ativo não está em Imobilizado em Andamento. Esta ação move um bem para fora ' +
          'dessa classe; um ativo já em depreciação precisa de outra capacidade, não de uma ' +
          'troca de classe.',
        FIXED_ASSET_TRANSFER_IN_SERVICE_BEFORE_ACQUISITION:
          'A data de disponibilidade para uso não pode ser anterior à data de aquisição.',
        FIXED_ASSET_TRANSFER_SCHEDULE_EXISTS:
          'Este ativo já tem parcelas de depreciação registradas, o que não deveria acontecer ' +
          'em Imobilizado em Andamento. Fale com a Alchemy antes de transferir.',
        FIXED_ASSET_TRANSFER_TARGET_CLASS_NOT_FOUND: 'Classe de destino não encontrada.',
        FIXED_ASSET_CLASS_NOT_ACTIVE: 'A classe de destino não está disponível para capitalização.',
        FIXED_ASSET_TRANSFER_TARGET_RIGHT_OF_USE:
          'A classe de destino é de direito de uso. Uma linha de conta a pagar nunca se torna um ' +
          'ativo de direito de uso — somente o reconhecimento de arrendamento cria um, contra um ' +
          'contrato.',
        FIXED_ASSET_TRANSFER_TARGET_SAME_AS_SOURCE: 'O ativo já está nesta classe.',
        FIXED_ASSET_CLASS_HAS_NO_BOOK_RULE:
          'Nenhuma regra de depreciação da classe de destino pertence aos livros ativos desta ' +
          'entidade. Configure a régua antes de transferir.',
        FIXED_ASSET_CLASS_MISSING_BOOK_RULE:
          'A classe de destino não tem régua de depreciação em um dos livros contábeis desta ' +
          'entidade, e o custo não seria reclassificado de CIP nesse livro. Configure a régua da ' +
          'classe em cada livro contábil antes de transferir.',
        FIXED_ASSET_TRANSFER_CURRENCY_UNSUPPORTED:
          'A moeda deste ativo diverge da moeda funcional de um dos livros da classe de destino.',
        FIXED_ASSET_TRANSFER_BAD_DATE: 'A data de disponibilidade para uso é inválida.',
        NO_OPEN_PERIOD:
          'O período contábil da data da transferência está fechado em algum livro. Escolha uma ' +
          'data dentro de um período aberto, ou reabra o período antes de transferir.',
        FIXED_ASSET_NOT_IN_SERVICE:
          'A régua de depreciação só é gerada para bem em serviço, em manutenção ou ocioso ' +
          '(CPC 27 §55). Informe a data de disponibilidade para uso e conclua a transferência ' +
          'antes de depreciar.',
      },
    },
    custodian: {
      action: 'Alterar responsável',
      dialog: {
        title: 'Alterar responsável por {{code}}',
        description: 'Registra quem passa a responder pelo bem, com data e motivo.',
        effectiveDate: 'Data',
        custodian: 'Novo responsável',
        custodianPlaceholder: 'Selecione um usuário (ou deixe em branco para nenhum)',
        reason: 'Motivo',
        reasonPlaceholder: 'Contexto da mudança.',
        submit: 'Registrar',
        cancel: 'Cancelar',
        close: 'Fechar',
        success: 'Responsável atualizado.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Ativo não encontrado. Atualize a página e tente novamente.',
        FIXED_ASSET_CUSTODIAN_CHANGE_NOT_ALLOWED:
          'Este ativo já foi baixado e não tem mais responsável para acompanhar.',
        FIXED_ASSET_CUSTODIAN_UNCHANGED: 'Este já é o responsável atual — nada para registrar.',
        FIXED_ASSET_CUSTODIAN_NOT_FOUND: 'O usuário selecionado não existe neste tenant.',
        FIXED_ASSET_CUSTODIAN_BAD_DATE: 'A data é inválida.',
        FIXED_ASSET_CUSTODIAN_REASON_REQUIRED: 'Informe o motivo da alteração.',
      },
    },
    draft: {
      actions: {
        promote: 'Capitalizar',
        edit: 'Editar rascunho',
        delete: 'Excluir rascunho',
      },
      ladderPreview:
        'Prévia: cada livro como abrirá ao capitalizar, se a conferência com a contabilidade ' +
        'passar. Nada foi lançado ou depreciado ainda.',
      plan: {
        title: 'Plano de capitalização',
        hintNoDocument:
          'O que será aplicado ao capitalizar: o bem entra em uso e o custo é lançado contra a ' +
          'conta abaixo.',
        hintMigrated:
          'O que será aplicado ao capitalizar: o bem entra em uso com a depreciação já tomada até ' +
          'o corte, sem lançamento — os valores já estão nos saldos de abertura.',
        inServiceDate: 'Entrada em uso',
        inServiceDateDefault: '{{date}} (data de aquisição)',
        counterAccount: 'Conta de contrapartida',
        cutover: 'Data de corte',
        openingPositions: 'Depreciação acumulada até o corte',
        netBookValue: 'Valor líquido {{amount}}',
        remainingLife_one: '{{count}} mês restante',
        remainingLife_other: '{{count}} meses restantes',
        notFilled: 'Não informado',
      },
      promote: {
        title: 'Capitalizar {{code}}?',
        descriptionNoDocument:
          'O bem entra em uso em {{date}} e é lançado: débito no imobilizado e crédito em ' +
          '{{account}}, no valor de {{amount}}.',
        descriptionMigrated:
          'O bem entra em uso em {{date}} com a posição de abertura abaixo. Nada é lançado: o ' +
          'custo e a depreciação já estão nos saldos de abertura e são conferidos com o razão.',
        accumulatedIn: 'Depreciação acumulada · {{book}}',
        gapsTitle: 'Falta no plano',
        gaps: {
          counterAccount: 'A conta de contrapartida da capitalização.',
          cutover: 'A data de corte da migração.',
          openingBooks: 'A depreciação acumulada até o corte em: {{books}}.',
        },
        gapsNote: 'Edite o rascunho para completar o plano antes de capitalizar.',
        confirm: 'Capitalizar',
        success: 'Ativo {{code}} capitalizado.',
      },
      delete: {
        title: 'Excluir o rascunho {{code}}?',
        description:
          'O rascunho é removido e o código {{code}} fica livre para outro bem. Nada foi lançado ' +
          'no razão; a exclusão fica registrada na trilha de auditoria.',
        confirm: 'Excluir rascunho',
        success: 'Rascunho {{code}} excluído.',
      },
    },
  },

  leases: {
    title: 'Contratos de arrendamento',
    description: 'Direito de uso sob o CPC 06 (R2), com o tratamento de cada livro.',
    empty: 'Nenhum contrato encontrado.',
    searchPlaceholder: 'Código ou descrição do contrato',
    lessorPlaceholder: 'Nome do arrendador',
    paymentMin: 'Contraprestação mínima',
    paymentMax: 'Contraprestação máxima',
    filters: {
      status: 'Status',
      classification: 'Classificação',
      lessor: 'Arrendador',
    },
    columns: {
      code: 'Contrato',
      lessor: 'Arrendador',
      term: 'Prazo',
      payment: 'Contraprestação',
      classification: 'Classificação',
      status: 'Status',
      liability: 'Passivo por livro',
    },
    classification: {
      RECOGNIZED: 'Reconhecido',
      // Bug 035: um contrato RECOGNIZED ainda em DRAFT nunca rodou a recognição — não tem
      // passivo, não tem direito de uso, não tem tabela de amortização. Dizer "Reconhecido"
      // nesse estado afirma um evento contábil que não aconteceu.
      RECOGNIZED_PENDING: 'Exige reconhecimento',
      SHORT_TERM_EXEMPT: 'Dispensa — curto prazo',
      LOW_VALUE_EXEMPT: 'Dispensa — baixo valor',
    },
    status: {
      DRAFT: 'Rascunho',
      ACTIVE: 'Ativo',
      TERMINATED: 'Encerrado',
      EXPIRED: 'Vencido',
    },
    frequency: {
      MONTHLY: 'Mensal',
      BIMONTHLY: 'Bimestral',
      QUARTERLY: 'Trimestral',
      SEMESTERLY: 'Semestral',
      YEARLY: 'Anual',
    },
    recognitionMode: {
      RECOGNIZE_ROU: 'Reconhece direito de uso',
      EXPENSE_PAYMENTS: 'Deduz a contraprestação',
    },
    expensePaymentsHint:
      'Este livro não carrega ativo nem passivo (Lei 12.973/2014, arts. 47-49). Essa ausência é ' +
      'a diferença temporária — não um dado faltando.',
    new: {
      action: 'Novo arrendamento',
      backToList: 'Voltar para contratos',
      jurisdiction: {
        title: 'Arrendamentos ainda não disponíveis nesta entidade',
        body:
          'Esta entidade mantém {{books}}. O motor implementa CPC 06 (R2) e IFRS 16. A NIF D-5 ' +
          'e o ASC 842 divergem na mensuração e na apresentação — o ASC 842 separa finance de ' +
          'operating lease no balanço, e o fiscal americano não reconhece direito de uso em ' +
          'operating lease. Fale com a Alchemy antes de registrar arrendamentos aqui.',
      },
      sections: {
        entity: 'Entidade',
        contract: 'Contrato',
        indexation: 'Reajuste',
        discountRate: 'Taxa de desconto',
        costs: 'Custos e provisões',
        cutover: 'Saldos de abertura',
        renewal: 'Opção de renovação',
        classification: 'Classificação',
      },
      fields: {
        entity: 'Entidade',
        entityHint:
          'Todo arrendamento pertence a uma entidade: é ela que mantém os livros, a política de ' +
          'ativos e o plano de contas que medem o contrato.',
        entityPlaceholder: 'Selecione a entidade',
        contractCode: 'Código do contrato',
        contractCodePlaceholder: 'Ex.: ARR-2026-001',
        description: 'Descrição',
        descriptionPlaceholder: 'Ex.: Sala comercial — Av. Paulista, 1000',
        lessor: 'Arrendador',
        lessorPlaceholder: 'Buscar fornecedor',
        lessorHint: 'O arrendador é um fornecedor: as contraprestações correm pelo contas a pagar.',
        commencementDate: 'Data de início',
        termMonths: 'Prazo em meses',
        termMonthsHint:
          'O prazo do arrendamento, com as renovações razoavelmente certas já dentro dele.',
        paymentAmount: 'Valor da parcela',
        paymentFrequency: 'Periodicidade',
        paymentDueDay: 'Dia de vencimento',
        paymentDueDayHint:
          'Move apenas a data de vencimento, nunca o desconto. A parcela paga no início mantém a ' +
          'data de começo do contrato.',
        paymentInAdvance: 'Pagamento antecipado',
        paymentInAdvanceHint:
          'Antecipado é a norma brasileira do aluguel: a primeira parcela cai na data de início, ' +
          'fica fora do passivo pelo §26 e entra no ativo pelo §24(b).',
        indexMode: 'Base do reajuste',
        indexModeHint:
          'Ou uma base contratual, ou um índice publicado — nunca os dois. O banco de dados ' +
          'recusa o contrato que carregue ambos.',
        indexModeFixed: 'Fixo — a parcela não muda',
        indexModeStep: 'Degrau contratual — percentual conhecido na assinatura',
        indexModeIndex: 'Índice econômico publicado',
        economicIndexCode: 'Índice econômico',
        economicIndexPlaceholder: 'Selecione a série',
        economicIndexHint:
          'A parcela indexada é medida no nível do índice na data de início (§27(b)) e só se move ' +
          'na remensuração — a expectativa de inflação não entra no passivo inicial.',
        contractualStepRate: 'Percentual do degrau',
        contractualStepRateHint:
          'Conhecido na assinatura, portanto pagamento fixo em substância (§27(a)-(b)): entra no ' +
          'passivo desde o primeiro dia.',
        indexAnniversaryMonth: 'Mês do aniversário',
        indexAnniversaryDay: 'Dia do aniversário',
        indexWindowMonths: 'Janela do índice (meses)',
        indexPublicationLagDays: 'Defasagem de publicação (dias)',
        indexFloorAtZero: 'Piso em zero (sem reajuste negativo)',
        discountRateAnnual: 'Taxa de desconto (a.a.)',
        discountRateAnnualHint:
          'Taxa efetiva anual. O §26 exige a taxa implícita no contrato ou, quando não ' +
          'determinável, a taxa incremental de empréstimo do arrendatário.',
        discountRateDefaultNote:
          'Padrão da política do cliente: {{rate}} (taxa incremental de empréstimo).',
        usesRateImplicitInLease: 'A taxa implícita no contrato era determinável e foi usada',
        discountRateBasisNote: 'Fundamento da taxa',
        discountRateBasisNotePlaceholder:
          'Por que esta é a taxa incremental de empréstimo desta entidade.',
        rouAssetClass: 'Classe do direito de uso',
        rouAssetClassHint:
          'Aplicada a todos os livros que reconhecem — um contrato produz um único direito de ' +
          'uso, e todos os livros precisam apontar para a mesma classe.',
        rouAssetClassPlaceholder: 'Selecione a classe do direito de uso',
        initialDirectCosts: 'Custos diretos iniciais (§24(b))',
        initialDirectCostsHint:
          'Capitalizados no direito de uso, nunca no passivo. Ex.: comissão de corretagem.',
        restorationProvision: 'Provisão de restauração (§24(d))',
        restorationProvisionHint:
          'Obrigação de desmontagem ou recomposição (CPC 25). Também só no ativo.',
        isCutover: 'Este arrendamento já estava em andamento na migração',
        isCutoverHint:
          'Carrega os saldos contábeis existentes sem refazer o reconhecimento nem a amortização anterior.',
        openingPositionAsOf: 'Data-base dos saldos de abertura',
        openingPositionAsOfHint:
          'Informe a data do fechamento migrado. Ela deve ser posterior ao início do contrato.',
        openingLiabilityAmount: 'Passivo de arrendamento na data-base',
        openingAccumulatedAmortizationAmount: 'Amortização acumulada do direito de uso',
        containsPurchaseOption: 'O contrato contém opção de compra?',
        containsPurchaseOptionPlaceholder: 'Selecione sim ou não',
        containsPurchaseOptionHint:
          'Decisão obrigatória: qualquer opção de compra afasta a dispensa de curto prazo do Apêndice A.',
        includesRenewalOptionInTerm: 'O prazo inclui opção de renovação',
        renewalJudgementNote: 'Julgamento da renovação',
        renewalJudgementNotePlaceholder: 'Por que o exercício da renovação é razoavelmente certo.',
        renewalJudgementRequired:
          'Obrigatório quando o prazo inclui renovação: o CPC 06 R2 exige que esse julgamento ' +
          'seja explícito, porque ele muda tanto o ativo quanto o passivo.',
        renewalMonthsInsideTermHint:
          'Os meses da renovação já devem estar somados em “Prazo em meses” — este campo não os ' +
          'acrescenta.',
        underlyingAssetValueWhenNew: 'Valor do ativo subjacente NOVO',
        underlyingAssetValueWhenNewHint:
          'Valor de um ativo equivalente NOVO — não o valor presente do contrato, não o valor de ' +
          'mercado do bem usado. É esse número que o teste de baixo valor (§5(b), §B3-B8) lê.',
        classificationOverride: 'Sobrepor a classificação',
        classificationOverrideAssessed: 'Manter o resultado avaliado',
        classificationOverrideHint:
          'O §5 é uma eleição de política do arrendatário, então a sobreposição é aceita — mas ' +
          'uma dispensa sem motivo registrado é indistinguível de uma omissão.',
        exemptionRationale: 'Justificativa da dispensa',
        exemptionRationalePlaceholder: 'Por que este contrato é dispensado do reconhecimento.',
        notes: 'Observações',
      },
      panel: {
        incomplete: 'Preencha prazo, parcela, data de início e taxa para ver a mensuração.',
        policy: {
          selectEntity:
            'Selecione uma entidade para avaliar a classificação: a política de ativos e a ' +
            'moeda usadas abaixo são lidas dela.',
          loading: 'Carregando a política de ativos da entidade…',
          loadFailed:
            'Não foi possível carregar a política de ativos ou os livros da entidade. ' +
            'Atualize a página e tente novamente.',
          missing:
            'Não existe uma política de ativos cadastrada para esta entidade nem para o ' +
            'cliente, então os limites de curto prazo e de baixo valor são desconhecidos. ' +
            'Configure a política em Configurações de Ativos antes de continuar.',
          currencyUnresolved:
            'Não foi possível determinar a moeda desta entidade a partir dos livros contábeis ' +
            'cadastrados. Verifique os livros da entidade antes de continuar.',
        },
        testState: {
          PASS: 'Atende',
          FAIL: 'Não atende',
          NOT_EVALUATED: 'Não avaliado',
          NOT_ASSESSABLE: 'Sem dados',
        },
        classification: {
          title: 'Classificação CPC 06 R2 §5',
          outcome: 'Resultado:',
          pending: 'Aguardando prazo',
          overridden: 'Sobreposto',
          rationaleRequired:
            'Sobrepor para uma dispensa exige justificativa. O servidor recusa o contrato sem ela ' +
            '(LEASE_EXEMPTION_RATIONALE_REQUIRED).',
          shortTerm: {
            label: '1. Prazo curto — §5(a)',
            detail: 'Prazo de {{term}} meses contra o limite de {{max}} meses da política.',
            purchaseOptionPresent:
              'O contrato contém opção de compra; por isso, o Apêndice A afasta a dispensa de curto prazo.',
            renewalIncluded:
              'A renovação razoavelmente certa já está dentro do prazo, então um contrato que só ' +
              'passa de 12 meses por causa dela corretamente não é de curto prazo.',
          },
          lowValue: {
            label: '2. Baixo valor — §5(b), §B3-B8',
            detail: 'Ativo novo por {{value}} contra o limite de {{threshold}} da política.',
            underlyingAssetWarning:
              'Este teste lê o valor do ATIVO SUBJACENTE NOVO — nunca o valor presente do ' +
              'contrato. Um imóvel nunca é de baixo valor por mais barata que seja a parcela.',
          },
          recognized: {
            label: '3. Reconhecimento — §22',
            detail: 'Sem dispensa aplicável: nasce um direito de uso e um passivo de arrendamento.',
          },
        },
        exempt: {
          title: 'Despesa da contraprestação',
          shortTermBody:
            'Dispensa de curto prazo: nenhum direito de uso e nenhum passivo. O §6 leva as ' +
            'contraprestações ao resultado de forma linear ao longo do prazo.',
          lowValueBody:
            'Dispensa de baixo valor: nenhum direito de uso e nenhum passivo. O §6 leva as ' +
            'contraprestações ao resultado de forma linear ao longo do prazo.',
          monthlyExpense: 'Despesa mensal',
          nominalTotal: 'Total nominal do prazo',
        },
        presentValue: {
          title: 'Valor presente — §26',
          liability: 'Passivo de arrendamento',
          rouAsset: 'Ativo de direito de uso',
          nominalTotal: 'Total nominal do prazo',
          embeddedInterest: 'Juros embutidos',
          currentPortion: 'Passivo circulante',
          nonCurrentPortion: 'Passivo não circulante',
          periodicRate: 'Taxa por período: {{rate}} · {{installments}} parcelas.',
        },
        convention: {
          title: 'As duas convenções de pagamento dão números diferentes',
          body:
            'Antecipado e postecipado não se reconciliam, e a diferença é invisível no resultado: ' +
            'um valor presente parece plausível qualquer que seja a convenção que o produziu. ' +
            'Confirme qual delas o contrato realmente estabelece.',
          inAdvance: 'Antecipado (§24(b))',
          inArrears: 'Postecipado',
          difference: 'Diferença',
        },
        entry: {
          title: 'Lançamento de reconhecimento',
          account: 'Conta',
          debit: 'Débito',
          credit: 'Crédito',
          total: 'Total',
          asymmetryTitle: 'A assimetria do dia um é correta',
          asymmetryBody:
            'O direito de uso supera o passivo em {{excess}}. O §26 mede o passivo apenas pelas ' +
            'parcelas ainda NÃO pagas; o §24(b) e o §24(d) somam ao ativo a parcela paga no ' +
            'início, os custos diretos iniciais e a provisão de restauração. O lançamento fecha ' +
            'porque cada um desses três tem o seu próprio crédito.',
          accountsFixtureNote:
            'As contas mostradas acima são nomes indicativos, sem código: a resolução das contas ' +
            'definitivas é do servidor e o endpoint de preview ainda não existe. Os valores vêm ' +
            'do motor de mensuração.',
        },
        pfLessor: {
          title: 'Arrendador pessoa física',
          body:
            'Aluguel pago a pessoa física tem IRRF retido pela trilha IRRF_RETIDO_ALUGUEIS do ' +
            'contas a pagar. Confirme que o mapeamento do slot para conta contábil deste cliente ' +
            'está configurado antes de lançar as contraprestações. A retenção em si é tratada ' +
            'pelas trilhas fiscais do AP, fora deste módulo.',
        },
      },
      cutover: {
        noticeTitle: 'Use os saldos fechados no sistema anterior',
        noticeBody:
          'O passivo e a amortização acumulada são independentes e obrigatórios para cada livro. ' +
          'O Alchemy não cria lançamento de abertura: esses saldos já precisam estar no razão migrado.',
      },
      purchaseOptionConflict: {
        title: 'A dispensa de curto prazo não está disponível',
        body: 'O contrato contém opção de compra. Mantenha a classificação reconhecida ou escolha outra avaliação aplicável.',
      },
      /**
       * `POST /assets/lease-contracts` maps every stable error code it can return to an
       * actionable pt-BR string. The code is what the API guarantees; the wording is ours —
       * see `getLeaseContractErrorMessage` in `@/lib/assets-error-messages`, which falls
       * back to `getErrorMessage` for any code this map does not (yet) name.
       */
      errors: {
        LEASE_CONTRACT_TERM_INVALID:
          'O prazo do arrendamento deve ser um número inteiro positivo de meses.',
        LEASE_CONTRACT_AMOUNT_INVALID:
          'Um dos valores informados é inválido. Confira a parcela, os custos diretos iniciais, ' +
          'a provisão de restauração e os saldos de abertura: todos devem ser números não negativos com no máximo ' +
          'duas casas decimais, e a parcela deve ser maior que zero.',
        LEASE_EXEMPTION_RATIONALE_REQUIRED:
          'Sobrepor a classificação para uma dispensa exige uma justificativa. Preencha ' +
          '“Justificativa da dispensa” antes de salvar (CPC 06 R2 §5).',
        ASSET_TENANT_POLICY_NOT_FOUND:
          'Não existe uma política de ativos cadastrada para esta entidade nem para o cliente, ' +
          'então os limites de curto prazo e de baixo valor são desconhecidos. Peça para ' +
          'configurar a política ou sobreponha a classificação manualmente.',
        LEASE_CONTRACT_NO_BOOKS:
          'Esta entidade não mantém nenhum livro ativo, então nenhuma régua contábil pode ' +
          'registrar este contrato. Configure ao menos um livro ativo para a entidade.',
        LEASE_CONTRACT_BOOK_NOT_FOUND:
          'Um dos livros informados não é um livro ativo desta entidade. Atualize a página e ' +
          'tente novamente.',
        LEASE_TREATMENT_RATE_UNEXPECTED:
          'Um livro que apenas deduz a contraprestação não tem passivo a descontar e não deve ' +
          'carregar uma taxa de desconto.',
        LEASE_TREATMENT_ROU_CLASS_UNEXPECTED:
          'Um livro que apenas deduz a contraprestação não reconhece direito de uso e não deve ' +
          'indicar uma classe de ativo.',
        LEASE_CONTRACT_JURISDICTION_UNSUPPORTED:
          'Esta entidade mantém um livro sob uma norma que o motor de arrendamento ainda não ' +
          'implementa — apenas CPC 06 (R2) e IFRS 16 são suportadas. Fale com a Alchemy antes de ' +
          'registrar arrendamentos nesta entidade.',
        LEASE_TREATMENT_CONTRADICTS_CLASSIFICATION:
          'A classificação escolhida é uma dispensa: nenhum livro pode reconhecer direito de uso ' +
          'enquanto ela estiver ativa. Mude a classificação se a dispensa não se aplicar.',
        LEASE_TREATMENT_CONTRADICTS_TAX_BASIS:
          'Um livro fiscal (base TAX_BASIS) deduz a contraprestação e nunca reconhece direito de ' +
          'uso (Lei 12.973/2014, arts. 47-49) — isso não pode ser sobreposto.',
        LEASE_DISCOUNT_RATE_UNRESOLVED:
          'Falta uma taxa de desconto para um livro que reconhece direito de uso, e a política ' +
          'do cliente não tem uma taxa incremental de empréstimo padrão. Informe a taxa de ' +
          'desconto anual (CPC 06 R2 §26).',
        LEASE_DISCOUNT_RATE_INVALID:
          'A taxa de desconto informada não é positiva. Informe uma taxa de desconto anual maior ' +
          'que zero.',
        LEASE_INDEXATION_BASIS_INVALID:
          'Informe exatamente uma base de reajuste: fixo ou degrau contratual, OU um índice ' +
          'econômico publicado — nunca os dois, nem nenhum. Eles se comportam de formas ' +
          'diferentes (CPC 06 R2 §27(a)-(b)).',
        LEASE_CONTRACTUAL_STEP_RATE_INVALID:
          'O percentual do degrau contratual é obrigatório para essa base de reajuste e precisa ' +
          'ser uma fração decimal utilizável maior que -100%.',
        LEASE_RENEWAL_JUDGEMENT_NOTE_REQUIRED:
          'O prazo inclui uma opção de renovação: registre o julgamento de por que o exercício é ' +
          'razoavelmente certo (CPC 06 R2 §18).',
        LEASE_PAYMENT_DUE_DAY_INVALID: 'O dia de vencimento deve ser um dia do mês entre 1 e 31.',
        ENTITY_NOT_FOUND:
          'A entidade selecionada não foi encontrada. Atualize a página e tente novamente.',
        AP_VENDOR_NOT_FOUND:
          'O arrendador selecionado não foi encontrado como fornecedor. Atualize a lista de ' +
          'fornecedores e tente novamente.',
        LEASE_CONTRACT_CODE_TAKEN:
          'Já existe um contrato de arrendamento com este código. Escolha um código diferente.',
        LEASE_ECONOMIC_INDEX_NOT_FOUND:
          'O índice econômico selecionado não está no catálogo de índices. Escolha outro índice ' +
          'ou fale com a Alchemy para incluí-lo.',
        LEASE_CONTRACT_BAD_DATE: 'A data de início informada é inválida.',
        LEASE_PURCHASE_OPTION_DECISION_REQUIRED:
          'Informe se o contrato contém opção de compra. Sem essa decisão, a dispensa de curto prazo não pode ser avaliada.',
        LEASE_SHORT_TERM_PURCHASE_OPTION_CONFLICT:
          'Um contrato com opção de compra não pode usar a dispensa de curto prazo. Mantenha o reconhecimento ou revise a opção.',
        LEASE_OPENING_POSITIONS_REQUIRED:
          'A data-base foi informada sem os saldos por livro. Preencha o passivo e a amortização acumulada de cada livro.',
        LEASE_OPENING_POSITION_DATE_REQUIRED:
          'Os saldos por livro exigem uma data-base de abertura.',
        LEASE_OPENING_POSITION_BOOK_DUPLICATE:
          'O mesmo livro aparece mais de uma vez nos saldos de abertura. Mantenha uma única posição por livro.',
        LEASE_OPENING_POSITION_BOOK_MISSING:
          'Falta o saldo de abertura de um livro que reconhece o arrendamento. Preencha todos os livros exibidos.',
        LEASE_OPENING_POSITION_BOOK_NOT_RECOGNIZED:
          'Foi informado saldo para um livro que apenas leva as parcelas à despesa. Remova essa posição.',
        LEASE_CUTOVER_REQUIRES_RECOGNITION:
          'Saldos de abertura só existem em um arrendamento reconhecido. Remova a migração ou revise a classificação.',
        LEASE_CUTOVER_DATE_INVALID:
          'A data-base deve ser posterior ao início e anterior ao fim do arrendamento.',
        LEASE_CUTOVER_DATE_NOT_PERIOD_END:
          'A data-base deve ser o último dia da competência, por exemplo 31/08/2026. A amortização corre por competência inteira.',
        LEASE_CUTOVER_DATE_IN_FUTURE:
          'A data-base não pode ser futura. O cutover registra amortização já ocorrida e um passivo já devido.',
        LEASE_CUTOVER_ACCUMULATED_AMORTIZATION_INVALID:
          'A amortização acumulada de abertura deve ser não negativa e não pode superar o valor original do direito de uso.',
        LEASE_CUTOVER_LIABILITY_INVALID:
          'O passivo de abertura deve ser positivo e não pode superar as parcelas nominais que ainda restam.',
      },
      success: 'Arrendamento criado.',
      footer: {
        saveDraft: 'Salvar rascunho',
        submit: 'Criar arrendamento',
      },
    },
    detail: {
      backToList: 'Voltar para contratos',
      notFound: 'Contrato não encontrado.',
      sections: {
        contract: 'Contrato',
        indexation: 'Reajuste',
        books: 'Posição por livro',
        installments: 'Tabela de amortização',
        remeasurements: 'Remensurações',
      },
      fields: {
        lessor: 'Arrendador',
        commencement: 'Início',
        end: 'Fim',
        term: 'Prazo',
        termMonths: '{{count}} meses',
        payment: 'Contraprestação',
        frequency: 'Periodicidade',
        paymentTiming: 'Momento do pagamento',
        inAdvance: 'Antecipado',
        inArrears: 'Postecipado',
        dueDay: 'Dia de vencimento',
        purchaseOption: 'Opção de compra',
        openingPositionAsOf: 'Data-base dos saldos de abertura',
        renewalIncluded: 'Inclui renovação no prazo',
        renewalNote: 'Julgamento da renovação',
        initialDirectCosts: 'Custos diretos iniciais',
        restorationProvision: 'Provisão de restauração',
        exemptionRationale: 'Justificativa da dispensa',
        lowValueThreshold: 'Limite de baixo valor avaliado',
        notes: 'Observações',
      },
      paymentTimingHint:
        'Num contrato antecipado o direito de uso nasce maior que o passivo, exatamente pela ' +
        'parcela paga no início: o §26 a mantém fora do passivo e o §24(b) a coloca no ativo.',
      books: {
        recognitionMode: 'Tratamento',
        discountRate: 'Taxa de desconto',
        openingLiability: 'Passivo de abertura',
        openingAccumulatedAmortization: 'Amortização acumulada de abertura',
        rateImplicit: 'Taxa implícita no contrato',
        rateIncremental: 'Taxa incremental de empréstimo',
        liability: 'Passivo',
        current: 'Circulante',
        nonCurrent: 'Não circulante',
        rouAsset: 'Direito de uso',
        rouAssetCost: 'Custo',
        rouAssetNbv: 'Valor contábil líquido',
        progress: '{{settled}} de {{total}} parcelas liquidadas',
      },
      installments: {
        empty: 'Nenhuma parcela gerada.',
        loading: 'Carregando parcelas',
        error: 'Não foi possível carregar as parcelas. Tente novamente.',
        columns: {
          number: 'Parcela',
          dueDate: 'Vencimento',
          opening: 'Saldo inicial',
          payment: 'Pagamento',
          interest: 'Juros',
          principal: 'Principal',
          closing: 'Saldo final',
          portion: 'Classificação',
          status: 'Status',
        },
        current: 'Circulante',
        nonCurrent: 'Não circulante',
      },
      remeasurements: {
        empty: 'Nenhuma remensuração aplicada.',
        appliedRate: 'Reajuste aplicado',
        newPayment: 'Nova contraprestação',
        source: 'Fonte do índice',
        effectiveDate: 'Vigência',
        competency: 'Competência',
        effects: 'Efeito por livro',
        liabilityBefore: 'Passivo antes',
        liabilityAfter: 'Passivo depois',
        rouAdjustment: 'Ajuste no direito de uso',
        profitOrLoss: 'Resultado',
        reason: {
          INDEX_ADJUSTMENT: 'Reajuste por índice',
          TERM_CHANGE: 'Mudança de prazo',
          PAYMENT_CHANGE: 'Mudança de contraprestação',
          SCOPE_CHANGE: 'Mudança de escopo',
          DISCOUNT_RATE_CHANGE: 'Mudança de taxa de desconto',
          TERMINATION: 'Encerramento',
        },
        treatment: {
          ADJUST_ROU_ASSET: 'Ajusta o direito de uso',
          RECOGNIZE_IN_PROFIT_OR_LOSS: 'Vai para o resultado',
        },
      },
      recognize: {
        action: 'Reconhecer arrendamento',
        title: 'Reconhecer {{code}}',
        description:
          'Mede o contrato na data de início e cria o direito de uso e o passivo em cada livro ' +
          'que reconhece (CPC 06 R2 §24 e §26). Roda uma única vez, em uma transação — depois ' +
          'disso, qualquer mudança é uma remensuração, não um segundo reconhecimento.',
        cutoverDescription:
          'Carrega o ativo de direito de uso e o passivo pelos saldos de abertura de cada livro, ' +
          'sem relançar o reconhecimento nem a amortização anterior. Nenhum lançamento de abertura será criado.',
        prepaidAccount: 'Conta da parcela paga na data de início',
        prepaidAccountHint:
          'Este contrato paga antecipado: a primeira parcela cai na data de início, fica fora ' +
          'do passivo (§26) e entra no ativo (§24(b)). Informe a conta que ela liquida — o ' +
          'fornecedor a pagar, ou o banco se já foi paga na assinatura.',
        directCostsAccount: 'Conta dos custos diretos iniciais',
        restorationAccount: 'Conta da provisão de restauração',
        restorationAccountHint:
          'Precisa ser uma conta de passivo — a provisão de restauração é uma obrigação (CPC 25), ' +
          'não um crédito em caixa.',
        accountPlaceholder: 'Buscar conta contábil',
        rouAssetClass: 'Classe do direito de uso',
        rouAssetClassHint:
          'Já gravada nos livros que reconhecem. Deixe em branco para manter — uma classe ' +
          'diferente será recusada.',
        rouAssetClassRequiredHint:
          'Obrigatória: nenhum livro que reconhece tem uma classe de ativo gravada ainda, e o ' +
          'reconhecimento não segue sem ela.',
        rouAssetClassPlaceholder: 'Selecione a classe do direito de uso',
        dimensions: 'Dimensões gerenciais',
        dimensionsHint:
          'Opcional. O centro de custo que ocupa o imóvel arrendado nasce com o direito de uso e ' +
          'acompanha toda a amortização depois — evita ter que mapear isso mais tarde.',
        costCenterPlaceholder: 'Centro de custo',
        departmentPlaceholder: 'Departamento',
        managerialAccountPlaceholder: 'Conta gerencial',
        cancel: 'Cancelar',
        close: 'Fechar',
        submit: 'Reconhecer',
        success: 'Arrendamento reconhecido.',
        exemptOutcome: 'Contrato dispensado — nada foi reconhecido.',
        recognizedOutcome: 'Reconhecido em {{count}} livro(s).',
        installmentsGenerated: '{{count}} parcelas geradas',
        errors: {
          LEASE_CONTRACT_NOT_FOUND:
            'Este contrato não foi encontrado. Atualize a página e tente novamente.',
          LEASE_CONTRACT_ALREADY_RECOGNIZED:
            'Este contrato já saiu do rascunho — o reconhecimento inicial roda uma única vez. ' +
            'Uma mudança depois do reconhecimento é uma remensuração, não um segundo ' +
            'reconhecimento.',
          LEASE_CONTRACT_RECOGNITION_RACED:
            'Este contrato foi reconhecido por outra requisição enquanto esta era processada. ' +
            'Atualize a página para ver o estado atual.',
          LEASE_CONTRACT_NO_TREATMENTS:
            'Este contrato não tem nenhum tratamento por livro configurado, então nenhuma régua ' +
            'contábil tem uma posição sobre ele. Atualize a página e tente novamente.',
          LEASE_DISCOUNT_RATE_UNRESOLVED:
            'Um livro que reconhece direito de uso não tem taxa de desconto, e a política do ' +
            'cliente não tem uma taxa incremental de empréstimo padrão. É preciso corrigir isso ' +
            'no cadastro do contrato (CPC 06 R2 §26).',
          LEASE_OPENING_POSITION_BOOK_MISSING:
            'Falta a posição de abertura de um livro que reconhece este arrendamento. Corrija o cadastro antes de reconhecer.',
          LEASE_OPENING_POSITION_BOOK_NOT_RECOGNIZED:
            'Existe uma posição de abertura em um livro que não reconhece ativo nem passivo. Corrija o cadastro antes de reconhecer.',
          LEASE_OPENING_POSITION_BOOK_DUPLICATE:
            'O mesmo livro aparece mais de uma vez na posição de abertura. Corrija o cadastro antes de reconhecer.',
          LEASE_OPENING_POSITION_AMOUNT_INVALID:
            'Um saldo de abertura não é um valor monetário válido. Corrija o passivo e a amortização acumulada do livro.',
          LEASE_CUTOVER_DATE_INVALID:
            'A data-base deve ser posterior ao início e anterior ao fim do arrendamento. Corrija a data-base antes de reconhecer.',
          LEASE_CUTOVER_DATE_NOT_PERIOD_END:
            'A data-base deve ser o último dia da competência, por exemplo 31/08/2026. A amortização corre por competência inteira.',
          LEASE_CUTOVER_DATE_IN_FUTURE:
            'A data-base não pode ser futura. O cutover registra amortização já ocorrida e um passivo já devido.',
          LEASE_CUTOVER_ACCUMULATED_AMORTIZATION_INVALID:
            'A amortização acumulada de abertura deve ser não negativa e não pode superar o valor original do direito de uso.',
          LEASE_CUTOVER_LIABILITY_INVALID:
            'O passivo de abertura deve ser positivo e não pode superar as parcelas nominais que ainda restam.',
          LEASE_EXEMPTION_RATIONALE_REQUIRED:
            'Este contrato está classificado como dispensa mas não tem justificativa registrada. ' +
            'É preciso corrigir isso no cadastro do contrato antes de reconhecer (CPC 06 R2 §60).',
          LEASE_ROU_ASSET_CLASS_REQUIRED:
            'Um livro que reconhece direito de uso não tem uma classe de ativo indicada, e não há ' +
            'um padrão seguro para isso. É preciso corrigir isso no cadastro do contrato.',
          LEASE_ROU_ASSET_CLASS_CONFLICT:
            'Os livros que reconhecem indicam classes de ativo diferentes. Um contrato produz um ' +
            'único direito de uso, então todos precisam apontar para a mesma classe — corrija ' +
            'isso no cadastro do contrato.',
          FIXED_ASSET_CLASS_NOT_FOUND:
            'A classe de ativo indicada para o direito de uso não existe mais. Corrija o cadastro ' +
            'do contrato antes de reconhecer.',
          FIXED_ASSET_CLASS_INACTIVE:
            'A classe de ativo indicada para o direito de uso não está ativa. Corrija o cadastro ' +
            'do contrato ou reative a classe antes de reconhecer.',
          LEASE_ROU_ASSET_CLASS_NOT_RIGHT_OF_USE:
            'A classe indicada não está marcada como direito de uso — reconhecer nela ' +
            'apresentaria o arrendamento dentro do imobilizado próprio, o que o CPC 06 R2 §47 ' +
            'proíbe. Escolha uma classe de direito de uso.',
          ASSET_STATUS_CONCURRENT_CHANGE:
            'Outra pessoa alterou este registro enquanto você trabalhava. Atualize a página e tente novamente.',
          ASSET_DIMENSION_NOT_FOUND:
            'O centro de custo, departamento ou conta gerencial selecionado não existe mais. ' +
            'Atualize a página e selecione novamente.',
          ASSET_DIMENSION_WRONG_ENTITY:
            'O centro de custo, departamento ou conta gerencial selecionado pertence a outra ' +
            'entidade — este arrendamento é desta entidade, e a dimensão precisa ser da mesma.',
          LEASE_RECOGNITION_BOOK_NOT_POSTABLE:
            'Um dos livros que reconhecem direito de uso não mantém razão para esta entidade. ' +
            'Fale com a Alchemy antes de reconhecer.',
          LEASE_RECOGNITION_CURRENCY_UNSUPPORTED:
            'A moeda do contrato é diferente da moeda funcional de um dos livros. Um arrendamento ' +
            'em moeda estrangeira é um item monetário que exigiria remensuração cambial a cada ' +
            'data de fechamento (CPC 02 §23), o que este módulo ainda não faz.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_REQUIRED:
            'Este contrato paga antecipado: selecione a conta que a parcela do dia um liquida.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_UNEXPECTED:
            'Este contrato paga postecipado e não tem parcela a liquidar na data de início — ' +
            'remova a conta selecionada.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_INVALID:
            'A conta selecionada para a parcela antecipada não existe neste tenant. Selecione ' +
            'outra conta.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_TYPE_INVALID:
            'A conta selecionada para a parcela antecipada precisa ser de ativo ou de passivo.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_NATURE_INVALID:
            'A conta da parcela antecipada tem natureza incompatível com o tipo: conta de ativo ' +
            'precisa ser de natureza devedora e conta de passivo de natureza credora, e nenhuma ' +
            'pode ser redutora.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_REQUIRED:
            'Este contrato tem custos diretos iniciais: selecione a conta que foi creditada por ' +
            'eles.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_UNEXPECTED:
            'Este contrato não tem custos diretos iniciais — remova a conta selecionada.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_INVALID:
            'A conta selecionada para os custos diretos iniciais não existe neste tenant. ' +
            'Selecione outra conta.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_TYPE_INVALID:
            'A conta selecionada para os custos diretos iniciais precisa ser de ativo ou de ' +
            'passivo.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_NATURE_INVALID:
            'A conta dos custos diretos iniciais tem natureza incompatível com o tipo: conta de ' +
            'ativo precisa ser de natureza devedora e conta de passivo de natureza credora, e ' +
            'nenhuma pode ser redutora.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_REQUIRED:
            'Este contrato tem provisão de restauração: selecione a conta de passivo que a ' +
            'carrega.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_UNEXPECTED:
            'Este contrato não tem provisão de restauração — remova a conta selecionada.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_INVALID:
            'A conta selecionada para a provisão de restauração não existe neste tenant. ' +
            'Selecione outra conta.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_TYPE_INVALID:
            'A conta selecionada para a provisão de restauração precisa ser de passivo — a ' +
            'provisão é uma obrigação (CPC 25), não um crédito em caixa.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_NATURE_INVALID:
            'A conta da provisão de restauração precisa ser uma conta de passivo de natureza ' +
            'credora e não pode ser uma conta redutora.',
          LEASE_LIABILITY_ACCOUNT_UNRESOLVED:
            'O plano de contas não tem uma conta mapeada para o passivo de arrendamento (slot do ' +
            'sistema não configurado). Peça para configurar o mapeamento de contas antes de ' +
            'reconhecer.',
          LEASE_LIABILITY_ACCOUNT_TYPE_INVALID:
            'A conta configurada para o passivo de arrendamento não é de PASSIVO com natureza ' +
            'credora. O passivo é a obrigação de pagar as contraprestações restantes. Corrija o ' +
            'mapeamento da conta antes de reconhecer.',
          LEASE_LIABILITY_ACCOUNTS_NOT_SEGREGATED:
            'O passivo circulante e o não circulante de arrendamento estão mapeados para a mesma ' +
            'conta. O CPC 26 §66-70 exige essa separação na apresentação — peça para configurar ' +
            'contas diferentes antes de reconhecer.',
          LEASE_INTEREST_EXPENSE_ACCOUNT_UNRESOLVED:
            'O plano de contas não tem uma conta mapeada para a despesa financeira do ' +
            'arrendamento (slot do sistema não configurado). Peça para configurar o mapeamento ' +
            'de contas antes de reconhecer.',
          LEASE_INTEREST_EXPENSE_ACCOUNT_TYPE_INVALID:
            'A conta mapeada para a despesa financeira do arrendamento não é uma conta de ' +
            'resultado de natureza devedora. Peça para corrigir o mapeamento de contas antes de ' +
            'reconhecer.',
          LEASE_MEASUREMENT_DISCOUNT_RATE_INVALID:
            'A taxa de desconto registrada para um dos livros não é utilizável. O CPC 06 R2 §26 ' +
            'exige a taxa implícita no contrato ou a taxa incremental de empréstimo, como uma ' +
            'fração decimal positiva. Corrija a taxa no cadastro do contrato.',
          LEASE_MEASUREMENT_DIRECT_COSTS_INVALID:
            'O valor dos custos diretos iniciais registrado no contrato não é válido. Corrija-o ' +
            'no cadastro do contrato.',
          LEASE_MEASUREMENT_RESTORATION_INVALID:
            'O valor da provisão de restauração registrado no contrato não é válido. Corrija-o ' +
            'no cadastro do contrato.',
          LEASE_MEASUREMENT_BAD_DATE:
            'A data de início registrada no contrato é inválida. Corrija-a no cadastro do ' +
            'contrato.',
          LEASE_MEASUREMENT_FREQUENCY_INVALID:
            'A periodicidade de pagamento registrada no contrato não é suportada. Corrija-a no ' +
            'cadastro do contrato.',
          LEASE_MEASUREMENT_TERM_INVALID:
            'O prazo registrado no contrato não é um número inteiro positivo de meses. Corrija-o ' +
            'no cadastro do contrato.',
          LEASE_MEASUREMENT_TERM_NOT_WHOLE_PERIODS:
            'O prazo do contrato não é um número inteiro de períodos de pagamento — parcelas ' +
            'finais parciais não são suportadas. Corrija o prazo no cadastro do contrato.',
          LEASE_MEASUREMENT_PAYMENT_INVALID:
            'O valor da parcela registrado no contrato não é válido. Corrija-o no cadastro do ' +
            'contrato.',
          LEASE_MEASUREMENT_STEP_RATE_INVALID:
            'O percentual do degrau contratual registrado no contrato não é uma fração decimal ' +
            'utilizável. Corrija-o no cadastro do contrato.',
          LEASE_MEASUREMENT_PAYMENT_DUE_DAY_INVALID:
            'O dia de vencimento registrado no contrato não é um dia do mês válido. Corrija-o no ' +
            'cadastro do contrato.',
          NO_OPEN_PERIOD:
            'O período contábil da data de início do arrendamento está fechado em algum livro. ' +
            'Reabra o período antes de reconhecer, ou fale com quem fechou o período.',
        },
      },
      settlement: {
        errors: {
          LEASE_CONTRACT_NOT_ACTIVE:
            'Somente um contrato ativo tem passivo a liquidar: um contrato em rascunho ainda não ' +
            'foi reconhecido, e um encerrado não tem mais nada a pagar.',
          LEASE_INSTALLMENT_NOT_PENDING:
            'Este contrato não tem essa parcela pendente. Ela já foi liquidada, foi cancelada ' +
            'por uma remensuração, existe apenas em livro memo, ou está fora do prazo do ' +
            'contrato.',
          LEASE_SETTLEMENT_ALREADY_LINKED:
            'Esta parcela já está vinculada a outra conta a pagar. Reapontá-la deixaria aquela ' +
            'conta lançar aluguel como despesa enquanto esta liquida o passivo, reconhecendo o ' +
            'mesmo aluguel duas vezes. Desvincule a parcela primeiro.',
          LEASE_SETTLEMENT_AMOUNT_MISMATCH:
            'O valor classificado na linha da conta a pagar não é igual à contraprestação da ' +
            'parcela. A linha permanece bruta — ela é a base do IRRF —, então precisa bater ' +
            'exatamente com o pagamento contratual. Se o aluguel em si mudou, isso é uma ' +
            'remensuração (CPC 06 R2 §42), não a liquidação de um valor diferente.',
          LEASE_SETTLEMENT_BILL_ALREADY_POSTED:
            'Esta conta a pagar já foi contabilizada, e o débito dela foi para a conta que a ' +
            'classificação indicou. A liquidação redireciona esse débito no momento em que a ' +
            'conta é lançada, então não pode ser anexada depois. Estorne a conta a pagar, ' +
            'vincule a parcela e lance de novo.',
          LEASE_SETTLEMENT_BILL_ALREADY_SETTLES_ANOTHER_INSTALLMENT:
            'Esta conta a pagar já liquida outra parcela em um destes livros. Uma conta liquida ' +
            'uma parcela por livro: a liquidação viaja no lançamento único da conta em cada ' +
            'livro, e duas parcelas exigiriam duas linhas contra ele. Use uma conta a pagar por ' +
            'parcela.',
          LEASE_SETTLEMENT_BILL_LEVEL_AMBIGUOUS:
            'A conta a pagar está classificada no nível da conta, e essa única classificação cobre ' +
            'mais de uma linha. A liquidação redirecionaria a conta inteira para o passivo de ' +
            'arrendamento, não só a linha do aluguel. Classifique a conta por linha e vincule a ' +
            'linha do aluguel.',
          LEASE_SETTLEMENT_BILL_LINE_ALREADY_LINKED:
            'Esta linha da conta a pagar já liquida outra parcela. Uma linha bruta liquida uma ' +
            'parcela: vinculá-la duas vezes dividiria o mesmo pagamento entre duas linhas de ' +
            'amortização e liquidaria uma delas com dinheiro que nunca foi pago.',
          LEASE_SETTLEMENT_BILL_LINE_NOT_CLASSIFIED:
            'A linha da conta a pagar não tem classificação. A liquidação redireciona o débito ' +
            'que a classificação produz, então é preciso haver uma antes de o vínculo significar ' +
            'alguma coisa.',
          LEASE_SETTLEMENT_BILL_LINE_NOT_FOUND:
            'A linha selecionada não pertence a esta conta a pagar. Atualize a página e escolha ' +
            'uma linha da conta correta.',
          LEASE_SETTLEMENT_BILL_WRONG_ENTITY:
            'A conta a pagar e o contrato de arrendamento pertencem a entidades diferentes. O ' +
            'passivo e o pagamento que o liquida são da mesma entidade, ou o débito atravessaria ' +
            'a fronteira de uma pessoa jurídica.',
          LEASE_SETTLEMENT_BILL_WRONG_VENDOR:
            'A conta a pagar é devida a um fornecedor diferente do arrendador do contrato. Um ' +
            'pagamento a outra pessoa não quita este passivo de arrendamento.',
          LEASE_SETTLEMENT_CLASSIFICATION_MISSING:
            'A parcela está vinculada a uma linha desta conta a pagar, mas a linha não carrega ' +
            'mais uma classificação. Lançar agora debitaria despesa de aluguel e deixaria o ' +
            'passivo de arrendamento em pé, reconhecendo o mesmo aluguel duas vezes. ' +
            'Reclassifique a linha, ou desvincule a parcela se esta conta não for o pagamento ' +
            'dela.',
          LEASE_SETTLEMENT_COMPETENCY_MISMATCH:
            'A parcela e a conta a pagar estão em competências diferentes. A liquidação é ' +
            'lançada no próprio lançamento da conta, então um mês diferente reconheceria a ' +
            'despesa financeira deste período em outro.',
          LEASE_SETTLEMENT_LINK_RACED:
            'Esta parcela foi vinculada ou liquidada por outra requisição enquanto esta a ' +
            'validava. A tentativa foi desfeita em vez de sobrescrever o vínculo que venceu. ' +
            'Atualize a página e tente novamente.',
          LEASE_SETTLEMENT_NOT_LINKED:
            'Esta parcela não está vinculada a nenhuma conta a pagar, então não há vínculo a ' +
            'desfazer.',
        },
      },
      reclassification: {
        errors: {
          LEASE_LIABILITY_RECLASSIFICATION_DATE_REGRESSIVE:
            'A reclassificação deste contrato não pode retroceder para a data informada, porque ' +
            'uma apresentação posterior já foi registrada no livro. Reverta ou superseda o ' +
            'evento posterior antes de alterar a data-base.',
          LEASE_LIABILITY_RECLASSIFICATION_RACED:
            'Este contrato foi reclassificado por outra requisição enquanto esta o media. A ' +
            'tentativa foi desfeita em vez de lançar uma apresentação de circulante e não ' +
            'circulante desatualizada. Tente novamente.',
          NO_OPEN_PERIOD:
            'O período contábil da data-base da reclassificação está fechado em algum livro. Reabra o ' +
            'período ou escolha uma data-base dentro de um período aberto.',
          LEASE_RECLASSIFICATION_DATE_INVALID:
            'A data-base da reclassificação é inválida. Use uma data real no formato AAAA-MM-DD.',
        },
      },
    },
    remeasure: {
      action: 'Remensurar por índice',
      title: 'Remensuração por índice',
      description:
        'O índice vem do catálogo BCB SGS sincronizado diariamente. A observação usada e a data ' +
        'de referência ficam gravadas no evento.',
      noIndex:
        'Este contrato não tem índice econômico configurado. Reajuste por índice exige um código ' +
        'de série no cadastro do contrato.',
      contractIndex: 'Índice do contrato',
      observationUsed: 'Observação mais recente',
      referenceDate: 'Data de referência',
      value: 'Valor',
      freshness: 'Frescor',
      freshnessState: {
        FRESH: 'Atualizado',
        STALE: 'Atrasado',
        MISSING: 'Sem observação',
        UNKNOWN: 'Desconhecido',
      },
      staleWarning:
        'A série está atrasada em relação à cadência esperada. A remensuração usará a última ' +
        'observação publicada; confira antes de aplicar.',
      missingWarning:
        'Nenhuma observação sincronizada para esta série. A remensuração será bloqueada até que ' +
        'a sincronização do BCB SGS traga o dado.',
      lastSync: 'Última sincronização',
      neverSynced: 'Nunca sincronizado',
      effectiveDate: 'Data de vigência',
      effectiveDateHint: 'Aniversário do reajuste — a data a partir da qual a nova parcela vale.',
      competencyDate: 'Competência',
      competencyDateHint: 'Mês em que o efeito é lançado. Padrão: o mês da vigência.',
      rationale: 'Justificativa',
      rationalePlaceholder: 'Contexto do reajuste (opcional).',
      submit: 'Calcular e aplicar',
      cancel: 'Cancelar',
      preview: 'Resultado',
      blocked: 'Remensuração bloqueada',
      blockedReason: 'Motivo: {{reason}}',
      oldPayment: 'Contraprestação atual',
      newPayment: 'Nova contraprestação',
      adjustment: 'Reajuste',
      formula: 'Fórmula',
      success: 'Remensuração aplicada.',
      error: 'Não foi possível aplicar a remensuração.',
      errors: {
        LEASE_INDEX_REMEASUREMENT_ALREADY_APPLIED:
          'Já existe uma remensuração por índice para este contrato nesta data-base. Reverta ou ' +
          'superseda o evento existente antes de aplicar outro reajuste.',
        LEASE_INDEX_REMEASUREMENT_NOT_APPLICABLE:
          'Só um contrato ativo e ligado ao catálogo de índices econômicos pode ser remensurado ' +
          'por índice. Ative o contrato, ou informe o código da série no cadastro dele.',
        LEASE_INDEX_REMEASUREMENT_RECLASSIFICATION_DATE_REGRESSIVE:
          'A remensuração não pode vigorar antes de uma reclassificação de circulante já ' +
          'registrada no livro. Reverta ou superseda essa apresentação posterior antes de ' +
          'recalcular a régua.',
        LEASE_ORIGINAL_DISCOUNT_RATE_MISSING:
          'Um dos livros que reconhece este contrato não tem taxa de desconto registrada. A ' +
          'remensuração por índice desconta as novas contraprestações pela taxa original, então ' +
          'sem ela não há como recalcular o passivo. Informe a taxa desse livro no cadastro do ' +
          'contrato antes de remensurar.',
        LEASE_REMEASUREMENT_RESULT_ACCOUNT_UNRESOLVED:
          'A conta de resultado da remensuração do arrendamento não está configurada para esta ' +
          'entidade. Configure a conta do slot de resultado de remensuração ' +
          '(FA_LEASE_REMEASUREMENT_RESULT) nas contas do sistema e tente novamente.',
        NO_OPEN_PERIOD:
          'O período contábil da data da remensuração está fechado em algum livro. Reabra o período ' +
          'ou escolha uma data dentro de um período aberto.',
        LEASE_REMEASUREMENT_ROU_SCHEDULE_RACED:
          'A régua de amortização do direito de uso foi alterada por outra operação — uma ' +
          'revisão ou o fechamento da depreciação — durante esta remensuração. Nada foi salvo; ' +
          'refaça a remensuração.',
      },
    },
  },

  register: {
    newAssetAction: 'Novo ativo',
    pageTitle: 'Novo ativo imobilizado',
    breadcrumbRegistry: 'Ativos imobilizados',
    saveFailed: 'Não foi possível salvar o ativo',

    manualNotice: {
      title: 'Cadastro manual — o caminho de exceção',
      body:
        'Existe porque às vezes é necessário: um bem comprado sem nota no Alchemy, ou uma base ' +
        'que veio de outro sistema. Mas não deve virar hábito — o bem cadastrado aqui nasce sem ' +
        'documento vinculado. Se a NF-e já entrou, ela está esperando na fila de revisão, onde o ' +
        'ativo nasce amarrado à nota e à conta a pagar que o originou.',
      link: 'Abrir a fila de revisão',
    },

    entity: {
      title: 'Entidade',
      hint:
        'Todo bem pertence a uma entidade: as regras da classe, o limite de capitalização e o ' +
        'plano de contas mudam com ela.',
      placeholder: 'Selecione a entidade',
    },

    origin: {
      title: 'Origem',
      hint: 'Decide se a capitalização lança no razão ou não.',
      NO_DOCUMENT: {
        label: 'Sem documento',
        description:
          'Bem comprado de verdade, sem nota no Alchemy. A aquisição nunca chegou ao razão, ' +
          'então capitalizar lança D imobilizado / C a conta que pagou.',
      },
      MIGRATED: {
        label: 'Base migrada',
        description:
          'Bem que a empresa já tinha na virada. A aquisição entrou no razão pelos saldos de ' +
          'abertura, então capitalizar não lança nada — lançaria o imobilizado duas vezes. Em ' +
          'vez disso, ele traz a depreciação já acumulada.',
      },
    },

    identification: {
      title: 'Identificação',
      assetCode: 'Código do ativo',
      assetCodePlaceholder: 'Em branco reserva um código a partir da classe',
      description: 'Descrição',
      class: 'Classe',
      classPlaceholder: 'Selecione a classe',
      classNeedsEntity: 'Selecione a entidade primeiro',
      classNoneMeasurable: 'Nenhuma classe medida nesta entidade',
      classNoBookRule: 'sem régua nesta entidade',
      classSearchPlaceholder: 'Buscar por nome, código ou descrição',
      classNoMatch: 'Nenhuma classe encontrada.',
      classLeaseBound: 'prazo do contrato',
      classNeverDepreciates: 'Não deprecia',
      classUsefulLife: 'Vida útil padrão: {{months}} meses',
      classGroup: {
        PROPERTY_PLANT_EQUIPMENT: 'Imobilizado — CPC 27',
        RIGHT_OF_USE: 'Direito de uso — CPC 06 (R2)',
        INTANGIBLE: 'Intangível — CPC 04',
      },
      acquisitionDate: 'Data de aquisição',
      inServiceDate: 'Disponível para uso em',
      inServiceDateHint:
        'A depreciação começa nesta data (CPC 27: quando o bem está disponível para uso). Acompanha a data de aquisição até você alterar.',
      acquisitionCost: 'Custo de aquisição',
      serialNumber: 'Número de série',
      custodian: 'Custodiante',
      custodianPlaceholder: 'Selecione o responsável',
      location: 'Localização',
      notes: 'Notas',
    },

    books: {
      title: 'Réguas por livro',
      hint:
        'Cada livro mede o mesmo bem com a sua própria régua. Em branco, o livro herda o padrão ' +
        'da classe.',
      classDefault: 'Padrão da classe: {{months}} meses · residual {{residual}} · {{norm}}',
      noClassDefault: 'Selecione uma classe para ver o padrão deste livro.',
      usefulLifeOverride: 'Vida útil (meses)',
      residualOverride: 'Valor residual (fração)',
    },

    temporaryDifference: {
      noBooks: 'Nenhum livro de mensuração configurado para esta entidade.',
      singleBook: 'Um único livro configurado ({{book}}) — nenhuma diferença a apurar.',
      notAPair:
        'Mais de dois livros configurados: a diferença temporária só é definida para um par.',
      incomplete: 'Informe classe, custo e data de aquisição para apurar a diferença temporária.',
      none: 'Nenhuma diferença temporária será gerada.',
      monthly: 'Diferença mensal',
      firstYear: 'Primeiras {{count}} parcelas',
      adicao: {
        title: 'Esta régua gera uma adição na Parte A do e-Lalur',
        badge: 'Adição',
        explanation:
          'O livro fiscal ({{tax}}) deprecia menos que o societário ({{statutory}}), então o ' +
          'lucro tributável sobe pela diferença.',
      },
      exclusao: {
        title: 'Esta régua gera uma exclusão na Parte A do e-Lalur',
        badge: 'Exclusão',
        explanation:
          'O livro fiscal ({{tax}}) deprecia mais que o societário ({{statutory}}), então o ' +
          'lucro tributável cai pela diferença.',
      },
      art42: {
        title: 'Esta régua gera uma adição mensal na Parte A do e-Lalur',
        badge: 'Origem: art. 42',
        explanation:
          'O custo deste projeto já foi excluído do lucro real na competência do gasto ' +
          '(Lei 12.973, art. 42), então o livro fiscal ({{tax}}) não tem base a amortizar. ' +
          'Toda a amortização do livro societário ({{statutory}}) volta como adição, mês a ' +
          'mês, até reverter a exclusão.',
      },
    },

    opening: {
      title: 'Posição de abertura',
      perBookNote:
        'A depreciação acumulada é pedida por livro, e não uma vez só, de propósito. Repetir um ' +
        'único saldo nos dois livros zera a diferença temporária do primeiro ano — que é ' +
        'exatamente o número que a Parte A do e-Lalur reporta.',
      asOf: 'Data de corte',
      accumulated: 'Depreciação acumulada',
      remainingLife: 'Vida útil restante (meses)',
    },

    counterAccount: {
      title: 'Contrapartida da capitalização',
      hint:
        'A conta creditada pelo custo: o banco que pagou ou o passivo que deve. Exigida só na ' +
        'origem sem documento — um bem migrado já teve a aquisição lançada.',
      label: 'Conta de contrapartida',
      placeholder: 'Selecione a conta',
    },

    allocation: {
      title: 'Alocação',
      hint: 'Onde a depreciação deste bem vai aparecer no gerencial.',
      costCenter: 'Centro de custo',
      department: 'Departamento',
      managerialAccount: 'Conta gerencial',
      selectEntityFirst: 'Selecione a entidade primeiro',
    },

    check: {
      title: 'Verificação de capitalização',
      threshold: {
        unknown: 'Custo não informado',
        unresolved: 'Limite não configurado',
        clears: 'Acima do limite',
        below: 'Abaixo do limite',
        hint: 'Limite de capitalização desta entidade: {{threshold}}.',
        unresolvedHint: 'Configure a política de capitalização desta entidade antes de confirmar.',
      },
      journal: {
        title: 'Lançamento previsto',
        side: { DEBIT: 'D', CREDIT: 'C' },
        noPosting:
          'Nada será lançado: a aquisição de um bem migrado já entrou pelos saldos de abertura.',
        hint: 'Prévia. As contas definitivas são resolvidas no servidor na capitalização.',
      },
      openingAccumulated: 'Acumulada de abertura (livro contábil)',
      netBookValue: 'Valor contábil líquido',
      problems: {
        title: 'Pendências',
        none: 'Nenhuma pendência.',
      },
    },

    problems: {
      entityRequired: 'Selecione a entidade do bem.',
      classRequired: 'Selecione a classe do bem.',
      descriptionRequired: 'Informe a descrição do bem.',
      acquisitionDateRequired: 'Informe uma data de aquisição válida (dd/mm/aaaa).',
      inServiceBeforeAcquisition:
        'A data de disponibilidade para uso não pode ser anterior à data de aquisição.',
      openingBeforeInService:
        'A data de corte não pode ser anterior à data em que o bem ficou disponível para uso.',
      costRequired: 'Informe o custo de aquisição.',
      belowThreshold:
        'O custo está abaixo do limite efetivo desta classe. Registre a compra como despesa.',
      policyUnresolved: 'Não há uma política de capitalização efetiva para esta entidade e classe.',
      currencyUnresolved:
        'Não foi possível determinar a moeda funcional desta entidade. Nenhum livro ativo a ' +
        'mede, ou os livros dela discordam entre si — configure isso antes de informar o custo.',
      classHasNoBookRule:
        'Nenhum livro desta entidade mede esta classe, então o bem nasceria sem régua. ' +
        'Configure uma regra de classe por livro antes de cadastrar.',
      counterAccountRequired: 'Informe a conta de contrapartida da capitalização.',
      openingAsOfRequired: 'Informe a data de corte das posições de abertura.',
      openingBookMissing: 'Informe a depreciação acumulada de cada livro. Faltam: {{books}}.',
      openingExceedsCost:
        'A depreciação acumulada não pode passar do custo de aquisição. Revise: {{books}}.',
      assetCodeRequiredForCapitalization:
        'Informe o código do ativo para capitalizar: sem ele, reenviar o formulário criaria um ' +
        'segundo bem.',
    },

    /**
     * `POST /assets` maps most of its stable error codes to an actionable pt-BR string here —
     * see `getFixedAssetErrorMessage` in `@/lib/assets-error-messages`, which falls back to
     * the server’s own message (still sanitised) for any code this map does not name, on
     * purpose for the few codes whose whole point is a number this map cannot reproduce (a gap
     * amount) or that already arrive as a complete pt-BR sentence from the server.
     */
    errors: {
      FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
        'Créditos de PIS/COFINS do imobilizado estão disponíveis somente para entidades brasileiras. Revise a entidade do ativo e a origem fiscal.',
      FIXED_ASSET_IN_SERVICE_BEFORE_ACQUISITION:
        'A data de entrada em uso não pode ser anterior à data de aquisição: um bem não pode ' +
        'estar em uso antes de ser adquirido.',
      FIXED_ASSET_ACQUISITION_COST_INVALID:
        'O custo de aquisição deve ser maior que zero. Informe o custo bruto original, não o ' +
        'saldo residual.',
      FIXED_ASSET_UNIT_QUANTITY_INVALID: 'A quantidade de unidades deve ser maior que zero.',
      FIXED_ASSET_CAPITALIZATION_POLICY_NOT_FOUND:
        'Não há uma política de capitalização efetiva para esta entidade e classe.',
      FIXED_ASSET_BELOW_CAPITALIZATION_THRESHOLD:
        'O custo está abaixo do limite efetivo de capitalização. Registre a compra como despesa.',
      FIXED_ASSET_MIGRATED_COUNTER_ACCOUNT_REFUSED:
        'Um bem migrado não aceita conta de contrapartida: a aquisição já está no saldo de ' +
        'abertura, então não há um segundo lançamento a fazer.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_REQUIRED:
        'Informe a conta de contrapartida para capitalizar um bem sem documento: nada foi ' +
        'lançado ainda, então o lançamento precisa creditar o banco que pagou ou o passivo que ' +
        'ainda se deve.',
      FIXED_ASSET_CODE_REQUIRED_FOR_CAPITALIZATION:
        'Informe o código do ativo para confirmar o cadastro: sem ele, um reenvio do formulário ' +
        'criaria um segundo bem.',
      FIXED_ASSET_OPENING_POSITION_NOT_MIGRATED:
        'Uma posição de abertura só existe para um bem migrado. Um bem sem documento está sendo ' +
        'capitalizado agora e ainda não tomou nenhuma depreciação.',
      FIXED_ASSET_MIGRATED_CUTOVER_DATE_REQUIRED:
        'Informe a data de corte para capitalizar um bem migrado, mesmo que ele nunca tenha sido ' +
        'depreciado: sem ela, a única data disponível seria a de aquisição, o que depreciaria ' +
        'retroativamente competências já fechadas no sistema anterior.',
      FIXED_ASSET_OPENING_POSITION_DATE_REQUIRED:
        'Informe a data de corte sempre que houver uma posição de abertura: um valor acumulado ' +
        'sem data não pode ser posicionado na linha do tempo.',
      FIXED_ASSET_OPENING_POSITION_IN_FUTURE:
        'A data de corte não pode estar no futuro: a posição de abertura registra depreciação ' +
        'que já ocorreu.',
      FIXED_ASSET_OPENING_POSITION_BEFORE_IN_SERVICE:
        'A data de corte é anterior à data de entrada em uso do bem, então a depreciação ' +
        'acumulada informada não poderia ter sido tomada.',
      FIXED_ASSET_OPENING_POSITION_DUPLICATED:
        'Um dos livros aparece mais de uma vez na posição de abertura. Informe cada livro uma ' +
        'única vez.',
      FIXED_ASSET_OPENING_POSITION_NEGATIVE:
        'A depreciação acumulada informada é negativa. Informe um valor maior ou igual a zero.',
      FIXED_ASSET_OPENING_POSITION_EXCEEDS_COST:
        'A depreciação acumulada de um dos livros é maior que o custo de aquisição, o que ' +
        'geraria um valor contábil líquido negativo.',
      ENTITY_NOT_FOUND:
        'A entidade selecionada não foi encontrada. Atualize a página e tente novamente.',
      FIXED_ASSET_NOT_FOUND: 'Ativo não encontrado. Atualize a página e tente novamente.',
      FIXED_ASSET_NOT_DRAFT:
        'Este ativo não é mais um rascunho — já foi capitalizado ou alterado por outra pessoa. ' +
        'Atualize a página.',
      FIXED_ASSET_DRAFT_NOT_HAND_REGISTERED:
        'Só rascunhos cadastrados manualmente podem ser editados, excluídos ou capitalizados ' +
        'aqui. Este veio de outra origem (nota fiscal ou contrato).',
      FIXED_ASSET_PROMOTE_OPENING_POSITIONS_REQUIRED:
        'Este bem migrado não tem posição de abertura salva. Edite o rascunho e informe a ' +
        'depreciação acumulada até o corte em cada livro — 0 se nunca foi depreciado.',
      FIXED_ASSET_DRAFT_BOOK_RULES_STALE:
        'Os livros que medem a entidade mudaram desde que este rascunho foi salvo. Edite e salve ' +
        'o rascunho para refazer as réguas de depreciação antes de capitalizar.',
      FIXED_ASSET_DRAFT_CURRENCY_STALE:
        'Os livros da entidade agora medem em outra moeda que a deste rascunho. Exclua o ' +
        'rascunho e cadastre o bem de novo na moeda dos livros.',
      FIXED_ASSET_CODE_TAKEN:
        'Já existe um bem cadastrado com este código. Se for o mesmo bem, o cadastro já foi ' +
        'feito; se for outro, escolha um código diferente.',
      FIXED_ASSET_REGISTRATION_FAILED:
        'Não foi possível concluir o cadastro do bem. Tente novamente ou fale com a Alchemy se o ' +
        'problema persistir.',
      FIXED_ASSET_BOOK_OVERRIDE_BOOK_NOT_MEASURED:
        'Um dos livros com régua sobreposta não mede este bem. Apenas livros cuja regra de ' +
        'classe está no escopo da entidade podem ser ajustados.',
      FIXED_ASSET_BOOK_OVERRIDE_LIFE_INVALID:
        'A vida útil sobreposta precisa ser de pelo menos um mês.',
      FIXED_ASSET_BOOK_OVERRIDE_RESIDUAL_INVALID:
        'O valor residual sobreposto precisa ser uma fração entre 0 e 1 (exclusive). Um residual ' +
        'igual ao custo total não depreciaria nada.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_NOT_FOUND:
        'A conta de contrapartida informada não existe neste tenant.',
      ASSET_CAPITALIZATION_CLASSIFICATION_MULTIPLE_CLASSES:
        'Esta classificação capitaliza bens de mais de uma classe. Todo o débito iria para a ' +
        'conta de custo da primeira classe. Classifique a nota por linha, para cada classe ' +
        'chegar à sua própria conta.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_TYPE_INVALID:
        'A conta de contrapartida precisa ser ativa, lançável, não ser uma contra-conta, e ser ' +
        'do tipo ATIVO (o banco ou caixa que pagou) ou PASSIVO (o que ainda se deve).',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_WRONG_ENTITY:
        'A conta de contrapartida pertence a um plano de contas que esta entidade não utiliza. ' +
        'Selecione uma conta do plano de contas desta entidade.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_IS_SUBLEDGER:
        'A conta de contrapartida pertence ao subledger de um fornecedor ou cliente. Registre a ' +
        'nota como uma conta a pagar, ou credite uma conta de caixa ou banco.',
      FIXED_ASSET_DATE_INVALID: 'Uma das datas informadas não é válida.',
      FIXED_ASSET_AMOUNT_INVALID: 'Um dos valores informados não é um número decimal válido.',
      FIXED_ASSET_OPENING_POSITION_BOOK_MISSING:
        'Informe a depreciação acumulada de todos os livros que medem este bem. Zero é uma ' +
        'resposta válida, mas precisa ser informada — repetir um único valor entre livros ' +
        'destruiria a diferença temporária que a Parte A do e-Lalur reporta.',
      FIXED_ASSET_OPENING_POSITION_BOOK_NOT_MEASURED:
        'Um dos livros informados na posição de abertura não mede este bem. Apenas livros com ' +
        'regra de classe no escopo da entidade podem receber uma posição de abertura.',
      FIXED_ASSET_OPENING_REMAINING_LIFE_INVALID:
        'A vida útil remanescente informada é negativa. Informe um valor maior ou igual a zero.',
      FIXED_ASSET_CAPITALIZATION_CURRENCY_UNSUPPORTED:
        'O bem está denominado em uma moeda diferente da moeda funcional de um dos livros que ' +
        'medem esta entidade. Configure uma régua na mesma moeda funcional, ou traga o bem por ' +
        'um caminho que carregue a taxa e a evidência do câmbio, antes de capitalizar.',
      FIXED_ASSET_CAPITALIZATION_EXPENSE_ACCOUNT_AMBIGUOUS:
        'A linha da conta a pagar foi classificada em mais de uma conta contábil, então não ' +
        'existe uma única conta de despesa da qual reclassificar o custo. Nada foi gravado. ' +
        'Deixe a linha com uma única conta de despesa e capitalize de novo, ou estorne e relance ' +
        'a nota com a classificação correta.',
      FIXED_ASSET_CAPITALIZATION_NOT_DRAFT:
        'Este bem não está mais em rascunho: o custo dele já chegou ao razão, e capitalizar de ' +
        'novo debitaria imobilizado uma segunda vez. Atualize a página para ver o estado atual.',
      FIXED_ASSET_CAPITALIZATION_NO_OPEN_PERIOD:
        'Nenhuma data a partir da informada está aberta em todos os livros desta entidade, então ' +
        'a capitalização não tem onde ser lançada. Reabra o período, ou escolha uma data dentro ' +
        'de um período aberto.',
      ASSET_STATUS_CONCURRENT_CHANGE:
        'Outra pessoa alterou o status deste bem enquanto você trabalhava, e a operação foi ' +
        'desfeita. Atualize a página para ver o estado atual e tente novamente.',
      FIXED_ASSET_CLASS_FRAMEWORK_MISMATCH:
        'A classe do bem pertence a um padrão contábil diferente do dos livros desta entidade. ' +
        'Escolha uma classe do mesmo padrão contábil da entidade ou ajuste o cadastro da classe.',
      FIXED_ASSET_CLASS_MISSING_BOOK_RULE:
        'A classe deste bem não tem régua de depreciação nos livros que medem esta entidade. Sem ' +
        'ela o bem ficaria sem base fiscal e sem diferença temporária para a Parte A do e-Lalur. ' +
        'Configure a régua da classe em cada um desses livros antes de capitalizar.',
      FIXED_ASSET_CODE_AMBIGUOUS:
        'Um único código de ativo foi informado para mais de um bem, e não há como dizer a qual ' +
        'deles ele pertence. Cadastre um bem por vez, ou deixe o código em branco para ele ser ' +
        'reservado automaticamente.',
      FIXED_ASSET_COST_ACCOUNT_UNRESOLVED:
        'A classe deste bem aponta o custo para uma conta que não é uma conta de ativo ativa, ' +
        'lançável, não contra-conta e de natureza devedora. Corrija o mapeamento da classe — ou ' +
        'o slot de conta de sistema do imobilizado — antes de capitalizar.',
      FIXED_ASSET_COUNT_INVALID:
        'Uma capitalização confirmada precisa criar pelo menos um ativo. Informe uma quantidade ' +
        'maior que zero.',
      FIXED_ASSET_METHOD_NOT_IMPLEMENTED:
        'A régua da classe usa um método de depreciação que ainda não é suportado: somente o ' +
        'método linear está implementado. Ajuste o método na régua da classe antes de ' +
        'capitalizar.',
      FIXED_ASSET_MIGRATED_ACCUMULATED_EXCEEDS_LEDGER:
        'A depreciação acumulada informada é maior do que o razão registra na conta da classe ' +
        'até a data de corte. Um bem migrado não lança nada: o valor precisa já estar no razão. ' +
        'Confira o valor informado, ou importe os saldos de abertura que faltam, antes de ' +
        'confirmar a migração.',
      FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
        'A conta de depreciação acumulada mapeada para a classe não é de natureza credora. ' +
        'Corrija o mapeamento da classe e tente novamente.',
      FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
        'A conta de depreciação acumulada mapeada para a classe não é uma conta de ativo. ' +
        'Corrija o mapeamento da classe e tente novamente.',
      FIXED_ASSET_MIGRATED_COST_EXCEEDS_LEDGER:
        'O custo informado é maior do que o razão registra na conta da classe até a data de ' +
        'corte. Um bem migrado não lança nada: o valor precisa já estar no razão. Confira o ' +
        'valor informado, ou importe os saldos de abertura que faltam, antes de confirmar a ' +
        'migração.',
      FIXED_ASSET_MIGRATED_LEDGER_BALANCE_ABSENT:
        'A conta de custo da classe não tem nenhum lançamento contabilizado nesta entidade até a ' +
        'data de corte. Um bem migrado é, por definição, um bem cujo custo já está no razão. ' +
        'Importe os saldos de abertura da entidade — ou corrija o mapeamento de contas da classe ' +
        '— antes de confirmar a migração.',
      FIXED_ASSET_MIGRATED_LEDGER_PERIOD_NOT_FOUND:
        'A entidade não tem período contábil que contenha a data de corte. Gere o calendário ' +
        'contábil que cubra essa data antes de migrar bens com ela.',
      FIXED_ASSET_REGISTRATION_CURRENCY_REQUIRED:
        'Os livros que medem esta entidade não usam a mesma moeda funcional, então não há moeda ' +
        'a assumir para o bem. Informe a moeda explicitamente — e note que ela terá de coincidir ' +
        'com a de todos esses livros, porque este cadastro não carrega base de conversão.',
      FIXED_ASSET_REGISTRATION_CURRENCY_UNSUPPORTED:
        'A moeda informada para o bem difere da moeda funcional de um dos livros que medem esta ' +
        'entidade. O cadastro não carrega taxa nem evidência de câmbio, e converter o custo de ' +
        'um bem adquirido antes do corte exigiria a taxa da data da transação (CPC 02 (R2) §21). ' +
        'Cadastre o bem na moeda funcional do livro, ou traga-o por um caminho que carregue a ' +
        'base de conversão com evidência.',
      FIXED_ASSET_REGISTRATION_NO_MEASURING_BOOK:
        'Nenhum livro ativo mede esta entidade, então o bem não tem moeda funcional nem régua ' +
        'para congelar. Configure um livro que meça esta entidade antes de cadastrar o bem.',
      FIXED_ASSET_IDEMPOTENCY_KEY_REQUIRED:
        'Este cadastro foi enviado sem a chave de idempotência (cabeçalho Idempotency-Key), ' +
        'então o servidor não teria como distinguir um reenvio de um segundo bem e recusou a ' +
        'requisição. Nenhum bem foi criado: recarregue a página e cadastre novamente.',
    },
    success: 'Ativo {{code}} registrado.',

    footer: {
      costSummary: 'Custo de aquisição: {{cost}}',
      saveDraft: 'Salvar rascunho',
      createAndCapitalize: 'Criar e capitalizar',
    },

    edit: {
      pageTitle: 'Editar rascunho {{code}}',
      breadcrumb: 'Editar rascunho',
      save: 'Salvar alterações',
      cancel: 'Cancelar',
      saveFailed: 'Não foi possível salvar o rascunho',
      loadFailed: 'Não foi possível carregar o rascunho',
      notEditableTitle: 'Este ativo não pode ser editado aqui',
      success: 'Rascunho {{code}} atualizado.',
      notEditable:
        'O ativo {{code}} não é um rascunho cadastrado manualmente, então não pode ser editado aqui.',
      backToAsset: 'Voltar ao ativo',
      loading: 'Carregando o rascunho',
    },
  },
};

export const assetsEn: typeof assetsPtBr = {
  intangibleArt42: {
    title: 'Art. 42 exclusion (Lei 12.973)',
    label: 'Exclude development cost in the competência of the spend',
    help:
      'On, each of this entity’s projects has its capitalised cost excluded from taxable ' +
      'profit in the month it was spent, and the asset is born with a zero fiscal base. Off ' +
      '(the default), the cost is amortised in the fiscal book like any other asset. A ' +
      'loss-making company usually gains nothing by deducting today: a tax loss offsets only ' +
      '30% of each following year’s profit — the accountant decides.',
    reasonLabel: 'Reason for the change',
    reasonPlaceholder: 'Why is this entity’s regime changing?',
    notRetroactive:
      'The change applies only to projects created from now on. Running projects keep the ' +
      'answer they froze at creation — a new regime does not reinterpret months a project has ' +
      'already closed.',
    historyTitle: 'History',
    historyEntry: 'From {{from}} to {{to}} on {{at}}',
    value: { true: 'on', false: 'off' },
    saved: 'Art. 42 setting saved.',
    saving: 'Saving…',
    saveFailed: 'Could not save the art. 42 setting.',
  },
  oneFortyEighth: oneFortyEighthEn,
  common: {
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: '—',
    entity: 'Entity',
    activeFilters: 'Active filters',
    book: 'Book',
    clearFilters: 'Clear filters',
    search: 'Search',
    truncated: 'Showing the first {{limit}} results. Narrow the filters to see the rest.',
    loadError: 'Could not load the data.',
    loading: 'Loading…',
    memoBook: 'Memo book',
    memoBookHint: 'Measures without posting to the ledger — the tax régua is extra-accounting.',
    postedBook: 'Posted book',
    warnings: 'Warnings',
    apply: 'Apply',
    clear: 'Clear',
    filterColumn: 'Filter {{column}}',
    pagination: {
      showing: 'Showing {{start}}–{{end}} of {{total}}',
      noResults: 'No results',
      previous: 'Previous',
      next: 'Next',
      page: '{{current}} of {{total}}',
    },
  },

  queue: {
    title: 'Asset review queue',
    description:
      'Purchases classified as capitalisation candidates. Each row shows the rule that decided ' +
      'and the number that drove it.',
    empty: 'No candidates in this queue.',
    emptyPending: 'Nothing awaiting review. Accounts payable classification feeds this queue.',
    tabs: {
      needsDecision: 'Your decision',
      hasVerdict: 'We have a verdict',
      decided: 'Already decided',
    },
    needsDecisionHint:
      'The engine did not choose between one asset and many: unitising later is impossible, ' +
      'because an aggregated asset cannot be disposed of individually.',
    hasVerdictHint:
      'The engine proposed a verdict. Confirming keeps it; changing it records an override.',
    columns: {
      source: 'Source',
      line: 'Bill line',
      proposal: 'Proposal',
      drivenBy: 'What decided',
      amount: 'Line amount',
      threshold: 'Applied threshold',
      unitization: 'Unitisation',
      // ALC-3487 — shown only when a row on the page names one.
      project: 'Project',
      status: 'Status',
      decidedAt: 'Decided at',
    },
    source: {
      billReference: 'Bill {{reference}}',
      currentHint: 'Current source bill identity; decision amounts remain snapshotted.',
      lineUnavailable: 'Source line unavailable ({{id}})',
      open: 'View source',
    },
    drivenBy: {
      rule: 'Learned rule',
      threshold: 'Capitalisation threshold',
      ruleAndThreshold: 'Rule + threshold',
      unitAmount: '{{unit}} per unit × {{quantity}}',
      overThreshold: '{{amount}} above the {{threshold}} threshold',
      underThreshold: '{{amount}} below the {{threshold}} threshold',
      thresholdSnapshot:
        'Threshold snapshotted at proposal time — a later policy change does not alter this decision.',
      noRule: 'No learned rule fired.',
    },
    verdict: {
      CAPITALIZE: 'Capitalise',
      EXPENSE: 'Expense',
      // The engine also recognises a line as a lease: neither PP&E nor a plain expense, and the
      // decision goes through contract registration (CPC 06 R2).
      LEASE_SERVICE: 'Lease',
      LEASE_ASSESSMENT_REQUIRED: 'Assess as a lease',
      // ADR 0017 D3: the line is cost of a built intangible. It never becomes an asset of its
      // own — it joins the project, which becomes a single asset at go-live.
      ADD_TO_PROJECT: 'Intangible project cost',
      UNDECIDED: 'No proposal',
    },
    status: {
      PENDING: 'Pending',
      CONFIRMED: 'Confirmed',
      OVERRIDDEN: 'Overridden',
      DISCARDED: 'Discarded',
    },
    unitization: {
      PER_UNIT: 'One asset per unit',
      AGGREGATE: 'A single asset',
      ASK: 'Needs a decision',
    },
    decide: {
      action: 'Decide',
      title: 'Decide candidate',
      description:
        'The decision applies to this line and creates the assets immediately when capitalised.',
      verdict: 'Verdict',
      class: 'Asset class',
      classPlaceholder: 'Keep the proposed class',
      unitization: 'Unitisation',
      unitizationRequired: 'Required choice: the engine has no safe default here.',
      assetCount: 'Number of assets',
      project: 'Development project',
      projectPlaceholder: 'Select the project',
      projectHint:
        'Only open projects of this bill’s entity. The debit reaches "Intangible in progress" ' +
        'when the development gate is already open on the bill competência; otherwise it stays ' +
        'on the expense account and is linked to the project for information only ' +
        '(CPC 04 §54 and §57).',
      projectNoneOpen:
        'No open project on this entity. Open a development project before routing cost to it.',
      note: 'Justification',
      notePlaceholder: 'Why this decision (optional).',
      promoteToRule: 'Learn as a rule',
      promoteToRuleHint: 'A learned rule proposes; it never confirms on its own.',
      submit: 'Record decision',
      cancel: 'Cancel',
      close: 'Close',
      successCapitalized: 'Capitalised. {{count}} asset(s) created.',
      successExpensed: 'Line kept as an expense.',
      successAddedToProject: 'Line routed to the development project.',
      error: 'Could not record the decision.',
      creditPolicy: {
        title: 'PIS and COFINS credit recognition',
        description:
          'Choose one legal method for both taxes. This decision is stored on the created assets and does not affect ordinary purchases.',
        pis: 'PIS credit to recognise',
        cofins: 'COFINS credit to recognise',
        billIssueDate: 'Bill issue date (reference)',
        billCompetencyDate: 'Bill competency date (reference)',
        acquisitionDate: 'Acquisition or receipt date',
        acquisitionDateHint:
          'The issue date is prefilled only as a suggestion. Confirm the actual acquisition or receipt date; accounting competency does not replace this decision.',
        acquisitionDateConfirm: 'I confirm this is the asset’s actual acquisition or receipt date',
        accountingInService: {
          title: 'Available for use — IAS 16 / CPC 27',
          description:
            'Confirm separately when the asset became available for its intended use. In this workflow, the date must remain in the bill’s accounting competency month.',
          date: 'Date the asset became available for use',
          evidenceCode: 'Availability evidence code',
          evidenceDescription: 'Availability evidence description',
          confirm: 'I confirm this date and its accounting evidence',
        },
        notAvailable: 'Not provided',
        method: 'Recognition method',
        select: 'Select',
        methods: {
          QUOTA_1_48: 'In 48 monthly installments',
          DEPRECIATION_RFB: 'According to RFB tax depreciation',
          IMMEDIATE_NEW_MACHINE_EQUIPMENT: 'Immediate for new machinery or equipment',
        },
        commonImmediateUnsupported:
          'For common use, the legally eligible portion is determined by monthly allocation; immediate recognition is unavailable.',
        fortyEightUnsupported:
          'The 1/48 method is not yet supported in this workflow and will not be replaced with another option.',
        destination: 'Asset use',
        destinations: {
          PRODUCTION_GOODS: 'Production of goods',
          SERVICE_PROVISION: 'Provision of services',
          RENTAL_THIRD_PARTY: 'Rental to third parties',
        },
        origin: 'Acquisition origin',
        origins: {
          DOMESTIC: 'Domestic market',
          IMPORT: 'Import',
        },
        condition: 'Asset condition',
        conditions: {
          NEW: 'New',
          USED: 'Used',
        },
        usedUnsupported:
          'Credit recognition for a used asset is not yet supported. This decision cannot be completed in this workflow.',
        evidenceCode: 'Evidence code',
        evidenceDescription: 'Evidence description',
        recoverability: {
          title: 'Recoverability of the PIS/COFINS potential',
          description:
            'Record an independent accounting conclusion about realizing the full deferred potential.',
          commonDescription:
            'Record an independent accounting conclusion about realizing the portion that is legally admitted after common-use allocation.',
          conclusion: 'I confirm that full recovery of the PIS and COFINS potential is expected',
          conclusionHint:
            'This conclusion recognises the expected realization of the tax asset; legal credit eligibility alone does not establish it.',
          commonConclusion:
            'I confirm that full recovery of the portion legally admitted after common-use allocation is expected',
          commonConclusionHint:
            'Common-use allocation and the legally excluded portion are determined by period. That eligibility exclusion is not a recoverability loss.',
          evidenceCode: 'Forecast or accounting-policy reference',
          evidenceDescription: 'Basis for the recovery expectation',
          evidenceHint:
            'Cite the forecast, approved budget or accounting policy supporting full recovery. Do not merely repeat the legal eligibility basis.',
          commonEvidenceHint:
            'Cite the forecast, approved budget or accounting policy supporting full recovery of the portion legally admitted after allocation. Do not treat the legally excluded portion as a recoverability loss.',
        },
        firstUse: {
          title: 'Tax first use',
          description:
            'This workflow capitalises an asset that is already available for use. Record the tax date and evidence now only if they have been confirmed.',
          alreadyInUse: 'Record the tax first-use date now',
          deferredHint:
            'If it is not recorded now, the credit remains deferred until later confirmation on the asset; no tax instalment is generated before then.',
          date: 'First-use date',
          evidenceCode: 'First-use evidence code',
          evidenceDescription: 'First-use evidence description',
        },
      },
    },
    errors: {
      ASSET_STATUS_CONCURRENT_CHANGE:
        "Someone else changed this asset's status while you were working, and the operation was " +
        'rolled back. Reload the page to see the current state and try again.',
      FIXED_ASSET_CLASS_FRAMEWORK_MISMATCH:
        "The asset class belongs to a different accounting framework than this entity's books. " +
        "Pick a class from the entity's accounting framework, or fix the class setup.",
      FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
        'Fixed-asset PIS/COFINS credits are available only for Brazilian entities. Review the asset entity and fiscal source.',
      FIXED_ASSET_PIS_COFINS_CLASS_REQUIRED:
        'Select an active asset class appropriate for the asset before recording the tax-credit method.',
      FIXED_ASSET_PIS_COFINS_CREDIT_EVIDENCE_REQUIRED:
        'Enter the reference and description of the evidence supporting the PIS and COFINS credit method.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_HUMAN_REQUIRED:
        'This asset has PIS/COFINS credit and requires human review. Open the candidate and confirm its tax-credit method.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_NOT_APPLICABLE:
        'The tax-credit decision does not apply to this candidate. Refresh the source bill and review its credit type before continuing.',
      FIXED_ASSET_PIS_COFINS_CREDIT_POLICY_REQUIRED:
        'Select the PIS and COFINS credit method and record its evidence on the asset candidate. Capitalization does not select this method automatically.',
      FIXED_ASSET_PIS_COFINS_LATE_CAPITALIZATION_UNSUPPORTED:
        'This bill has already been posted. Defining the fixed asset tax credit after posting requires a reclassification that is not yet available in this workflow. The existing journal entries were preserved.',
      FIXED_ASSET_PIS_COFINS_RECOVERABILITY_REQUIRED:
        'Confirm whether full recovery of the PIS/COFINS potential is expected and enter the forecast or accounting policy supporting that conclusion.',
      FIXED_ASSET_PIS_COFINS_RECOVERABILITY_NOT_APPLICABLE:
        'The recoverability conclusion applies only to recognition according to RFB tax depreciation. Review the selected recognition method.',
      FIXED_ASSET_PIS_COFINS_FIRST_USE_EVIDENCE_REQUIRED:
        'Review the fixed asset’s first-use date and evidence. The date cannot precede acquisition or be in the future; do not record an expected date as an actual first use.',
      FIXED_ASSET_PIS_COFINS_FIRST_USE_BEFORE_IN_SERVICE:
        'The recorded tax first-use date precedes the asset’s in-service date. Review the date and the asset’s tax decision; do not silently change dates merely to release the credit.',
      FIXED_ASSET_PIS_COFINS_ACQUISITION_DATE_REQUIRED:
        'Confirm the asset’s actual acquisition or receipt date. The bill issue date is only a reference, and accounting competency does not replace this decision.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_REQUIRED:
        'Confirm when the asset became available for use and enter the corresponding accounting evidence. This date is distinct from acquisition and tax first use.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_PERIOD_UNSUPPORTED:
        'In this workflow, availability for use must remain in the source bill’s accounting competency month. Review the actual date; do not change evidence merely to bypass the block.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_IMMUTABLE:
        'Availability for use was already confirmed with another date or evidence. Refresh the asset and review the existing record before continuing.',
      FIXED_ASSET_PIS_COFINS_ACCOUNTING_IN_SERVICE_RACED:
        'The asset or its accounting decision changed during confirmation. Refresh the page, review the current record and try again.',
      FIXED_ASSET_PIS_COFINS_ACQUISITION_AFTER_IN_SERVICE:
        'Acquisition or receipt cannot occur after the asset became available for use. Review both dates and their evidence; do not silently change them to release the workflow.',
      FIXED_ASSET_PIS_COFINS_IMMEDIATE_METHOD_INELIGIBLE:
        'The asset does not meet the requirements of the selected immediate method. Review its qualification as new machinery/equipment, acquisition and production or service use. Do not select another method merely to bypass this block.',
      FIXED_ASSET_PIS_COFINS_IMPORT_EVIDENCE_REQUIRED:
        'This imported asset requires the import basis and contributions actually paid. This treatment is not yet available in this flow; do not change its origin to a domestic acquisition.',
      FIXED_ASSET_PIS_COFINS_OWNER_LINKAGE_REQUIRED:
        'The credit could not be safely linked to the asset and purchase line. Review the source bill and candidate before continuing.',
      FIXED_ASSET_PIS_COFINS_OWNER_TYPE_MISMATCH:
        'The credit category recorded on the bill does not match the linked asset. Review the purchase classification and asset linkage before continuing.',
      FIXED_ASSET_PIS_COFINS_REVENUE_USE_UNSUPPORTED:
        'The selected revenue use for this fixed asset is not fully supported in this flow. Preserve the purchase’s actual classification and keep the candidate pending; do not change its use merely to release the credit.',
      FIXED_ASSET_PIS_COFINS_RFB_RULE_REQUIRED:
        'Configure one valid tax rule for the class in the entity’s tax book, with consistent useful life, annual rate and legal reference. The financial-accounting rule does not replace the tax rule.',
      FIXED_ASSET_PIS_COFINS_SOURCE_LINE_REQUIRED:
        'Fixed-asset credit requires a saved bill line item. Create and classify the purchase item before deciding the candidate.',
      FIXED_ASSET_PIS_COFINS_USED_ASSET_UNSUPPORTED:
        'Tax qualification of used assets is not yet available in this flow. Preserve the asset’s actual condition; this product limitation does not mean all used assets are ineligible.',
      ASSET_CANDIDATE_ALREADY_DECIDED:
        'This candidate has already been decided. Refresh the queue to see the decision on ' +
        'record.',
      ASSET_CANDIDATE_SOURCE_FACTS_CHANGED:
        'The source data changed after this candidate was prepared. Keep it pending and update the purchase classification to generate a new candidate from the current data.',
      ASSET_CANDIDATE_ASSET_COUNT_REQUIRED:
        'Capitalising per unit needs a whole number of units. Enter the asset count, or choose a ' +
        'single aggregated asset for this line.',
      ASSET_CANDIDATE_AUTO_CONFIRM_ON_POSTED_BILL:
        'The bill behind this line is already posted, and capitalising now needs a journal — ' +
        'which needs an author. A learned rule cannot auto-confirm a posted bill; decide this ' +
        'candidate by hand.',
      ASSET_CANDIDATE_CLASS_REQUIRED:
        'Select an asset class: without one the asset would have no depreciation rule and no ' +
        'mapped accounts.',
      ASSET_CANDIDATE_FX_BASIS_MISSING:
        'The bill is in {{originalCurrencyCode}} and has no provision exchange rate on record, ' +
        'so there is no basis to measure the asset in {{functionalCurrencyCode}}. Nothing was ' +
        'saved. Record the bill rate — the same one the payable was provisioned at — and ' +
        'reclassify the bill so the candidate is raised with the converted amount.',
      ASSET_CANDIDATE_LINE_ALREADY_CAPITALIZED:
        'This bill line has already been capitalised into asset {{assetCode}}, and deciding this ' +
        'candidate now would duplicate it. The candidate is stale — usually after a ' +
        'capitalisation policy change — and disappears once this account is reclassified. If the ' +
        'existing asset is wrong, fix it first.',
      ASSET_CANDIDATE_UNITIZATION_REQUIRED:
        'Choose the unitisation: say whether the line becomes one asset or one per unit. An ' +
        'aggregated asset cannot be disposed of unit by unit later, so there is no safe default ' +
        'to assume.',
    },
  },

  list: {
    title: 'Fixed assets',
    description: 'Fixed and right-of-use assets, with both depreciation réguas.',
    empty: 'No assets found.',
    searchPlaceholder: 'Code, description or serial number',
    codePlaceholder: 'Asset code',
    descriptionPlaceholder: 'Asset description',
    classPlaceholder: 'Class name',
    custodianPlaceholder: 'Custodian name',
    acquisitionPlaceholder: 'Select the period',
    costMin: 'Minimum cost',
    costMax: 'Maximum cost',
    filters: {
      class: 'Class',
      status: 'Status',
      source: 'Source',
      custodian: 'Custodian',
      costCenter: 'Cost centre',
    },
    columns: {
      source: 'Source',
      code: 'Code',
      description: 'Description',
      class: 'Class',
      acquisition: 'Acquisition',
      cost: 'Cost',
      custodian: 'Custodian',
      books: 'Net book value per book',
    },
    rightOfUse: 'Right of use',
    overdueInstallments_one: '{{count}} instalment due and unposted',
    overdueInstallments_other: '{{count}} instalments due and unposted',
    openAsset: 'Open {{code}} — {{description}}',
  },

  status: {
    DRAFT: 'Draft',
    IN_SERVICE: 'In use',
    IN_MAINTENANCE: 'Under maintenance',
    // Idle assets keep depreciating (CPC 27 §55) and can be revised (CPC 27 §51).
    IDLE: 'Idle',
    DISPOSED: 'Disposed',
  },

  source: {
    AP_BILL: 'Accounts payable',
    LEASE_ROU: 'Right of use',
    LEASEHOLD_IMPROVEMENT: 'Leasehold improvement',
    MIGRATION: 'Migration',
    MANUAL: 'Manual',
  },

  detail: {
    pisCofinsRecoverability: {
      action: 'Confirm recoverability',
      review: 'Review details',
      title: 'PIS/COFINS recoverability — {{code}}',
      description:
        'Record the accounting expectation of realizing the deferred potential before recognizing the tax asset.',
      commonDescription:
        'Record the accounting expectation of realizing the portion legally admitted after common-use allocation before recognizing the tax asset.',
      conclusion: 'I confirm that full recovery of the PIS and COFINS potential is expected',
      commonConclusion:
        'I confirm that full recovery of the portion legally admitted after common-use allocation is expected',
      conclusionHint:
        'This conclusion is separate from legal credit eligibility and does not post the purchase.',
      commonConclusionHint:
        'Allocation and the legally excluded portion are period eligibility decisions; that exclusion is not a recoverability loss.',
      evidenceCode: 'Forecast or accounting-policy reference',
      evidenceDescription: 'Basis for the recovery expectation',
      evidenceHint:
        'Cite the forecast, approved budget or accounting policy supporting full recovery. Do not merely repeat the legal eligibility basis.',
      commonEvidenceHint:
        'Cite the forecast, approved budget or accounting policy supporting full recovery of the portion legally admitted after allocation. Do not treat the legally excluded portion as a recoverability loss.',
      confirm: 'Confirm recoverability',
      cancel: 'Cancel',
      success: 'Recoverability recorded. Return to the source bill and retry approval.',
      required: 'Confirm the accounting conclusion and enter the evidence reference and basis.',
      recordedConclusion: 'Accounting conclusion',
      fullPotentialExpected: 'Full recovery expected',
      classifiedAt: 'Confirmed on',
      errors: {
        FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
          'Fixed-asset PIS/COFINS credits are available only for Brazilian entities. Review the asset entity and fiscal source.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_REQUIRED:
          'Confirm the accounting conclusion and enter the forecast or policy supporting full recovery.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_NOT_APPLICABLE:
          'This asset does not use recognition according to RFB tax depreciation. Refresh the asset and review its recorded tax decision.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_IMMUTABLE:
          'Recoverability was already confirmed with different evidence. Refresh the asset and review the existing record before continuing.',
        FIXED_ASSET_PIS_COFINS_RECOVERABILITY_RACED:
          'The asset or its tax decision changed during confirmation. Refresh the page, review the current record and try again.',
      },
    },
    pisCofinsAllowance: {
      action: 'Review recoverability',
      title: 'Review recoverability — {{code}}',
      description:
        'Set the new expected carrying value separately for PIS and COFINS. The review changes only the accounting allowance; legal face and instalments remain intact.',
      method: 'Tax recognition method',
      effectiveDate: 'Effective date',
      carryingValue: 'New expected carrying value — {{tax}}',
      currentCarryingValue: 'Current carrying value: {{value}}',
      evidenceCode: 'Forecast or accounting-policy reference',
      evidenceDescription: 'Basis for the new recovery expectation',
      preview: 'View preview',
      confirm: 'Confirm review',
      cancel: 'Cancel',
      close: 'Close',
      previewRequired:
        'Complete the decision and generate the accounting preview before confirming. No journal was created.',
      previewWarning:
        'Review the buckets and journal context below. Confirmation records the allowance and its journal atomically.',
      success: 'Review recorded in {{count}} event(s).',
      previewTitle: 'Accounting preview by tax',
      accountingContext: 'Book {{book}} · period {{period}} · loss account {{account}}',
      tax: 'Tax',
      available: 'Available',
      deferred: 'Deferred',
      allowance: 'Open allowance',
      allowanceDelta: 'Loss change',
      newCarrying: 'New carrying value',
      direction: 'Movement',
      directions: { INCREASE: 'Loss increase', REVERSAL: 'Reversal', UNCHANGED: 'No change' },
      allocations: 'Affected allocations',
      ownerTypes: { INSTALLMENT: 'Deferred', ENTITLEMENT: 'Available' },
      effectiveSummary:
        'Effective date: {{date}}. Latest-first allocation calculated by the server.',
      resultTitle: 'Position after review',
      resultTax: '{{tax}} carrying value',
      viewJournal: 'View accounting journal {{number}}',
      position: 'Recoverability — {{tax}}',
      positionValue: 'Carrying value {{carrying}} · allowance {{allowance}}',
      eligibleBasis: 'Frozen gross source amount',
      nextLegalInstallment: 'Next legal instalment — {{tax}}',
      nextLegalInstallmentValue: '{{value}} · period {{competence}}',
      errors: {
        FA_PIS_COFINS_RECOVERABILITY_LOSS_REQUEST_INVALID:
          'Review the effective date, carrying values and evidence.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_UNSUPPORTED:
          'This asset cannot receive a recoverability review in its current status or method.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_CURRENCY_UNSUPPORTED:
          'The review requires a BRL source and statutory book. Preserve the actual currency and send the asset for review.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_POST_2026_UNSUPPORTED:
          'The effective date is in or after 2027 and is not yet supported in this workflow. Preserve the actual date; future tail instalments are not, by themselves, a blocker.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_MAPPING_REQUIRED:
          'Configure the PIS/COFINS recoverability-loss account in the statutory book before continuing.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_PREVIEW_STALE:
          'Balances changed after the preview. Generate a new preview and review the values before confirming.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_EFFECTIVE_DATE_BEFORE_LATEST_MOVEMENT:
          'Choose an effective date on or after {{latestLedgerDate}}, the date of the latest credit-ledger movement.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_PERIOD_NOT_OPEN:
          'The effective-date period is not open. Preserve the actual date and regularise the accounting period.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_HISTORICAL_JOURNAL_INVALID:
          'The credit accounting history does not reconcile with its source. Request review before recording another loss.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_BALANCE_INVALID:
          'The new value exceeds the eligible legal face or does not reconcile with the open allowance. Review the values.',
        FA_PIS_COFINS_RECOVERABILITY_LOSS_ALLOWANCE_REVERSAL_REQUIRED:
          'A used credit is covered by allowance. Reverse the corresponding loss before consuming the credit.',
      },
    },
    pisCofinsAccountingInService: {
      action: 'Confirm availability for use',
      review: 'Review details',
      title: 'Available for use — {{code}}',
      description:
        'Record when the asset became available for its intended use. This accounting confirmation is separate from acquisition and tax first use.',
      confirm: 'Confirm availability',
      cancel: 'Cancel',
      success: 'Availability for use recorded. Return to the source bill and retry approval.',
      required: 'Enter a valid date, evidence code and evidence description.',
      immutable:
        'This record is final for this capitalisation and does not post the bill automatically.',
      date: 'Available-for-use date',
      evidenceCode: 'Evidence code',
      evidenceDescription: 'Evidence description',
    },
    pisCofinsFirstUse: {
      action: 'Record first use',
      review: 'Review details',
      title: 'First use — {{code}}',
      description:
        'Record when the equipment started being used. The date and evidence start its PIS/COFINS tax credit schedule.',
      confirm: 'Record first use',
      cancel: 'Cancel',
      success:
        'First use recorded. Refresh the assessment sources and review the available installment before approval.',
      required:
        'Enter a date from the later of acquisition and in-service date through today, plus an evidence code and description.',
      immutable:
        'This record is final for the tax decision. It does not accelerate future installments or record a supplier payment.',
      date: 'First-use date',
      evidenceCode: 'Evidence code',
      evidenceDescription: 'Evidence description',
      sectionTitle: 'PIS/COFINS credit recognition',
      awaitingEvidence:
        'The credit remains deferred until first use and its evidence are recorded.',
      awaitingProvision:
        'Tax first-use registration requires an in-service asset and a posted purchase provision. Review the source bill.',
      errors: {
        FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
          'Fixed-asset PIS/COFINS credits are available only for Brazilian entities. Review the asset entity and fiscal source.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_NOT_APPLICABLE:
          'This recognition method does not use first-use activation. Review the asset’s recorded tax decision.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_ASSET_NOT_IN_SERVICE:
          'The asset is not in service yet. Complete that step before recording tax first use.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_IMMUTABLE:
          'First use was already recorded with another date or evidence. Refresh the asset and review the existing record.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_SCHEDULE_MISSING:
          'First use was recorded without a corresponding tax schedule. Request reconciliation of this asset before continuing.',
        FIXED_ASSET_PIS_COFINS_FIRST_USE_SCHEDULE_CONFLICT:
          'A tax schedule already exists for this asset. Refresh and review its history before recording first use again.',
        FIXED_ASSET_PIS_COFINS_SOURCE_REQUIRED:
          'The purchase tax provision has not been posted. Review and approve the source bill before continuing.',
      },
    },
    backToList: 'Back to assets',
    notFound: 'Asset not found.',
    sections: {
      identification: 'Identification',
      dimensions: 'Managerial dimensions',
      provenance: 'Origin',
      books: 'Dual régua',
      booksSingle: 'Régua — {{book}}',
      schedule: 'Depreciation instalments',
      custody: 'Custody history',
      disposal: 'Disposal',
    },
    fields: {
      serialNumber: 'Serial number',
      acquisitionDate: 'Acquisition date',
      inServiceDate: 'In service since',
      availableForUseDate: 'Available for use on',
      availableForUseDraftHint:
        'Availability has been confirmed, but the asset remains a draft. The provision and schedules are posted only when the source bill is approved.',
      acquisitionCost: 'Acquisition cost',
      unitQuantity: 'Units',
      costCenter: 'Cost centre',
      department: 'Department',
      managerialAccount: 'Managerial account',
      custodian: 'Custodian',
      location: 'Location',
      vendor: 'Vendor',
      sourceBill: 'Originating bill',
      leaseContract: 'Lease contract',
      notes: 'Notes',
    },
    books: {
      title: 'Dual régua',
      description:
        'One column per book. The gap between statutory and tax accumulated depreciation is ' +
        'exactly the temporary difference Parte A do e-Lalur reports.',
      singleBookDescription:
        'A single book measures this asset ({{book}}) — no statutory-versus-tax difference to ' +
        'compute.',
      usefulLife: 'Remaining useful life',
      months: '{{count}} months',
      method: 'Method',
      residualRate: 'Residual rate',
      depreciableBase: 'Remaining depreciable base',
      depreciableBaseAtDisposal: 'Depreciable base at disposal',
      accumulated: 'Accumulated depreciation',
      netBookValue: 'Net book value',
      netBookValueAtDisposal: 'Net book value at disposal',
      disposalSnapshot: 'Historical position used by the {{date}} disposal. The asset is disposed.',
      progress: '{{settled}} of {{total}} instalments',
      period: 'Period',
      estimatedUsefulLifePeriod: 'Estimated useful life',
      depreciationSuspended: 'Depreciation suspended by residual-value floor',
      lastSettled: 'Last competência',
      termCapped: 'Term capped by the lease contract',
      norm: 'Norm',
      overrideReason: 'Useful-life override reason',
      difference: 'Statutory × tax difference',
      differenceHint:
        'Statutory minus tax. Positive means the statutory régua has consumed more cost than the tax one.',
      differenceMisaligned:
        'The {{book}} book has not settled any instalment yet. Run depreciation to compare.',
      differenceUnavailable: 'Difference unavailable without each book’s depreciation instalments.',
      noBooks: 'No régua created for this asset.',
      overdue: '{{count}} instalment(s) due and not yet posted.',
    },
    depreciation: {
      action: 'Run depreciation',
      dialog: {
        title: 'Run depreciation — {{code}}',
        titleBook: 'Run depreciation — {{code}} ({{book}})',
        description:
          'Posts every pending instalment through the competência below, inclusive. Memo books ' +
          'are never posted — they only measure.',
        monthLabel: 'Competência (through)',
        monthFutureError:
          'The competência cannot be in the future — no overdue instalment sits in a ' +
          'competência that has not arrived yet.',
        run: 'Run depreciation',
        running: 'Running…',
        runAgain: 'Run another competência',
        close: 'Close',
        success: '{{count}} instalment(s) posted.',
        summary: {
          total: 'Instalments',
          posted: 'Posted',
          failed: 'Failed',
          empty: 'No instalment pending through that competência.',
          failedSectionTitle: 'Failed instalments',
          errorCodeFallback: 'Unmapped failure ({{code}}).',
          errorCodes: {
            FIXED_ASSET_DEPRECIATION_BOOK_NOT_POSTABLE:
              'The book keeps no ledger for this asset’s entity — there is nowhere to post.',
            FIXED_ASSET_DEPRECIATION_CURRENCY_UNSUPPORTED:
              'This asset’s currency diverges from the book’s functional currency — foreign' +
              '-currency depreciation is not supported yet.',
            FIXED_ASSET_SCHEDULE_ALREADY_POSTED:
              'This instalment was already posted by another run in progress. No action needed.',
            FIXED_ASSET_CLASS_NOT_FOUND:
              'This asset’s class no longer exists. Fix the asset record before running again.',
            SYSTEM_ACCOUNT_SLOT_NOT_FOUND:
              'The system account configuration is incomplete for this counterpart. Contact ' +
              'Alchemy.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_UNRESOLVED:
              'This asset’s class has no accumulated-depreciation account mapped. Fix the class ' +
              'mapping before running again.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
              'The accumulated-depreciation account mapped for the class is not credit-nature. ' +
              'Fix the mapping before running again.',
            FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
              'The accumulated-depreciation account mapped for the class is not an asset ' +
              'account. Fix the mapping before running again.',
            FIXED_ASSET_DEPRECIATION_EXPENSE_ACCOUNT_UNRESOLVED:
              'This asset’s class has no depreciation-expense account mapped. Fix the class ' +
              'mapping before running again.',
            FIXED_ASSET_DEPRECIATION_EXPENSE_ACCOUNT_TYPE_INVALID:
              'The depreciation-expense account mapped for the class is not the expected type ' +
              '(COGS or expense, debit-nature). Fix the mapping before running again.',
            NO_OPEN_PERIOD:
              'The accounting period for that competência is closed in this book. Reopen the ' +
              'period or run an earlier competência.',
            FIXED_ASSET_NOT_DEPRECIABLE:
              'This asset’s status does not allow depreciation. Depreciation only runs while an ' +
              'asset is in service, in maintenance or idle.',
            FIXED_ASSET_SCHEDULE_BOOK_IS_MEMO:
              'This book is a memo book: it measures without posting, so its instalments ' +
              'terminate at Computed and are read by report (ADR 0003, Decision 1).',
            FIXED_ASSET_SCHEDULE_NOT_FOUND:
              'This instalment no longer exists. Refresh the page to see the current schedule.',
            FIXED_ASSET_SCHEDULE_NOT_PENDING:
              'This instalment is not pending, and only a pending instalment can be posted. It ' +
              'has already been posted, cancelled or replaced by a revision.',
            UNKNOWN: 'Unexpected failure posting this instalment.',
          },
        },
      },
      errors: {
        FIXED_ASSET_DEPRECIATION_BAD_MONTH: 'The competência is invalid. Use the YYYY-MM format.',
      },
    },
    schedule: {
      description:
        'One row per competência, side by side per book — the same competência can be posted ' +
        'in one book and overdue in the other.',
      empty: 'No instalments generated.',
      columns: {
        competency: 'Competência',
        installment: 'Instalment',
        amount: 'Amount',
        accumulated: 'Accumulated',
        netBookValue: 'Net book value',
        status: 'Status',
      },
      status: {
        PENDING: 'Pending',
        POSTED: 'Posted',
        REVERSED: 'Reversed',
        CANCELLED: 'Cancelled',
        COMPUTED: 'Computed (memo)',
        ROUNDED_ZERO: 'No posting (rounding)',
      },
      roundedZeroDescription:
        'This instalment rounds to zero at the currency precision and requires no journal posting. The rounding remainder stays in the final instalment without changing the asset useful life.',
      overdueTitle: 'Due and not yet posted',
      noRows: 'No book has instalments for this asset.',
      cancelledByRevision:
        '{{count}} instalment(s) in book {{bookCode}} cancelled by the {{date}} revision do not ' +
        'appear in this table — see the custody history.',
      errors: {
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'This asset is a right of use whose book carries a residual value, and a right of use is ' +
          'consumed in full over the lease term. Clear the residual value on the class book rule ' +
          'and try again.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          "This book's functional currency does not have 2 decimal places (or the book has no " +
          "functional currency for the asset's entity), and the assets module only measures " +
          '2-decimal currencies. Record this asset on a book in a 2-decimal currency.',
        FIXED_ASSET_SCHEDULE_NOT_POSTED:
          'Only a posted instalment can be reversed. This one is pending, cancelled or already ' +
          'reversed.',
        FIXED_ASSET_SCHEDULE_REVERSAL_AMBIGUOUS:
          'This instalment resolves to more than one journal group, or to none, and the reversal ' +
          'expects exactly one. Talk to Alchemy before trying again.',
      },
    },
    custody: {
      description:
        'Append-only trail. The asset carries where it is today; this list carries how it got there.',
      empty: 'No movements recorded.',
      columns: {
        date: 'Date',
        change: 'Change',
        from: 'From',
        to: 'To',
        reason: 'Reason',
        registeredBy: 'Registered by',
      },
      kinds: {
        custodian: 'Custodian',
        costCenter: 'Cost centre',
        location: 'Location',
        status: 'Status',
      },
      unassigned: 'Unassigned',
    },
    disposal: {
      action: 'Dispose asset',
      type: 'Disposal type',
      date: 'Date',
      proceeds: 'Proceeds',
      cost: 'Disposal costs',
      resultPerBook: 'Result per book',
      resultHint:
        'The gain or loss differs between réguas because accumulated depreciation diverged on the way here. In the statutory book, the result also includes the net PIS/COFINS credit loss shown below.',
      gain: 'Gain',
      loss: 'Loss',
      extinguishedLiability: 'Lease liability extinguished',
      taxCreditNetLoss: 'Net PIS/COFINS credit loss',
      assetJournals: 'Asset-disposal journals',
      pisCofinsJournals: 'PIS/COFINS credit journals',
      viewAssetJournal: 'View {{book}} journal',
      viewPisCofinsJournal: 'View tax journal {{number}}',
      types: {
        SALE: 'Sale',
        WRITE_OFF: 'Write-off',
        LOSS: 'Loss',
        DONATION: 'Donation',
        LEASE_RETURN: 'Return to lessor',
      },
      dialog: {
        title: 'Dispose of {{code}}',
        description:
          'Disposal derecognises the asset in every book that measures it: each posted book ' +
          'gets its own journal, and each memo book only records the result, with nothing posted.',
        typePlaceholder: 'Select the type',
        proceedsAccount: 'Proceeds account',
        proceedsAccountPlaceholder: 'Required when there are proceeds',
        costAccount: 'Cost credit account',
        costAccountPlaceholder: 'Required when there is a disposal cost',
        documentReference: 'Document (sales invoice, report, donation deed)',
        reason: 'Reason',
        reasonPlaceholder: 'Context for the disposal (optional).',
        preview: 'View preview',
        previewTitle: 'Authoritative preview by book',
        previewRequired:
          'Generate the authoritative preview before confirming disposal. No journal was created.',
        previewWarning:
          'Review the result by book and the tax tail. Confirmation disposes of the asset and credits in one transaction.',
        resultBeforeTaxCredit: 'Result before tax credit',
        taxTailTitle: 'PIS/COFINS legal tail',
        taxTailContext: 'Book {{book}} · period {{period}} · disposal result {{account}}',
        tax: 'Tax',
        maturedTarget: 'Matured target',
        accountedMatured: 'Matured and posted',
        preservedCurrentPeriod: 'Current-period instalment preserved',
        legalTail: 'Legal face derecognised',
        allowanceApplied: 'Allowance applied',
        netTaxLoss: 'Net tax loss',
        taxLegalFaceDerecognized: 'PIS/COFINS legal face derecognised',
        estimateTitle: 'Estimate per book',
        estimateHint:
          'Computed here in the browser from each book’s current net book value — ' +
          '(proceeds − costs − net book value). This is a preview, not the result the server ' +
          'will record: the server also nets out the extinguished lease liability when the ' +
          'disposal is a return to the lessor.',
        submit: 'Confirm disposal',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Asset disposed.',
        resultTitle: 'Result recorded',
        cancelledSchedules: '{{count}} future instalment(s) cancelled by the disposal.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Asset not found. Refresh the page and try again.',
        FIXED_ASSET_NOT_DISPOSABLE:
          'This asset cannot be disposed of in its current status. Disposal derecognises a cost ' +
          'that is on the balance sheet, so it only runs while the asset is in use, under ' +
          'maintenance or idle.',
        FIXED_ASSET_DISPOSAL_NO_BOOKS:
          'No book measures this asset, so there is no régua to derecognise. Check the asset’s ' +
          'book configuration.',
        FIXED_ASSET_DISPOSAL_DEPRECIATION_PENDING:
          'A posted book has a depreciation instalment due (dated on or before the disposal) ' +
          'that has not been posted yet. Run depreciation through that competência before ' +
          'disposing.',
        FIXED_ASSET_DISPOSAL_DEPRECIATION_POSTED_AFTER:
          'A book already has depreciation posted for competências after the disposal date. ' +
          'Reverse those entries before disposing.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_CREDIT_WRITE_OFF_REQUIRED:
          'This asset still has deferred PIS/COFINS potential. Disposal with that balance is not yet supported; keep the asset active until a dedicated tax-reversal workflow is available.',
        FIXED_ASSET_1_48_DISPOSAL_EVENT_UNSUPPORTED:
          'For 1/48 credits, this disposal workflow supports sales and donations. The selected event still requires a specific tax treatment.',
        FIXED_ASSET_COMMON_USE_DISPOSAL_NOT_SUPPORTED:
          'For assets with credits allocated across revenue regimes, this workflow supports a sale or donation through December 2026. Preserve the actual event type and date; other cases require tax treatment that is not yet available.',
        FIXED_ASSET_COMMON_USE_DISPOSAL_SCHEDULE_STATE_CHANGED:
          'The depreciation installments changed during disposal. Generate a new preview and review the installments before confirming.',
        FIXED_ASSET_1_48_DISPOSAL_INPUT_INVALID:
          'The disposal date is incompatible with the 1/48 election. Check the election start and event date before continuing.',
        FIXED_ASSET_1_48_DISPOSAL_BALANCE_INVALID:
          'The 1/48 installments and losses do not reconcile for this disposal. Review prior-period appropriation and recoverability adjustments.',
        FIXED_ASSET_1_48_DISPOSAL_FUTURE_MATURATION_CONFLICT:
          'Installments were appropriated after the disposal month. Reconcile those periods before disposing of the asset.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_PREVIEW_REQUIRED:
          'Generate and review the tax preview before confirming disposal.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_PREVIEW_STALE:
          'The tax tail changed after the preview. Generate a new preview before confirming.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_POST_2026_UNSUPPORTED:
          'Disposal dated January 2027 or later requires CBS transition treatment, which this workflow does not yet support.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_FUTURE_MATURATION_CONFLICT:
          'There is an incompatible maturation after the disposal cutoff. Reconcile the tax periods before continuing.',
        FIXED_ASSET_DISPOSAL_PIS_COFINS_HISTORICAL_JOURNAL_INVALID:
          'The credit accounting history does not reconcile with its source. Request review before disposal.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_REQUIRED:
          'Enter the proceeds account: there are proceeds, and the journal has to debit cash, ' +
          'bank or accounts receivable.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_UNEXPECTED:
          'Remove the proceeds account: there are no proceeds to post against it.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_REQUIRED:
          'Enter the disposal cost credit account: there is a cost, and the journal needs a ' +
          'counter-account.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_UNEXPECTED:
          'Remove the disposal cost account: there is no cost to post against it.',
        FIXED_ASSET_DISPOSAL_PROCEEDS_ACCOUNT_TYPE_INVALID:
          'The selected proceeds account is not of the expected type (cash, bank or accounts ' +
          'receivable). Select another account.',
        FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
          'The accumulated-depreciation account mapped for the class is not credit-nature. Fix ' +
          'the class mapping and try again.',
        FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
          'The accumulated-depreciation account mapped for the class is not an asset account. ' +
          'Fix the class mapping and try again.',
        FIXED_ASSET_DISPOSAL_COST_ACCOUNT_TYPE_INVALID:
          'The selected cost account is not of the expected type for this counter-entry. Select ' +
          'another account.',
        FIXED_ASSET_DISPOSAL_COUNTER_ACCOUNT_IS_SUBLEDGER:
          'The selected account belongs to a customer or vendor subledger and cannot receive ' +
          'the disposal entry directly. Select a non-subledger account.',
        FIXED_ASSET_DISPOSAL_BOOK_NOT_POSTABLE:
          'One of the books that measures this asset keeps no ledger for its entity. Talk to ' +
          'Alchemy before disposing.',
        FIXED_ASSET_DISPOSAL_CURRENCY_UNSUPPORTED:
          'This asset’s currency differs from the functional currency of one of the books that ' +
          'measures it — disposing of a foreign-currency asset is not yet supported.',
        FIXED_ASSET_DISPOSAL_LEASE_RETURN_NOT_RIGHT_OF_USE:
          'Return to lessor only applies to a right-of-use asset. Choose another disposal type ' +
          'for this asset.',
        FIXED_ASSET_DISPOSAL_LEASE_LIABILITY_OUTSTANDING:
          'This right of use’s lease contract still has outstanding liability instalments. ' +
          'Settle or remeasure the liability before returning the asset to the lessor.',
        FIXED_ASSET_DISPOSAL_RESULT_ACCOUNT_UNRESOLVED:
          'This asset’s class points its disposal result at an account that no longer exists. ' +
          'Fix the class mapping before disposing.',
        FIXED_ASSET_DISPOSAL_RESULT_ACCOUNT_TYPE_INVALID:
          'This asset’s class points its disposal result at an account that is not a ' +
          'profit-or-loss account. Fix the class mapping before disposing.',
        ASSET_STATUS_CONCURRENT_CHANGE:
          "Someone else changed this asset's status while you were working, and the operation was " +
          'rolled back. Reload the page to see the current state and try again.',
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'This asset is a right of use whose book carries a residual value, and a right of use is ' +
          'consumed in full over the lease term. Clear the residual value on the class book rule ' +
          'and try again.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          "This book's functional currency does not have 2 decimal places (or the book has no " +
          "functional currency for the asset's entity), and the assets module only measures " +
          '2-decimal currencies. Record this asset on a book in a 2-decimal currency.',
        FIXED_ASSET_DISPOSAL_BAD_DATE: 'The disposal date entered is invalid.',
        FIXED_ASSET_ALREADY_DISPOSED:
          'This asset already has a disposal. Partial disposal is out of scope, so an asset is ' +
          'derecognised once — this attempt was rolled back. Refresh the page to see the ' +
          'existing disposal.',
        NO_OPEN_PERIOD:
          'The accounting period of the disposal date is closed in at least one book. Choose a date ' +
          'inside an open period, or reopen the period before disposing.',
      },
    },
    revision: {
      action: 'Revise régua',
      dialog: {
        title: 'Revise the régua of {{code}}',
        description:
          'Revision of useful life, residual value or method (CPC 27 §51/§61) — a change in ' +
          'accounting estimate (CPC 23): applies only from the effective date forward, never ' +
          'retroactively. Every instalment already posted — or, in a memo book, already elapsed ' +
          '— stays exactly as it is. The fiscal (TAX_BASIS) book is never touched here: its ' +
          'life follows IN RFB 1.700/2017 Annex III, not the entity’s own judgement.',
        effectiveDate: 'Effective date',
        reason: 'Reason',
        reasonPlaceholder: 'Basis for the revision — required, this is the audit trail.',
        usefulLifeMonths: 'New remaining useful life (months)',
        usefulLifeMonthsHint: 'Counted from the effective date, not the total useful life.',
        residualRate: 'New residual value (%)',
        residualRatePlaceholder: 'Leave blank to keep the current rate',
        method: 'Method',
        methodHint: 'Only STRAIGHT_LINE is implemented.',
        submit: 'Revise',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Régua revised.',
        resultTitle: 'Revision applied',
        bookSummary: '{{preserved}} preserved, {{cancelled}} replaced, {{regenerated}} new.',
        suspendedSummary:
          '{{preserved}} preserved, {{cancelled}} cancelled. No zero-value instalment was created; estimated useful life continues through {{estimatedEndDate}}.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Asset not found. Refresh the page and try again.',
        FIXED_ASSET_REVISION_NOT_IN_SERVICE:
          'This asset cannot be revised in its current status. A revision changes the régua of ' +
          'a bem already in use, so it only runs while the asset is in service, under ' +
          'maintenance or idle.',
        FIXED_ASSET_HAS_NO_BOOK_RULE: 'No book measures this asset — there is no régua to revise.',
        FIXED_ASSET_REVISION_METHOD_NOT_IMPLEMENTED:
          'Only the straight-line method is implemented.',
        FIXED_ASSET_REVISION_RESIDUAL_INVALID:
          'The new residual value must be a fraction between 0 and 1 (e.g. 0.10 for 10%).',
        FIXED_ASSET_REVISION_RESIDUAL_EXCEEDS_CARRYING_AMOUNT:
          'The estimated residual value equals or exceeds the carrying amount. Depreciation must be zero in this condition, which this workflow does not yet support. No revision was saved; preserve the actual estimate and request review.',
        FIXED_ASSET_COMMON_USE_REVISION_PERIOD_REQUIRED:
          'An accounting period is missing for the revision month in one of the books. Create and open the period for the entity and book before revising the schedule.',
        FIXED_ASSET_COMMON_USE_REVISION_PERIOD_AMBIGUOUS:
          'Accounting periods overlap for the revision month. Correct the period dates for the entity and book before revising the schedule.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_DATE_INVALID:
          'The revision or history effective date is invalid. Review the entered date and earlier revisions.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_BEFORE_OWNER:
          'The revision cannot start before the current cost-allocation adjustment. Review the adjustment date and choose a compatible effective date.',
        FIXED_ASSET_COMMON_USE_REVISION_EFFECTIVE_DATE_REGRESSION:
          'The revision cannot start before an existing revision. Review the history and use an effective date on or after the latest revision.',
        FIXED_ASSET_COMMON_USE_REVISION_ASSET_SCOPE_INVALID:
          'The revision does not identify the asset correctly. Reopen the asset and try again.',
        FIXED_ASSET_COMMON_USE_REVISION_AMOUNT_INVALID:
          'The cost or installment amounts are invalid. No revision was saved; review the asset cost and schedules.',
        FIXED_ASSET_COMMON_USE_REVISION_OWNER_CHAIN_INVALID:
          'The cost-allocation history is incomplete or inconsistent. Review the adjustments linked to the asset before revising.',
        FIXED_ASSET_COMMON_USE_REVISION_OWNER_COST_DIVERGENT:
          'The current cost differs from the latest approved allocation adjustment. Review the cost and its journals before revising.',
        FIXED_ASSET_COMMON_USE_REVISION_COMMAND_CHAIN_INVALID:
          'The useful-life revision history is incomplete or inconsistent. Submit the asset history for review.',
        FIXED_ASSET_COMMON_USE_REVISION_COMMAND_OWNER_INVALID:
          'An earlier revision does not match the cost-adjustment history. Review the links between the asset revisions and adjustments.',
        FIXED_ASSET_COMMON_USE_REVISION_BOOK_SCOPE_INVALID:
          'The revision books are missing or duplicated. Review the books and their schedules before revising.',
        FIXED_ASSET_COMMON_USE_REVISION_SCHEDULE_LINEAGE_INVALID:
          'The revision installments do not match the expected asset and book. Review the schedule history; no revision was saved.',
        FIXED_ASSET_COMMON_USE_REVISION_SCHEDULE_STATUS_INVALID:
          'The installments changed status and cannot be replaced by this revision. Refresh the asset and review its journals before retrying.',
        FIXED_ASSET_COMMON_USE_REVISION_BOOK_RECONCILIATION_INVALID:
          'Preserved depreciation and replacement installments do not reconcile with the revised basis. Review the schedule amounts; no revision was saved.',
        FIXED_ASSET_REVISION_ZERO_DEPRECIATION_UNSUPPORTED:
          'The estimated residual value equals or exceeds the carrying amount. Depreciation must be zero in this condition, which this workflow does not yet support. No revision was saved; preserve the actual estimate and request review.',
        FIXED_ASSET_REVISION_BEFORE_LAST_POSTED:
          'The effective date must be after the last instalment already posted in that book — ' +
          'this estimate revision applies prospectively and preserves earlier journals.',
        FIXED_ASSET_REVISION_BEFORE_LAST_COMPUTED:
          'The effective date must be after the last competência already elapsed in that memo ' +
          'book — this estimate revision applies prospectively and preserves earlier installments.',
        FIXED_ASSET_REVISION_PERIOD_CLOSED:
          'A competência that the revision would replace is closed or locked in one of the books. ' +
          'Review the affected periods and choose an effective date that preserves closed competências, or follow the reopening workflow before revising.',
        FIXED_ASSET_REVISION_PERIOD_REQUIRED:
          'An elapsed competência that the revision would replace has no accounting period. Configure the period in the correct book before revising; no changes were saved.',
        FIXED_ASSET_REVISION_PERIOD_AMBIGUOUS:
          'Overlapping accounting periods cover a competência that the revision would replace. Resolve the overlap in the book before revising; no changes were saved.',
        FIXED_ASSET_REVISION_SCOPE_CHANGED_RETRY:
          'The asset entity or books changed during the revision. Nothing was saved. Refresh the page, review the details, and try again.',
        FIXED_ASSET_REVISION_NO_REVISABLE_BOOK:
          'No book on this asset can be revised: all are fiscal (TAX_BASIS) or have no active ' +
          'régua. Nothing was changed.',
        FIXED_ASSET_REVISION_SCHEDULE_RACED:
          'This book was changed by another operation (another revision or depreciation posting) ' +
          'while this revision was being processed. Nothing was saved; redo the revision.',
        FIXED_ASSET_REVISION_REVERSED_INSTALLMENT_IN_TAIL:
          'This book has a reversed (REVERSED) instalment inside the period the revision would ' +
          'replace. Repost or cancel that instalment before revising, or choose an effective ' +
          'date after it.',
        FIXED_ASSET_REVISION_BAD_DATE: 'The effective date entered is invalid.',
        FIXED_ASSET_USEFUL_LIFE_INVALID:
          'The remaining useful life must be a whole number of months greater than zero.',
        FIXED_ASSET_STARTING_INSTALLMENT_INVALID: 'Could not continue the instalment numbering.',
        FIXED_ASSET_SCHEDULE_BAD_DATE: 'The effective date entered is invalid.',
        FIXED_ASSET_DEPRECIABLE_BASE_INVALID: 'The resulting depreciable base is invalid.',
        FIXED_ASSET_ROU_RESIDUAL_UNSUPPORTED:
          'This asset is a right of use whose book carries a residual value, and a right of use is ' +
          'consumed in full over the lease term. Clear the residual value on the class book rule ' +
          'and try again.',
        FIXED_ASSET_CURRENCY_PRECISION_UNSUPPORTED:
          "This book's functional currency does not have 2 decimal places (or the book has no " +
          "functional currency for the asset's entity), and the assets module only measures " +
          '2-decimal currencies. Record this asset on a book in a 2-decimal currency.',
        FIXED_ASSET_REVISION_REVERSED_INSTALLMENT_BEFORE_EFFECTIVE:
          'This book has a reversed instalment dated before the effective date, with no live ' +
          'posting that replaced it. The reversal gave the expense and the accumulated ' +
          'depreciation back to the ledger, so the carrying amount on the effective date depends ' +
          'on that instalment being reposted, cancelled or corrected — revising first would ' +
          'leave a balance on the asset at the end of the revised life. Repost or cancel that ' +
          'instalment before revising.',
      },
      warnings: {
        FIXED_ASSET_REVISION_BOOK_NOT_DEPRECIATING:
          'One book measures this asset with no active depreciation rule — there was nothing to ' +
          'revise in it.',
        FIXED_ASSET_REVISION_FISCAL_BOOK_EXCLUDED:
          'The tax book was left untouched: its useful life follows Annex III of IN RFB ' +
          '1.700/2017, not the entity’s judgement (CPC 27 §51). Its instalments stay exactly as ' +
          'they were.',
      },
    },
    transfer: {
      action: 'Transfer from work in progress',
      dialog: {
        title: 'Transfer {{code}} into its final class',
        description:
          'Moves the bem out of imobilizado em andamento (CPC 27 §55): the régua is generated ' +
          'from the stated in-service date forward, never from the acquisition date.',
        targetClass: 'Target class',
        targetClassPlaceholder: 'Select the final class',
        inServiceDate: 'Available for use on',
        reason: 'Reason',
        reasonPlaceholder: 'Context for the transfer (optional).',
        submit: 'Transfer',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Asset transferred.',
        resultTitle: 'Transfer complete',
        booksGenerated: '{{count}} book(s) with a régua generated from {{date}}.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Asset not found. Refresh the page and try again.',
        ASSET_STATUS_CONCURRENT_CHANGE:
          "Someone else changed this asset's status while you were working, and the operation was " +
          'rolled back. Reload the page to see the current state and try again.',
        FIXED_ASSET_TRANSFER_STATUS_INVALID:
          'This asset does not carry its cost on the ledger (draft or disposed) and cannot be ' +
          'transferred out of work-in-progress.',
        FIXED_ASSET_TRANSFER_NOT_IN_PROGRESS:
          'This asset is not in work-in-progress (EM_ANDAMENTO). This action moves a bem out of ' +
          'that class; an asset already depreciating needs a different capability, not a class ' +
          'swap.',
        FIXED_ASSET_TRANSFER_IN_SERVICE_BEFORE_ACQUISITION:
          'The in-service date cannot be before the acquisition date.',
        FIXED_ASSET_TRANSFER_SCHEDULE_EXISTS:
          'This asset already has depreciation instalments recorded, which should not happen ' +
          'while it is in work-in-progress. Contact Alchemy before transferring.',
        FIXED_ASSET_TRANSFER_TARGET_CLASS_NOT_FOUND: 'Target class not found.',
        FIXED_ASSET_CLASS_NOT_ACTIVE: 'The target class is not available for capitalisation.',
        FIXED_ASSET_TRANSFER_TARGET_RIGHT_OF_USE:
          'The target class is a right-of-use class. A bill line never becomes a right-of-use ' +
          'asset — only lease recognition creates one, against a contract.',
        FIXED_ASSET_TRANSFER_TARGET_SAME_AS_SOURCE: 'The asset is already in this class.',
        FIXED_ASSET_CLASS_HAS_NO_BOOK_RULE:
          'No depreciation rule of the target class belongs to this entity’s active books. ' +
          'Configure the régua before transferring.',
        FIXED_ASSET_CLASS_MISSING_BOOK_RULE:
          'The target class has no depreciation rule in one of this entity’s ledger books, so the ' +
          'cost would not be reclassified out of CIP in that book. Set up the class’s rule in every ' +
          'ledger book before transferring.',
        FIXED_ASSET_TRANSFER_CURRENCY_UNSUPPORTED:
          'This asset’s currency does not match the functional currency of one of the target ' +
          'class’s books.',
        FIXED_ASSET_TRANSFER_BAD_DATE: 'The in-service date entered is invalid.',
        NO_OPEN_PERIOD:
          'The accounting period of the transfer date is closed in at least one book. Choose a date ' +
          'inside an open period, or reopen the period before transferring.',
        FIXED_ASSET_NOT_IN_SERVICE:
          'A depreciation schedule is only generated for an asset that is in service, in ' +
          'maintenance or idle (CPC 27 §55). Set the available-for-use date and finish the ' +
          'transfer before depreciating.',
      },
    },
    custodian: {
      action: 'Change custodian',
      dialog: {
        title: 'Change the custodian of {{code}}',
        description: 'Records who is now responsible for the bem, with a date and a reason.',
        effectiveDate: 'Date',
        custodian: 'New custodian',
        custodianPlaceholder: 'Select a user (or leave blank for none)',
        reason: 'Reason',
        reasonPlaceholder: 'Context for the change.',
        submit: 'Record',
        cancel: 'Cancel',
        close: 'Close',
        success: 'Custodian updated.',
      },
      errors: {
        FIXED_ASSET_NOT_FOUND: 'Asset not found. Refresh the page and try again.',
        FIXED_ASSET_CUSTODIAN_CHANGE_NOT_ALLOWED:
          'This asset has already been disposed of and has no custodian left to track.',
        FIXED_ASSET_CUSTODIAN_UNCHANGED:
          'This is already the current custodian — nothing to record.',
        FIXED_ASSET_CUSTODIAN_NOT_FOUND: 'The selected user does not exist in this tenant.',
        FIXED_ASSET_CUSTODIAN_BAD_DATE: 'The date entered is invalid.',
        FIXED_ASSET_CUSTODIAN_REASON_REQUIRED: 'Enter the reason for this change.',
      },
    },
    draft: {
      actions: {
        promote: 'Capitalise',
        edit: 'Edit draft',
        delete: 'Delete draft',
      },
      ladderPreview:
        'Preview: each book as it will open on capitalising, if the check against the ledger ' +
        'passes. Nothing has been posted or depreciated yet.',
      plan: {
        title: 'Capitalisation plan',
        hintNoDocument:
          'What capitalising will apply: the asset enters service and its cost is posted against ' +
          'the account below.',
        hintMigrated:
          'What capitalising will apply: the asset enters service with the depreciation already ' +
          'taken up to the cutover, with no posting — the amounts are already in the opening ' +
          'balances.',
        inServiceDate: 'In service from',
        inServiceDateDefault: '{{date}} (acquisition date)',
        counterAccount: 'Counter account',
        cutover: 'Cutover date',
        openingPositions: 'Accumulated depreciation up to the cutover',
        netBookValue: 'Net value {{amount}}',
        remainingLife_one: '{{count}} month remaining',
        remainingLife_other: '{{count}} months remaining',
        notFilled: 'Not entered',
      },
      promote: {
        title: 'Capitalise {{code}}?',
        descriptionNoDocument:
          'The asset enters service on {{date}} and is posted: debit to fixed assets and credit ' +
          'to {{account}}, for {{amount}}.',
        descriptionMigrated:
          'The asset enters service on {{date}} with the opening position below. Nothing is ' +
          'posted: the cost and depreciation are already in the opening balances and are checked ' +
          'against the ledger.',
        accumulatedIn: 'Accumulated depreciation · {{book}}',
        gapsTitle: 'Missing from the plan',
        gaps: {
          counterAccount: 'The capitalisation counter account.',
          cutover: 'The migration cutover date.',
          openingBooks: 'The accumulated depreciation up to the cutover in: {{books}}.',
        },
        gapsNote: 'Edit the draft to complete the plan before capitalising.',
        confirm: 'Capitalise',
        success: 'Asset {{code}} capitalised.',
      },
      delete: {
        title: 'Delete draft {{code}}?',
        description:
          'The draft is removed and code {{code}} becomes free for another asset. Nothing was ' +
          'posted to the ledger; the deletion is recorded in the audit trail.',
        confirm: 'Delete draft',
        success: 'Draft {{code}} deleted.',
      },
    },
  },

  leases: {
    title: 'Lease contracts',
    description: 'Right of use under CPC 06 (R2), with each book’s treatment.',
    empty: 'No contracts found.',
    searchPlaceholder: 'Contract code or description',
    lessorPlaceholder: 'Lessor name',
    paymentMin: 'Minimum instalment',
    paymentMax: 'Maximum instalment',
    filters: {
      status: 'Status',
      classification: 'Classification',
      lessor: 'Lessor',
    },
    columns: {
      code: 'Contract',
      lessor: 'Lessor',
      term: 'Term',
      payment: 'Instalment',
      classification: 'Classification',
      status: 'Status',
      liability: 'Liability per book',
    },
    classification: {
      RECOGNIZED: 'Recognised',
      // Bug 035: a RECOGNIZED contract still in DRAFT has never run recognition — no liability,
      // no right-of-use asset, no amortisation table. Calling it "Recognised" in that state
      // claims an accounting event that has not happened.
      RECOGNIZED_PENDING: 'Recognition required',
      SHORT_TERM_EXEMPT: 'Exempt — short term',
      LOW_VALUE_EXEMPT: 'Exempt — low value',
    },
    status: {
      DRAFT: 'Draft',
      ACTIVE: 'Active',
      TERMINATED: 'Terminated',
      EXPIRED: 'Expired',
    },
    frequency: {
      MONTHLY: 'Monthly',
      BIMONTHLY: 'Bi-monthly',
      QUARTERLY: 'Quarterly',
      SEMESTERLY: 'Semi-annual',
      YEARLY: 'Annual',
    },
    recognitionMode: {
      RECOGNIZE_ROU: 'Recognises right of use',
      EXPENSE_PAYMENTS: 'Deducts the instalments',
    },
    expensePaymentsHint:
      'This book carries neither asset nor liability (Lei 12.973/2014, arts. 47-49). That absence ' +
      'is the temporary difference — not missing data.',
    new: {
      action: 'New lease',
      backToList: 'Back to contracts',
      jurisdiction: {
        title: 'Leases are not available for this entity yet',
        body:
          'This entity keeps {{books}}. The engine implements CPC 06 (R2) and IFRS 16. NIF D-5 ' +
          'and ASC 842 differ in both measurement and presentation — ASC 842 separates finance ' +
          'from operating leases on the balance sheet, and US tax recognises no right-of-use ' +
          'asset for an operating lease. Talk to Alchemy before recording leases here.',
      },
      sections: {
        entity: 'Entity',
        contract: 'Contract',
        indexation: 'Indexation',
        discountRate: 'Discount rate',
        costs: 'Costs and provisions',
        cutover: 'Opening balances',
        renewal: 'Renewal option',
        classification: 'Classification',
      },
      fields: {
        entity: 'Entity',
        entityHint:
          'Every lease belongs to an entity: the entity keeps the books, the asset policy and the ' +
          'chart of accounts that measure the contract.',
        entityPlaceholder: 'Select the entity',
        contractCode: 'Contract code',
        contractCodePlaceholder: 'e.g. LEASE-2026-001',
        description: 'Description',
        descriptionPlaceholder: 'e.g. Office suite — Av. Paulista, 1000',
        lessor: 'Lessor',
        lessorPlaceholder: 'Search vendors',
        lessorHint: 'The lessor is a vendor: lease payments flow through accounts payable.',
        commencementDate: 'Commencement date',
        termMonths: 'Term in months',
        termMonthsHint:
          'The lease term, with any reasonably certain renewal option already inside it.',
        paymentAmount: 'Instalment amount',
        paymentFrequency: 'Frequency',
        paymentDueDay: 'Due day',
        paymentDueDayHint:
          'Moves the due date only, never the discounting. The instalment paid at commencement ' +
          'keeps the commencement date.',
        paymentInAdvance: 'Payment in advance',
        paymentInAdvanceHint:
          'In advance is the Brazilian norm for rent: the first instalment falls on the ' +
          'commencement date, stays out of the liability under §26 and enters the asset under §24(b).',
        indexMode: 'Escalation basis',
        indexModeHint:
          'Either a contractual basis or a published index — never both. The database refuses a ' +
          'contract carrying both.',
        indexModeFixed: 'Fixed — the instalment never changes',
        indexModeStep: 'Contractual step — a percentage known at signing',
        indexModeIndex: 'Published economic index',
        economicIndexCode: 'Economic index',
        economicIndexPlaceholder: 'Select the series',
        economicIndexHint:
          'An indexed payment is measured at the index level as at commencement (§27(b)) and moves ' +
          'only through remeasurement — expected inflation never enters the initial liability.',
        contractualStepRate: 'Step rate',
        contractualStepRateHint:
          'Known at signing, therefore an in-substance fixed payment (§27(a)-(b)): it belongs in ' +
          'the liability from day one.',
        indexAnniversaryMonth: 'Anniversary month',
        indexAnniversaryDay: 'Anniversary day',
        indexWindowMonths: 'Index window (months)',
        indexPublicationLagDays: 'Publication lag (days)',
        indexFloorAtZero: 'Floor at zero (no negative adjustment)',
        discountRateAnnual: 'Discount rate (p.a.)',
        discountRateAnnualHint:
          'An effective annual rate. §26 requires the rate implicit in the lease or, when that is ' +
          'not determinable, the lessee’s incremental borrowing rate.',
        discountRateDefaultNote: 'Client policy default: {{rate}} (incremental borrowing rate).',
        usesRateImplicitInLease: 'The rate implicit in the lease was determinable and was used',
        discountRateBasisNote: 'Rate basis',
        discountRateBasisNotePlaceholder: 'Why this is this entity’s incremental borrowing rate.',
        rouAssetClass: 'Right-of-use class',
        rouAssetClassHint:
          'Applied to every book that recognises — one contract produces one right of use, and ' +
          'every recognising book must point at the same class.',
        rouAssetClassPlaceholder: 'Select the right-of-use class',
        initialDirectCosts: 'Initial direct costs (§24(b))',
        initialDirectCostsHint:
          'Capitalised into the right-of-use asset, never into the liability. E.g. broker fees.',
        restorationProvision: 'Restoration provision (§24(d))',
        restorationProvisionHint:
          'Dismantling or make-good obligation (CPC 25). Likewise asset-only.',
        isCutover: 'This lease was already running at migration',
        isCutoverHint:
          'Loads existing carrying amounts without repeating initial recognition or prior amortisation.',
        openingPositionAsOf: 'Opening balance date',
        openingPositionAsOfHint:
          'Enter the migrated closing date. It must be later than the commencement date.',
        openingLiabilityAmount: 'Lease liability at the opening date',
        openingAccumulatedAmortizationAmount: 'Accumulated right-of-use amortisation',
        containsPurchaseOption: 'Does the contract contain a purchase option?',
        containsPurchaseOptionPlaceholder: 'Select yes or no',
        containsPurchaseOptionHint:
          'Required decision: any purchase option prevents the Appendix A short-term exemption.',
        includesRenewalOptionInTerm: 'The term includes a renewal option',
        renewalJudgementNote: 'Renewal judgement',
        renewalJudgementNotePlaceholder: 'Why exercising the renewal is reasonably certain.',
        renewalJudgementRequired:
          'Required when the term includes a renewal: CPC 06 R2 makes that judgement explicit, ' +
          'because it changes both the asset and the liability.',
        renewalMonthsInsideTermHint:
          'The renewal months must already be added into “Term in months” — this field does not ' +
          'add them.',
        underlyingAssetValueWhenNew: 'Value of the underlying asset WHEN NEW',
        underlyingAssetValueWhenNewHint:
          'The value of an equivalent asset WHEN NEW — not the contract’s present value, and not ' +
          'the used asset’s market value. This is the number the low-value test (§5(b), §B3-B8) reads.',
        classificationOverride: 'Override the classification',
        classificationOverrideAssessed: 'Keep the assessed outcome',
        classificationOverrideHint:
          '§5 is a lessee policy election, so the override is accepted — but an exemption with no ' +
          'recorded reason is indistinguishable from an omission.',
        exemptionRationale: 'Exemption rationale',
        exemptionRationalePlaceholder: 'Why this contract is exempt from recognition.',
        notes: 'Notes',
      },
      panel: {
        incomplete: 'Fill in term, instalment, commencement date and rate to see the measurement.',
        policy: {
          selectEntity:
            'Select an entity to assess the classification — the asset policy and the ' +
            'currency used below are both read from it.',
          loading: 'Loading the entity’s asset policy…',
          loadFailed:
            'Could not load the asset policy or the entity’s books. Refresh the page and try ' +
            'again.',
          missing:
            'No asset policy exists for this entity or for the tenant, so the short-term and ' +
            'low-value thresholds are unknown. Configure the policy in Asset Settings before ' +
            'continuing.',
          currencyUnresolved:
            'The entity’s currency could not be determined from its registered accounting ' +
            'books. Check the entity’s books before continuing.',
        },
        testState: {
          PASS: 'Met',
          FAIL: 'Not met',
          NOT_EVALUATED: 'Not evaluated',
          NOT_ASSESSABLE: 'No data',
        },
        classification: {
          title: 'CPC 06 R2 §5 classification',
          outcome: 'Outcome:',
          pending: 'Awaiting term',
          overridden: 'Overridden',
          rationaleRequired:
            'Overriding into an exemption requires a rationale. The server refuses the contract ' +
            'without one (LEASE_EXEMPTION_RATIONALE_REQUIRED).',
          shortTerm: {
            label: '1. Short term — §5(a)',
            detail: 'Term of {{term}} months against the policy limit of {{max}} months.',
            purchaseOptionPresent:
              'The contract contains a purchase option, so Appendix A prevents the short-term exemption.',
            renewalIncluded:
              'A reasonably certain renewal is already inside the term, so a contract that only ' +
              'passes 12 months through that option is correctly not short-term.',
          },
          lowValue: {
            label: '2. Low value — §5(b), §B3-B8',
            detail: 'Asset when new at {{value}} against the policy limit of {{threshold}}.',
            underlyingAssetWarning:
              'This test reads the value of the UNDERLYING ASSET WHEN NEW — never the contract’s ' +
              'present value. A building is never low-value however cheap the instalment is.',
          },
          recognized: {
            label: '3. Recognition — §22',
            detail: 'No exemption applies: a right-of-use asset and a lease liability arise.',
          },
        },
        exempt: {
          title: 'Instalment expense',
          shortTermBody:
            'Short-term exemption: no right-of-use asset and no liability. §6 takes the ' +
            'instalments to profit or loss on a straight-line basis over the term.',
          lowValueBody:
            'Low-value exemption: no right-of-use asset and no liability. §6 takes the ' +
            'instalments to profit or loss on a straight-line basis over the term.',
          monthlyExpense: 'Monthly expense',
          nominalTotal: 'Nominal total over the term',
        },
        presentValue: {
          title: 'Present value — §26',
          liability: 'Lease liability',
          rouAsset: 'Right-of-use asset',
          nominalTotal: 'Nominal total over the term',
          embeddedInterest: 'Embedded interest',
          currentPortion: 'Current liability',
          nonCurrentPortion: 'Non-current liability',
          periodicRate: 'Periodic rate: {{rate}} · {{installments}} instalments.',
        },
        convention: {
          title: 'The two payment conventions produce different numbers',
          body:
            'In advance and in arrears do not reconcile, and the disagreement is invisible in the ' +
            'result: a present value looks plausible whichever convention produced it. Confirm ' +
            'which one the contract actually states.',
          inAdvance: 'In advance (§24(b))',
          inArrears: 'In arrears',
          difference: 'Difference',
        },
        entry: {
          title: 'Recognition entry',
          account: 'Account',
          debit: 'Debit',
          credit: 'Credit',
          total: 'Total',
          asymmetryTitle: 'The day-one asymmetry is correct',
          asymmetryBody:
            'The right-of-use asset exceeds the liability by {{excess}}. §26 measures the ' +
            'liability from the instalments NOT yet paid; §24(b) and §24(d) add to the asset the ' +
            'instalment paid at commencement, the initial direct costs and the restoration ' +
            'provision. The entry balances because each of those three carries its own credit.',
          accountsFixtureNote:
            'The accounts shown above are indicative names only, with no code: definitive account ' +
            'resolution is server-side and the preview endpoint does not exist yet. The amounts ' +
            'come from the measurement engine.',
        },
        pfLessor: {
          title: 'Individual lessor',
          body:
            'Rent paid to an individual carries IRRF withheld through the accounts-payable ' +
            'IRRF_RETIDO_ALUGUEIS rail. Confirm this client’s slot-to-account mapping is in place ' +
            'before posting the instalments. The withholding itself is handled by the AP tax ' +
            'rails, outside this module.',
        },
      },
      cutover: {
        noticeTitle: 'Use the balances closed in the previous system',
        noticeBody:
          'The liability and accumulated amortisation are independent and required for every book. ' +
          'Alchemy creates no opening journal: these balances must already exist in the migrated ledger.',
      },
      purchaseOptionConflict: {
        title: 'The short-term exemption is not available',
        body: 'The contract contains a purchase option. Keep the recognised classification or choose another applicable assessment.',
      },
      errors: {
        LEASE_CONTRACT_TERM_INVALID: 'The lease term must be a positive whole number of months.',
        LEASE_CONTRACT_AMOUNT_INVALID:
          'One of the amounts entered is invalid. Check the payment, the initial direct costs, ' +
          'the restoration provision and the opening balances: each must be a non-negative number with at most two ' +
          'decimal places, and the payment must be greater than zero.',
        LEASE_EXEMPTION_RATIONALE_REQUIRED:
          'Overriding the classification to an exemption requires a rationale. Fill in ' +
          '“Exemption rationale” before saving (CPC 06 R2 §5).',
        ASSET_TENANT_POLICY_NOT_FOUND:
          'No asset policy exists for this entity or for the tenant, so the short-term and ' +
          'low-value thresholds are unknown. Ask for the policy to be configured, or override ' +
          'the classification manually.',
        LEASE_CONTRACT_NO_BOOKS:
          'This entity keeps no active book, so no rulebook can carry this contract. Configure ' +
          'at least one active book for the entity.',
        LEASE_CONTRACT_BOOK_NOT_FOUND:
          'One of the books supplied is not an active book of this entity. Refresh the page and ' +
          'try again.',
        LEASE_TREATMENT_RATE_UNEXPECTED:
          'A book that only expenses the payments has no liability to discount and must not ' +
          'carry a discount rate.',
        LEASE_TREATMENT_ROU_CLASS_UNEXPECTED:
          'A book that only expenses the payments recognises no right of use and must not name ' +
          'an asset class.',
        LEASE_CONTRACT_JURISDICTION_UNSUPPORTED:
          'This entity keeps a book under a framework the lease engine does not yet implement — ' +
          'only CPC 06 (R2) and IFRS 16 are supported. Talk to Alchemy before registering leases ' +
          'for this entity.',
        LEASE_TREATMENT_CONTRADICTS_CLASSIFICATION:
          'The chosen classification is an exemption: no book can recognise a right of use while ' +
          'it applies. Change the classification if the exemption does not apply.',
        LEASE_TREATMENT_CONTRADICTS_TAX_BASIS:
          'A tax (TAX_BASIS) book expenses the payments and never recognises a right of use ' +
          '(Lei 12.973/2014, arts. 47-49) — that cannot be overridden.',
        LEASE_DISCOUNT_RATE_UNRESOLVED:
          'A book that recognises a right of use has no discount rate, and the tenant policy has ' +
          'no default incremental borrowing rate. Enter the annual discount rate (CPC 06 R2 §26).',
        LEASE_DISCOUNT_RATE_INVALID:
          'The discount rate entered is not positive. Enter an annual discount rate greater than ' +
          'zero.',
        LEASE_INDEXATION_BASIS_INVALID:
          'Supply exactly one escalation basis: fixed or contractual step, OR a published ' +
          'economic index — never both, or neither. They behave differently (CPC 06 R2 ' +
          '§27(a)-(b)).',
        LEASE_CONTRACTUAL_STEP_RATE_INVALID:
          'The contractual step rate is required for this escalation basis and must be a usable ' +
          'decimal fraction greater than -100%.',
        LEASE_RENEWAL_JUDGEMENT_NOTE_REQUIRED:
          'The term includes a renewal option: record the judgement for why exercise is ' +
          'reasonably certain (CPC 06 R2 §18).',
        LEASE_PAYMENT_DUE_DAY_INVALID: 'The due day must be a day of the month between 1 and 31.',
        ENTITY_NOT_FOUND: 'The selected entity was not found. Refresh the page and try again.',
        AP_VENDOR_NOT_FOUND:
          'The selected lessor was not found as a vendor. Refresh the vendor list and try again.',
        LEASE_CONTRACT_CODE_TAKEN:
          'A lease contract with this code already exists. Choose a different code.',
        LEASE_ECONOMIC_INDEX_NOT_FOUND:
          'The selected economic index is not in the catalog. Choose another index or ask ' +
          'Alchemy to add it.',
        LEASE_CONTRACT_BAD_DATE: 'The commencement date entered is invalid.',
        LEASE_PURCHASE_OPTION_DECISION_REQUIRED:
          'State whether the contract contains a purchase option. The short-term exemption cannot be assessed without that decision.',
        LEASE_SHORT_TERM_PURCHASE_OPTION_CONFLICT:
          'A contract with a purchase option cannot use the short-term exemption. Keep recognition or review the option.',
        LEASE_OPENING_POSITIONS_REQUIRED:
          'An opening date was entered without per-book balances. Fill in the liability and accumulated amortisation for every book.',
        LEASE_OPENING_POSITION_DATE_REQUIRED:
          'Per-book opening balances require an opening balance date.',
        LEASE_OPENING_POSITION_BOOK_DUPLICATE:
          'The same book appears more than once in the opening balances. Keep one position per book.',
        LEASE_OPENING_POSITION_BOOK_MISSING:
          'An opening balance is missing for a book that recognises the lease. Fill in every displayed book.',
        LEASE_OPENING_POSITION_BOOK_NOT_RECOGNIZED:
          'A balance was supplied for a book that only expenses lease payments. Remove that position.',
        LEASE_CUTOVER_REQUIRES_RECOGNITION:
          'Opening balances only exist for a recognised lease. Remove the migration or review the classification.',
        LEASE_CUTOVER_DATE_INVALID:
          'The opening date must be after commencement and before the lease ends.',
        LEASE_CUTOVER_DATE_NOT_PERIOD_END:
          'The opening date must be the last day of its competencia, for example 2026-08-31, because amortisation runs by whole competencia.',
        LEASE_CUTOVER_DATE_IN_FUTURE:
          'The opening date cannot be in the future. A cutover records amortisation already taken and a liability already owed.',
        LEASE_CUTOVER_ACCUMULATED_AMORTIZATION_INVALID:
          'Opening accumulated amortisation must be non-negative and cannot exceed the original right-of-use amount.',
        LEASE_CUTOVER_LIABILITY_INVALID:
          'The opening liability must be positive and cannot exceed the nominal instalments still remaining.',
      },
      success: 'Lease contract created.',
      footer: {
        saveDraft: 'Save draft',
        submit: 'Create lease',
      },
    },
    detail: {
      backToList: 'Back to contracts',
      notFound: 'Contract not found.',
      sections: {
        contract: 'Contract',
        indexation: 'Indexation',
        books: 'Position per book',
        installments: 'Amortisation table',
        remeasurements: 'Remeasurements',
      },
      fields: {
        lessor: 'Lessor',
        commencement: 'Commencement',
        end: 'End',
        term: 'Term',
        termMonths: '{{count}} months',
        payment: 'Instalment',
        frequency: 'Frequency',
        paymentTiming: 'Payment timing',
        inAdvance: 'In advance',
        inArrears: 'In arrears',
        dueDay: 'Due day',
        purchaseOption: 'Purchase option',
        openingPositionAsOf: 'Opening balance date',
        renewalIncluded: 'Renewal included in the term',
        renewalNote: 'Renewal judgement',
        initialDirectCosts: 'Initial direct costs',
        restorationProvision: 'Restoration provision',
        exemptionRationale: 'Exemption rationale',
        lowValueThreshold: 'Assessed low-value threshold',
        notes: 'Notes',
      },
      paymentTimingHint:
        'On an advance contract the right of use is born larger than the liability, by exactly ' +
        'the instalment paid at commencement: §26 keeps it out of the liability and §24(b) puts ' +
        'it into the asset.',
      books: {
        recognitionMode: 'Treatment',
        discountRate: 'Discount rate',
        openingLiability: 'Opening liability',
        openingAccumulatedAmortization: 'Opening accumulated amortisation',
        rateImplicit: 'Rate implicit in the lease',
        rateIncremental: 'Incremental borrowing rate',
        liability: 'Liability',
        current: 'Current',
        nonCurrent: 'Non-current',
        rouAsset: 'Right of use',
        rouAssetCost: 'Cost',
        rouAssetNbv: 'Net book value',
        progress: '{{settled}} of {{total}} instalments settled',
      },
      installments: {
        empty: 'No instalments generated.',
        loading: 'Loading instalments',
        error: 'Could not load the instalments. Try again.',
        columns: {
          number: 'Instalment',
          dueDate: 'Due date',
          opening: 'Opening balance',
          payment: 'Payment',
          interest: 'Interest',
          principal: 'Principal',
          closing: 'Closing balance',
          portion: 'Classification',
          status: 'Status',
        },
        current: 'Current',
        nonCurrent: 'Non-current',
      },
      remeasurements: {
        empty: 'No remeasurement applied.',
        appliedRate: 'Applied adjustment',
        newPayment: 'New instalment',
        source: 'Index source',
        effectiveDate: 'Effective from',
        competency: 'Competência',
        effects: 'Effect per book',
        liabilityBefore: 'Liability before',
        liabilityAfter: 'Liability after',
        rouAdjustment: 'Right-of-use adjustment',
        profitOrLoss: 'Profit or loss',
        reason: {
          INDEX_ADJUSTMENT: 'Index adjustment',
          TERM_CHANGE: 'Term change',
          PAYMENT_CHANGE: 'Payment change',
          SCOPE_CHANGE: 'Scope change',
          DISCOUNT_RATE_CHANGE: 'Discount rate change',
          TERMINATION: 'Termination',
        },
        treatment: {
          ADJUST_ROU_ASSET: 'Adjusts the right of use',
          RECOGNIZE_IN_PROFIT_OR_LOSS: 'Goes to profit or loss',
        },
      },
      recognize: {
        action: 'Recognise lease',
        title: 'Recognise {{code}}',
        description:
          'Measures the contract at commencement and creates the right-of-use asset and the ' +
          'liability in every recognising book (CPC 06 R2 §24 and §26). Runs once, in one ' +
          'transaction — anything after this is a remeasurement, not a second recognition.',
        cutoverDescription:
          "Loads the right-of-use asset and liability from each book's opening balances without " +
          'reposting initial recognition or prior amortisation. No opening journal will be created.',
        prepaidAccount: 'Account for the instalment paid at commencement',
        prepaidAccountHint:
          'This contract pays in advance: the first instalment falls on the commencement date, ' +
          'stays out of the liability (§26) and goes into the asset (§24(b)). Name the account ' +
          'it settles against — the payable, or the bank if it was paid at signing.',
        directCostsAccount: 'Account for the initial direct costs',
        restorationAccount: 'Account for the restoration provision',
        restorationAccountHint:
          'Must be a liability account — the restoration provision is an obligation (CPC 25), ' +
          'not cash received.',
        accountPlaceholder: 'Search GL account',
        rouAssetClass: 'Right-of-use class',
        rouAssetClassHint:
          'Already recorded on the recognising books. Leave blank to keep it — a different ' +
          'class will be refused.',
        rouAssetClassRequiredHint:
          'Required: no recognising book has a class recorded yet, and recognition cannot ' +
          'proceed without one.',
        rouAssetClassPlaceholder: 'Select the right-of-use class',
        dimensions: 'Managerial dimensions',
        dimensionsHint:
          'Optional. The cost centre occupying the leased premises is born with the right-of-use ' +
          'asset and carries through every amortisation journal after it — set it here to avoid ' +
          'mapping it later.',
        costCenterPlaceholder: 'Cost centre',
        departmentPlaceholder: 'Department',
        managerialAccountPlaceholder: 'Managerial account',
        cancel: 'Cancel',
        close: 'Close',
        submit: 'Recognise',
        success: 'Lease recognised.',
        exemptOutcome: 'Exempt contract — nothing was recognised.',
        recognizedOutcome: 'Recognised in {{count}} book(s).',
        installmentsGenerated: '{{count}} instalments generated',
        errors: {
          LEASE_CONTRACT_NOT_FOUND: 'This contract was not found. Refresh the page and try again.',
          LEASE_CONTRACT_ALREADY_RECOGNIZED:
            'This contract has already left DRAFT — initial recognition runs once. A change ' +
            'after recognition is a remeasurement, not a second recognition.',
          LEASE_CONTRACT_RECOGNITION_RACED:
            'This contract was recognised by another request while this one was processing. ' +
            'Refresh the page to see the current state.',
          LEASE_CONTRACT_NO_TREATMENTS:
            'This contract has no per-book treatment configured, so no rulebook has a position ' +
            'on it. Refresh the page and try again.',
          LEASE_DISCOUNT_RATE_UNRESOLVED:
            'A book that recognises a right of use has no discount rate, and the tenant policy ' +
            'has no default incremental borrowing rate. Fix this on the contract record ' +
            '(CPC 06 R2 §26).',
          LEASE_OPENING_POSITION_BOOK_MISSING:
            'An opening position is missing for a book that recognises this lease. Fix the contract before recognition.',
          LEASE_OPENING_POSITION_BOOK_NOT_RECOGNIZED:
            'An opening position exists in a book that recognises neither an asset nor a liability. Fix the contract before recognition.',
          LEASE_OPENING_POSITION_BOOK_DUPLICATE:
            'The same book appears more than once in the opening position. Fix the contract before recognition.',
          LEASE_OPENING_POSITION_AMOUNT_INVALID:
            'An opening balance is not a valid monetary amount. Fix the book liability and accumulated amortisation.',
          LEASE_CUTOVER_DATE_INVALID:
            'The opening date must be after commencement and before the lease ends. Fix it before recognition.',
          LEASE_CUTOVER_DATE_NOT_PERIOD_END:
            'The opening date must be the last day of its competencia, for example 2026-08-31, because amortisation runs by whole competencia.',
          LEASE_CUTOVER_DATE_IN_FUTURE:
            'The opening date cannot be in the future. A cutover records amortisation already taken and a liability already owed.',
          LEASE_CUTOVER_ACCUMULATED_AMORTIZATION_INVALID:
            'Opening accumulated amortisation must be non-negative and cannot exceed the original right-of-use amount.',
          LEASE_CUTOVER_LIABILITY_INVALID:
            'The opening liability must be positive and cannot exceed the nominal instalments still remaining.',
          LEASE_EXEMPTION_RATIONALE_REQUIRED:
            'This contract is classified as an exemption but carries no rationale. Fix this on ' +
            'the contract record before recognising (CPC 06 R2 §60).',
          LEASE_ROU_ASSET_CLASS_REQUIRED:
            'A book that recognises a right of use names no asset class, and there is no safe ' +
            'default. Fix this on the contract record.',
          LEASE_ROU_ASSET_CLASS_CONFLICT:
            'The recognising books name different asset classes. One contract produces one ' +
            'right of use, so they must all point at the same class — fix this on the contract ' +
            'record.',
          FIXED_ASSET_CLASS_NOT_FOUND:
            'The asset class named for the right of use no longer exists. Fix the contract ' +
            'record before recognising.',
          FIXED_ASSET_CLASS_INACTIVE:
            'The asset class named for the right of use is not active. Fix the contract record ' +
            'or reactivate the class before recognising.',
          LEASE_ROU_ASSET_CLASS_NOT_RIGHT_OF_USE:
            'The named class is not flagged as a right-of-use class — recognising in it would ' +
            'present the lease inside owned PP&E, which CPC 06 R2 §47 forbids. Choose a ' +
            'right-of-use class.',
          ASSET_STATUS_CONCURRENT_CHANGE:
            'Someone else changed this record while you were working. Refresh the page and try again.',
          ASSET_DIMENSION_NOT_FOUND:
            'The selected cost centre, department or managerial account no longer exists. ' +
            'Refresh the page and select again.',
          ASSET_DIMENSION_WRONG_ENTITY:
            'The selected cost centre, department or managerial account belongs to another ' +
            'entity — this lease belongs to this entity, and the dimension must match.',
          LEASE_RECOGNITION_BOOK_NOT_POSTABLE:
            'One of the recognising books keeps no ledger for this entity. Talk to Alchemy ' +
            'before recognising.',
          LEASE_RECOGNITION_CURRENCY_UNSUPPORTED:
            'The contract currency differs from the functional currency of one of the books. A ' +
            'foreign-currency lease is a monetary item that would need remeasurement at each ' +
            'reporting date (CPC 02 §23), which this module does not do yet.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_REQUIRED:
            'This contract pays in advance: select the account the day-one instalment settles ' +
            'against.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_UNEXPECTED:
            'This contract pays in arrears and has nothing to settle at commencement — remove ' +
            'the selected account.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_INVALID:
            'The selected account for the advance instalment does not exist in this tenant. ' +
            'Choose another account.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_TYPE_INVALID:
            'The selected account for the advance instalment must be an asset or a liability.',
          LEASE_PREPAID_PAYMENT_ACCOUNT_NATURE_INVALID:
            'The account for the advance instalment has the wrong natural balance: an asset ' +
            'account must be debit-nature, a liability account credit-nature, and neither can ' +
            'be a contra-account.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_REQUIRED:
            'This contract carries initial direct costs: select the account they were credited ' +
            'against.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_UNEXPECTED:
            'This contract carries no initial direct costs — remove the selected account.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_INVALID:
            'The selected account for the initial direct costs does not exist in this tenant. ' +
            'Choose another account.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_TYPE_INVALID:
            'The selected account for the initial direct costs must be an asset or a liability.',
          LEASE_INITIAL_DIRECT_COSTS_ACCOUNT_NATURE_INVALID:
            'The account for the initial direct costs has the wrong natural balance: an asset ' +
            'account must be debit-nature, a liability account credit-nature, and neither can ' +
            'be a contra-account.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_REQUIRED:
            'This contract carries a restoration provision: select the liability account that ' +
            'carries it.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_UNEXPECTED:
            'This contract carries no restoration provision — remove the selected account.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_INVALID:
            'The selected account for the restoration provision does not exist in this tenant. ' +
            'Choose another account.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_TYPE_INVALID:
            'The selected account for the restoration provision must be a liability — the ' +
            'provision is an obligation (CPC 25), not cash received.',
          LEASE_RESTORATION_PROVISION_ACCOUNT_NATURE_INVALID:
            'The restoration provision account must be a credit-nature liability and cannot be ' +
            'a contra-account.',
          LEASE_LIABILITY_ACCOUNT_UNRESOLVED:
            'The chart of accounts has no account mapped for the lease liability (system slot ' +
            'not configured). Ask for the account mapping to be configured before recognising.',
          LEASE_LIABILITY_ACCOUNT_TYPE_INVALID:
            'The account configured for the lease liability is not a credit-nature LIABILITY. ' +
            'The liability is the obligation to make the remaining payments. Fix the account ' +
            'mapping before recognising.',
          LEASE_LIABILITY_ACCOUNTS_NOT_SEGREGATED:
            'The current and non-current lease liability are mapped to the same account. ' +
            'CPC 26 §66-70 requires that split in the presentation — ask for different accounts ' +
            'to be configured before recognising.',
          LEASE_INTEREST_EXPENSE_ACCOUNT_UNRESOLVED:
            'The chart of accounts has no account mapped for the lease finance expense (system ' +
            'slot not configured). Ask for the account mapping to be configured before ' +
            'recognising.',
          LEASE_INTEREST_EXPENSE_ACCOUNT_TYPE_INVALID:
            'The account mapped for the lease finance expense is not a debit-nature income ' +
            'statement account. Ask for the account mapping to be corrected before recognising.',
          LEASE_MEASUREMENT_DISCOUNT_RATE_INVALID:
            'The discount rate stored for one of the books is not usable. CPC 06 R2 §26 requires ' +
            'the rate implicit in the lease or the incremental borrowing rate, as a positive ' +
            'decimal fraction. Fix the rate on the contract record.',
          LEASE_MEASUREMENT_DIRECT_COSTS_INVALID:
            'The initial direct costs stored on the contract are not valid. Fix them on the ' +
            'contract record.',
          LEASE_MEASUREMENT_RESTORATION_INVALID:
            'The restoration provision stored on the contract is not valid. Fix it on the ' +
            'contract record.',
          LEASE_MEASUREMENT_BAD_DATE:
            'The commencement date stored on the contract is invalid. Fix it on the contract ' +
            'record.',
          LEASE_MEASUREMENT_FREQUENCY_INVALID:
            'The payment frequency stored on the contract is not supported. Fix it on the ' +
            'contract record.',
          LEASE_MEASUREMENT_TERM_INVALID:
            'The term stored on the contract is not a positive whole number of months. Fix it ' +
            'on the contract record.',
          LEASE_MEASUREMENT_TERM_NOT_WHOLE_PERIODS:
            'The contract term is not a whole number of payment periods — partial final periods ' +
            'are not supported. Fix the term on the contract record.',
          LEASE_MEASUREMENT_PAYMENT_INVALID:
            'The instalment amount stored on the contract is not valid. Fix it on the contract ' +
            'record.',
          LEASE_MEASUREMENT_STEP_RATE_INVALID:
            'The contractual step rate stored on the contract is not a usable decimal fraction. ' +
            'Fix it on the contract record.',
          LEASE_MEASUREMENT_PAYMENT_DUE_DAY_INVALID:
            'The due day stored on the contract is not a valid day of the month. Fix it on the ' +
            'contract record.',
          NO_OPEN_PERIOD:
            'The accounting period for the lease commencement date is closed in some book. ' +
            'Reopen the period before recognising, or talk to whoever closed it.',
        },
      },
      settlement: {
        errors: {
          LEASE_CONTRACT_NOT_ACTIVE:
            'Only an active contract has a liability to settle: a draft one has not been ' +
            'recognised yet, and a terminated one has nothing left to pay.',
          LEASE_INSTALLMENT_NOT_PENDING:
            'This contract has no pending instalment with that number. It is either already ' +
            'settled, cancelled by a remeasurement, computed in a memo book only, or outside the ' +
            'term.',
          LEASE_SETTLEMENT_ALREADY_LINKED:
            'This instalment is already linked to another bill. Re-pointing it would leave that ' +
            'bill to post as ordinary rent expense while this one settles the liability, ' +
            'recognising the same rent twice. Unlink it first.',
          LEASE_SETTLEMENT_AMOUNT_MISMATCH:
            'The amount classified on the bill line does not equal the instalment payment. The ' +
            'bill line stays gross — it is the IRRF base — so it must equal the contractual ' +
            'payment exactly. If the rent itself changed, that is a remeasurement (CPC 06 R2 ' +
            '§42), not a settlement of a different amount.',
          LEASE_SETTLEMENT_BILL_ALREADY_POSTED:
            'This bill is already posted, and its debit landed on the account the classification ' +
            'named. The settlement redirects that debit as the bill posts, so it cannot be ' +
            'attached afterwards. Reverse the bill, link the instalment, and post it again.',
          LEASE_SETTLEMENT_BILL_ALREADY_SETTLES_ANOTHER_INSTALLMENT:
            'This bill already settles another instalment in one of these books. One bill ' +
            'settles one instalment per book: the settlement rides the bill’s single journal per ' +
            'book, and two instalments would need two posting rows against it. Use one bill per ' +
            'instalment.',
          LEASE_SETTLEMENT_BILL_LEVEL_AMBIGUOUS:
            'The bill is classified at bill level, and that one classification covers more than ' +
            'one line. The settlement would redirect the whole bill onto the lease liability, not ' +
            'just the rent line. Classify the bill per line, then link the rent line.',
          LEASE_SETTLEMENT_BILL_LINE_ALREADY_LINKED:
            'This bill line already settles another instalment. One gross line settles one ' +
            'instalment: linking it twice would split the same payment across two amortisation ' +
            'rows and settle one of them with money that was never paid.',
          LEASE_SETTLEMENT_BILL_LINE_NOT_CLASSIFIED:
            'The bill line has no classification. The settlement redirects the debit the ' +
            'classification produces, so there has to be one before the link means anything.',
          LEASE_SETTLEMENT_BILL_LINE_NOT_FOUND:
            'The selected line does not belong to this bill. Refresh the page and pick a line ' +
            'from the right bill.',
          LEASE_SETTLEMENT_BILL_WRONG_ENTITY:
            'The bill and the lease contract belong to different entities. The liability and the ' +
            'payment that settles it belong to the same entity, or the debit would cross a legal ' +
            'entity boundary.',
          LEASE_SETTLEMENT_BILL_WRONG_VENDOR:
            'The bill is payable to a vendor other than the lessor on the contract. A payment to ' +
            'somebody else does not discharge this lease liability.',
          LEASE_SETTLEMENT_CLASSIFICATION_MISSING:
            'The instalment is linked to a line of this bill, but that line no longer carries a ' +
            'classification. Posting now would debit rent expense and leave the lease liability ' +
            'standing, recognising the same rent twice. Re-classify the line, or unlink the ' +
            'instalment if this bill is not its payment.',
          LEASE_SETTLEMENT_COMPETENCY_MISMATCH:
            'The instalment and the bill are in different competências. The settlement posts in ' +
            'the bill’s own journal, so a different month would recognise this period’s finance ' +
            'expense in another one.',
          LEASE_SETTLEMENT_LINK_RACED:
            'This instalment was linked or settled by another request while this one was ' +
            'validating it. The attempt was rolled back rather than overwriting the link that ' +
            'won. Refresh the page and try again.',
          LEASE_SETTLEMENT_NOT_LINKED:
            'This instalment is not linked to a bill, so there is no link to undo.',
        },
      },
      reclassification: {
        errors: {
          LEASE_LIABILITY_RECLASSIFICATION_DATE_REGRESSIVE:
            'The reclassification of this contract cannot move back to the date given, because a ' +
            'later presentation is already recorded in the book. Reverse or supersede the later ' +
            'event before changing the reference date.',
          LEASE_LIABILITY_RECLASSIFICATION_RACED:
            'This contract was reclassified by another request while this one was measuring it. ' +
            'The attempt was rolled back rather than posting an out-of-date current/non-current ' +
            'presentation. Try again.',
          NO_OPEN_PERIOD:
            'The accounting period of the reclassification date is closed in at least one book. ' +
            'Reopen the period or choose a date inside an open period.',
          LEASE_RECLASSIFICATION_DATE_INVALID:
            'The reclassification reference date is invalid. Use a real date in YYYY-MM-DD ' +
            'format.',
        },
      },
    },
    remeasure: {
      action: 'Remeasure by index',
      title: 'Index remeasurement',
      description:
        'The index comes from the BCB SGS catalog synced daily. The observation used and its ' +
        'reference date are recorded on the event.',
      noIndex:
        'This contract has no economic index configured. Index remeasurement requires a series ' +
        'code on the contract.',
      contractIndex: 'Contract index',
      observationUsed: 'Latest observation',
      referenceDate: 'Reference date',
      value: 'Value',
      freshness: 'Freshness',
      freshnessState: {
        FRESH: 'Up to date',
        STALE: 'Behind',
        MISSING: 'No observation',
        UNKNOWN: 'Unknown',
      },
      staleWarning:
        'The series is behind its expected cadence. The remeasurement will use the last published ' +
        'observation; check it before applying.',
      missingWarning:
        'No observation synced for this series. The remeasurement will be blocked until the BCB ' +
        'SGS sync brings the data in.',
      lastSync: 'Last sync',
      neverSynced: 'Never synced',
      effectiveDate: 'Effective date',
      effectiveDateHint: 'The adjustment anniversary — the date the new instalment applies from.',
      competencyDate: 'Competência',
      competencyDateHint: 'The month the effect is posted in. Defaults to the effective month.',
      rationale: 'Rationale',
      rationalePlaceholder: 'Context for the adjustment (optional).',
      submit: 'Calculate and apply',
      cancel: 'Cancel',
      preview: 'Result',
      blocked: 'Remeasurement blocked',
      blockedReason: 'Reason: {{reason}}',
      oldPayment: 'Current instalment',
      newPayment: 'New instalment',
      adjustment: 'Adjustment',
      formula: 'Formula',
      success: 'Remeasurement applied.',
      error: 'Could not apply the remeasurement.',
      errors: {
        LEASE_INDEX_REMEASUREMENT_ALREADY_APPLIED:
          'An index remeasurement already exists for this contract on this reference date. ' +
          'Reverse or supersede the existing event before applying another adjustment.',
        LEASE_INDEX_REMEASUREMENT_NOT_APPLICABLE:
          'Only an active contract linked to the economic-index catalog can be remeasured by ' +
          'index. Activate the contract, or set its series code in the contract record.',
        LEASE_INDEX_REMEASUREMENT_RECLASSIFICATION_DATE_REGRESSIVE:
          'The remeasurement cannot take effect before a current/non-current reclassification ' +
          'already recorded in the book. Reverse or supersede that later presentation before ' +
          'recomputing the schedule.',
        LEASE_ORIGINAL_DISCOUNT_RATE_MISSING:
          'One of the books that recognises this contract has no discount rate on record. An ' +
          'index remeasurement discounts the new payments at the original rate, so without it ' +
          'the liability cannot be recomputed. Set that book’s rate in the contract record ' +
          'before remeasuring.',
        LEASE_REMEASUREMENT_RESULT_ACCOUNT_UNRESOLVED:
          'The lease remeasurement result account is not set up for this entity. Configure the ' +
          'remeasurement result system account slot (FA_LEASE_REMEASUREMENT_RESULT) and try again.',
        NO_OPEN_PERIOD:
          'The accounting period of the remeasurement date is closed in at least one book. Reopen ' +
          'the period or choose a date inside an open period.',
        LEASE_REMEASUREMENT_ROU_SCHEDULE_RACED:
          'The right-of-use amortisation schedule was changed by another operation — a revision ' +
          'or the depreciation close — while this remeasurement was running. Nothing was saved; ' +
          'run the remeasurement again.',
      },
    },
  },

  register: {
    newAssetAction: 'New asset',
    pageTitle: 'New fixed asset',
    breadcrumbRegistry: 'Fixed assets',
    saveFailed: 'The asset could not be saved',

    manualNotice: {
      title: 'Manual registration — the exception path',
      body:
        'It exists because it is sometimes necessary: an asset bought with no bill in Alchemy, ' +
        'or a base migrated from another system. It must not become the habit — an asset ' +
        'registered here is born with no document attached. If the invoice already arrived, it ' +
        'is waiting in the review queue, where the asset is created linked to its NF-e and to ' +
        'the bill that originated it.',
      link: 'Open the review queue',
    },

    entity: {
      title: 'Entity',
      hint:
        'Every asset belongs to an entity: the class rules, the capitalisation floor, and the ' +
        'chart of accounts all change with it.',
      placeholder: 'Select the entity',
    },

    origin: {
      title: 'Origin',
      hint: 'Decides whether capitalisation posts to the ledger or not.',
      NO_DOCUMENT: {
        label: 'No document',
        description:
          'An asset genuinely bought, with no bill in Alchemy. Its acquisition never reached ' +
          'the ledger, so capitalising posts Dr fixed assets / Cr whatever settled it.',
      },
      MIGRATED: {
        label: 'Migrated base',
        description:
          'An asset the company already owned at cutover. Its acquisition reached the ledger ' +
          'inside the opening balances, so capitalising posts nothing — it would debit fixed ' +
          'assets a second time. It carries its accumulated depreciation instead.',
      },
    },

    identification: {
      title: 'Identification',
      assetCode: 'Asset code',
      assetCodePlaceholder: 'Left blank, a code is reserved from the class',
      description: 'Description',
      class: 'Class',
      classPlaceholder: 'Select the class',
      classNeedsEntity: 'Select the entity first',
      classNoneMeasurable: 'No class is measured in this entity',
      classNoBookRule: 'no régua in this entity',
      classSearchPlaceholder: 'Search by name, code or description',
      classNoMatch: 'No class found.',
      classLeaseBound: 'lease term',
      classNeverDepreciates: 'Does not depreciate',
      classUsefulLife: 'Default useful life: {{months}} months',
      classGroup: {
        PROPERTY_PLANT_EQUIPMENT: 'Property, plant and equipment — CPC 27',
        RIGHT_OF_USE: 'Right of use — CPC 06 (R2)',
        INTANGIBLE: 'Intangible — CPC 04',
      },
      acquisitionDate: 'Acquisition date',
      inServiceDate: 'Available for use on',
      inServiceDateHint:
        'Depreciation starts on this date (CPC 27: when the asset is available for use). It follows the acquisition date until you change it.',
      acquisitionCost: 'Acquisition cost',
      serialNumber: 'Serial number',
      custodian: 'Custodian',
      custodianPlaceholder: 'Select the owner',
      location: 'Location',
      notes: 'Notes',
    },

    books: {
      title: 'Réguas per book',
      hint:
        'Each book measures the same asset with its own régua. Left blank, a book inherits the ' +
        'class default.',
      classDefault: 'Class default: {{months}} months · residual {{residual}} · {{norm}}',
      noClassDefault: 'Select a class to see this book’s default.',
      usefulLifeOverride: 'Useful life (months)',
      residualOverride: 'Residual value (fraction)',
    },

    temporaryDifference: {
      noBooks: 'No measurement book configured for this entity.',
      singleBook: 'A single book configured ({{book}}) — no difference to compute.',
      notAPair:
        'More than two books configured: the temporary difference is only defined for a pair.',
      incomplete: 'Enter the class, cost and acquisition date to compute the temporary difference.',
      none: 'No temporary difference will be generated.',
      monthly: 'Monthly difference',
      firstYear: 'First {{count}} instalments',
      adicao: {
        title: 'This régua generates an addition in Parte A of the e-Lalur',
        badge: 'Addition',
        explanation:
          'The tax book ({{tax}}) depreciates less than the statutory one ({{statutory}}), so ' +
          'taxable profit goes up by the difference.',
      },
      exclusao: {
        title: 'This régua generates an exclusion in Parte A of the e-Lalur',
        badge: 'Exclusion',
        explanation:
          'The tax book ({{tax}}) depreciates more than the statutory one ({{statutory}}), so ' +
          'taxable profit goes down by the difference.',
      },
      art42: {
        title: 'This régua generates a monthly addition in Parte A of the e-Lalur',
        badge: 'Origin: art. 42',
        explanation:
          'This project’s cost was already excluded from taxable profit in the competência of ' +
          'the spend (Lei 12.973, art. 42), so the tax book ({{tax}}) has no base left to ' +
          'amortise. The whole statutory amortisation ({{statutory}}) comes back as an ' +
          'addition, month by month, until the exclusion reverses.',
      },
    },

    opening: {
      title: 'Opening position',
      perBookNote:
        'Accumulated depreciation is asked per book, never once, on purpose. Repeating a single ' +
        'balance across both books zeroes the first year’s temporary difference — which is ' +
        'exactly the figure Parte A of the e-Lalur reports.',
      asOf: 'Cutover date',
      accumulated: 'Accumulated depreciation',
      remainingLife: 'Remaining useful life (months)',
    },

    counterAccount: {
      title: 'Capitalisation counter-account',
      hint:
        'The account credited with the cost: the bank that paid or the liability that owes. ' +
        'Required only on the no-document origin — a migrated asset already had its acquisition ' +
        'posted.',
      label: 'Counter-account',
      placeholder: 'Select the account',
    },

    allocation: {
      title: 'Allocation',
      hint: 'Where this asset’s depreciation will land in managerial reporting.',
      costCenter: 'Cost center',
      department: 'Department',
      managerialAccount: 'Managerial account',
      selectEntityFirst: 'Select the entity first',
    },

    check: {
      title: 'Capitalisation check',
      threshold: {
        unknown: 'Cost not entered',
        unresolved: 'Floor not configured',
        clears: 'Above the floor',
        below: 'Below the floor',
        hint: 'This entity’s capitalisation floor: {{threshold}}.',
        unresolvedHint: 'Configure this entity’s capitalisation policy before confirming.',
      },
      journal: {
        title: 'Preview journal',
        side: { DEBIT: 'D', CREDIT: 'C' },
        noPosting:
          'Nothing will be posted: a migrated asset’s acquisition already arrived in the opening ' +
          'balances.',
        hint: 'Preview. The final accounts are resolved on the server at capitalisation.',
      },
      openingAccumulated: 'Opening accumulated (posted book)',
      netBookValue: 'Net book value',
      problems: {
        title: 'Blocking problems',
        none: 'No blocking problems.',
      },
    },

    problems: {
      entityRequired: 'Select the asset’s entity.',
      classRequired: 'Select the asset’s class.',
      descriptionRequired: 'Enter the asset’s description.',
      acquisitionDateRequired: 'Enter a valid acquisition date (dd/mm/yyyy).',
      inServiceBeforeAcquisition:
        'The available-for-use date cannot be earlier than the acquisition date.',
      openingBeforeInService:
        'The cutover date cannot be earlier than the date the asset became available for use.',
      costRequired: 'Enter the acquisition cost.',
      belowThreshold:
        'The cost is below this class’s effective floor. Register the purchase as an expense.',
      policyUnresolved: 'There is no effective capitalisation policy for this entity and class.',
      currencyUnresolved:
        'This entity’s functional currency could not be determined. No active book measures ' +
        'it, or its books disagree — configure that before entering a cost.',
      classHasNoBookRule:
        'No book in this entity measures this class, so the asset would be created with no ' +
        'régua. Configure a per-book class rule before registering.',
      counterAccountRequired: 'Enter the capitalisation counter-account.',
      openingAsOfRequired: 'Enter the cutover date for the opening positions.',
      openingBookMissing: 'Enter the accumulated depreciation for every book. Missing: {{books}}.',
      openingExceedsCost:
        'Accumulated depreciation cannot exceed the acquisition cost. Review: {{books}}.',
      assetCodeRequiredForCapitalization:
        'Enter the asset code to capitalise: without it, resubmitting the form would create a ' +
        'second asset.',
    },

    errors: {
      FIXED_ASSET_PIS_COFINS_JURISDICTION_UNSUPPORTED:
        'Fixed-asset PIS/COFINS credits are available only for Brazilian entities. Review the asset entity and fiscal source.',
      FIXED_ASSET_IN_SERVICE_BEFORE_ACQUISITION:
        'The in-service date cannot be earlier than the acquisition date: an asset cannot be in ' +
        'use before it was acquired.',
      FIXED_ASSET_ACQUISITION_COST_INVALID:
        'The acquisition cost must be greater than zero. Enter the gross original cost, not the ' +
        'remaining balance.',
      FIXED_ASSET_UNIT_QUANTITY_INVALID: 'The unit quantity must be greater than zero.',
      FIXED_ASSET_CAPITALIZATION_POLICY_NOT_FOUND:
        'There is no effective capitalisation policy for this entity and class.',
      FIXED_ASSET_BELOW_CAPITALIZATION_THRESHOLD:
        'The cost is below the effective capitalisation floor. Register the purchase as an expense.',
      FIXED_ASSET_MIGRATED_COUNTER_ACCOUNT_REFUSED:
        'A migrated asset does not take a counter-account: its acquisition is already in the ' +
        'opening balance, so there is no second entry to make.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_REQUIRED:
        'Enter the counter-account to capitalise an asset with no document: nothing has posted ' +
        'yet, so the entry must credit the bank that paid or the liability still owed.',
      FIXED_ASSET_CODE_REQUIRED_FOR_CAPITALIZATION:
        'Enter the asset code to confirm the registration: without it, resubmitting the form ' +
        'would create a second asset.',
      FIXED_ASSET_OPENING_POSITION_NOT_MIGRATED:
        'An opening position only belongs to a migrated asset. An asset with no document is ' +
        'being capitalised now and has taken no depreciation yet.',
      FIXED_ASSET_MIGRATED_CUTOVER_DATE_REQUIRED:
        'Enter the cutover date to capitalise a migrated asset, even one that was never ' +
        'depreciated: without it, the only date left to measure from is the acquisition date, ' +
        'which would depreciate retroactively through competências the prior system already ' +
        'closed.',
      FIXED_ASSET_OPENING_POSITION_DATE_REQUIRED:
        'Enter the cutover date whenever an opening position is supplied: an accumulated figure ' +
        'with no date cannot be placed on a timeline.',
      FIXED_ASSET_OPENING_POSITION_IN_FUTURE:
        'The cutover date cannot be in the future: the opening position records depreciation ' +
        'that has already happened.',
      FIXED_ASSET_OPENING_POSITION_BEFORE_IN_SERVICE:
        'The cutover date is earlier than the asset’s in-service date, so the accumulated ' +
        'depreciation entered could not have been taken.',
      FIXED_ASSET_OPENING_POSITION_DUPLICATED:
        'One of the books appears more than once in the opening position. Enter each book once.',
      FIXED_ASSET_OPENING_POSITION_NEGATIVE:
        'The accumulated depreciation entered is negative. Enter a value of zero or more.',
      FIXED_ASSET_OPENING_POSITION_EXCEEDS_COST:
        'The accumulated depreciation for one of the books is greater than the acquisition ' +
        'cost, which would produce a negative net book value.',
      ENTITY_NOT_FOUND: 'The selected entity was not found. Refresh the page and try again.',
      FIXED_ASSET_NOT_FOUND: 'Asset not found. Refresh the page and try again.',
      FIXED_ASSET_NOT_DRAFT:
        'This asset is no longer a draft — it was already capitalised or changed by someone ' +
        'else. Refresh the page.',
      FIXED_ASSET_DRAFT_NOT_HAND_REGISTERED:
        'Only manually registered drafts can be edited, deleted or capitalised here. This one ' +
        'came from another source (a bill or a lease).',
      FIXED_ASSET_PROMOTE_OPENING_POSITIONS_REQUIRED:
        'This migrated asset has no saved opening position. Edit the draft and enter the ' +
        'accumulated depreciation up to the cutover in each book — 0 if it was never depreciated.',
      FIXED_ASSET_DRAFT_BOOK_RULES_STALE:
        'The books that measure the entity changed since this draft was saved. Edit and save the ' +
        'draft to rebuild its depreciation rules before capitalising.',
      FIXED_ASSET_DRAFT_CURRENCY_STALE:
        "The entity's books now measure in a different currency from this draft's. Delete the " +
        'draft and register the asset again in the currency of the books.',
      FIXED_ASSET_CODE_TAKEN:
        'An asset is already registered with this code. If it is the same asset, it is already ' +
        'registered; if it is a different one, choose a different code.',
      FIXED_ASSET_REGISTRATION_FAILED:
        'The asset could not be registered. Try again, or contact Alchemy if the problem persists.',
      FIXED_ASSET_BOOK_OVERRIDE_BOOK_NOT_MEASURED:
        'One of the books with an overridden régua does not measure this asset. Only books ' +
        'whose class rule is in scope for the entity can be adjusted.',
      FIXED_ASSET_BOOK_OVERRIDE_LIFE_INVALID:
        'The overridden useful life must be at least one month.',
      FIXED_ASSET_BOOK_OVERRIDE_RESIDUAL_INVALID:
        'The overridden residual value must be a fraction between 0 and 1 (exclusive). A ' +
        'residual equal to the whole cost would depreciate nothing.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_NOT_FOUND:
        'The counter-account entered does not exist in this tenant.',
      ASSET_CAPITALIZATION_CLASSIFICATION_MULTIPLE_CLASSES:
        'This classification capitalises assets of more than one class. The whole debit would ' +
        'reach the first class cost account. Classify the bill per line so each class reaches ' +
        'its own account.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_TYPE_INVALID:
        'The counter-account must be active, postable, not a contra account, and either ASSET ' +
        '(the bank or cash that paid) or LIABILITY (what is still owed).',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_WRONG_ENTITY:
        'The counter-account belongs to a chart of accounts this entity does not use. Select an ' +
        'account from this entity’s own chart.',
      FIXED_ASSET_CAPITALIZATION_COUNTER_ACCOUNT_IS_SUBLEDGER:
        'The counter-account belongs to a vendor’s or customer’s subledger. Register the ' +
        'invoice as an accounts-payable bill instead, or credit a cash or bank account.',
      FIXED_ASSET_DATE_INVALID: 'One of the dates entered is not valid.',
      FIXED_ASSET_AMOUNT_INVALID: 'One of the amounts entered is not a valid decimal number.',
      FIXED_ASSET_OPENING_POSITION_BOOK_MISSING:
        'Enter the accumulated depreciation for every book that measures this asset. Zero is a ' +
        'valid answer, but it has to be stated — repeating one figure across books would ' +
        'destroy the temporary difference Parte A of the e-Lalur reports.',
      FIXED_ASSET_OPENING_POSITION_BOOK_NOT_MEASURED:
        'One of the books named in the opening position does not measure this asset. Only books ' +
        'whose class rule is in scope for the entity can carry an opening position.',
      FIXED_ASSET_OPENING_REMAINING_LIFE_INVALID:
        'The remaining useful life entered is negative. Enter a value of zero or more.',
      FIXED_ASSET_CAPITALIZATION_CURRENCY_UNSUPPORTED:
        'The asset is denominated in a currency other than the functional currency of one of the ' +
        'books that measure this entity. Configure a rule in the same functional currency, or ' +
        'bring the asset in through a path that carries the rate and its evidence, before ' +
        'capitalising.',
      FIXED_ASSET_CAPITALIZATION_EXPENSE_ACCOUNT_AMBIGUOUS:
        'The bill line is classified across more than one account, so there is no single expense ' +
        'account to reclassify the cost out of. Nothing was written. Leave the line with a ' +
        'single expense account and capitalise again, or reverse and re-enter the bill with the ' +
        'right classification.',
      FIXED_ASSET_CAPITALIZATION_NOT_DRAFT:
        'This asset is no longer a draft: its cost has already reached the ledger, and ' +
        'capitalising again would debit imobilizado a second time. Refresh the page to see the ' +
        'current state.',
      FIXED_ASSET_CAPITALIZATION_NO_OPEN_PERIOD:
        'No date on or after the one given is open in every book of this entity, so the ' +
        'capitalisation has nowhere to post. Reopen the period, or pick a date inside an open ' +
        'one.',
      ASSET_STATUS_CONCURRENT_CHANGE:
        "Someone else changed this asset's status while you were working, and the operation was " +
        'rolled back. Reload the page to see the current state and try again.',
      FIXED_ASSET_CLASS_FRAMEWORK_MISMATCH:
        "The asset class belongs to a different accounting framework than this entity's books. " +
        "Pick a class from the entity's accounting framework, or fix the class setup.",
      FIXED_ASSET_CLASS_MISSING_BOOK_RULE:
        'This asset class has no depreciation rule in the books that measure this entity. ' +
        'Without one the asset would have no tax basis and no temporary difference for Part A of ' +
        'the e-Lalur. Configure the class rule in each of those books before capitalising.',
      FIXED_ASSET_CODE_AMBIGUOUS:
        'A single asset code was supplied for more than one asset, so there is no way to say ' +
        'which one it belongs to. Register them one at a time, or leave the code blank and let ' +
        'it be reserved.',
      FIXED_ASSET_COST_ACCOUNT_UNRESOLVED:
        'This asset class points its cost at an account that is not an active, postable, ' +
        'non-contra ASSET account with a debit natural balance. Fix the class mapping — or the ' +
        'fixed-asset system-account slot — before capitalising.',
      FIXED_ASSET_COUNT_INVALID:
        'A confirmed capitalisation must create at least one asset. Enter a count greater than ' +
        'zero.',
      FIXED_ASSET_METHOD_NOT_IMPLEMENTED:
        'The class rule uses a depreciation method that is not supported yet: only straight-line ' +
        'is implemented. Change the method on the class rule before capitalising.',
      FIXED_ASSET_MIGRATED_ACCUMULATED_EXCEEDS_LEDGER:
        'The accumulated depreciation entered is larger than what the ledger holds on the class ' +
        'account up to the cutover date. A migrated asset posts nothing: the amount has to be in ' +
        'the ledger already. Check the amount, or import the missing opening balances, before ' +
        'confirming the migration.',
      FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_NATURE_INVALID:
        'The accumulated-depreciation account mapped for the class is not credit-nature. Fix ' +
        'the class mapping and try again.',
      FIXED_ASSET_ACCUMULATED_DEPRECIATION_ACCOUNT_TYPE_INVALID:
        'The accumulated-depreciation account mapped for the class is not an asset account. ' +
        'Fix the class mapping and try again.',
      FIXED_ASSET_MIGRATED_COST_EXCEEDS_LEDGER:
        'The cost entered is larger than what the ledger holds on the class account up to the ' +
        'cutover date. A migrated asset posts nothing: the amount has to be in the ledger ' +
        'already. Check the amount, or import the missing opening balances, before confirming ' +
        'the migration.',
      FIXED_ASSET_MIGRATED_LEDGER_BALANCE_ABSENT:
        'The class cost account has no posted entry in this entity up to the cutover date. A ' +
        'migrated asset is, by definition, one whose cost is already in the ledger. Import the ' +
        'entity opening balances — or fix the class account mapping — before confirming the ' +
        'migration.',
      FIXED_ASSET_MIGRATED_LEDGER_PERIOD_NOT_FOUND:
        'This entity has no accounting period containing the cutover date. Generate the ' +
        'accounting calendar covering that date before migrating assets with it.',
      FIXED_ASSET_REGISTRATION_CURRENCY_REQUIRED:
        'The books that measure this entity do not share one functional currency, so there is no ' +
        'currency to assume for the asset. State the currency explicitly — and note it will have ' +
        'to match every one of those books, because this form carries no conversion basis.',
      FIXED_ASSET_REGISTRATION_CURRENCY_UNSUPPORTED:
        'The currency given for the asset differs from the functional currency of one of the ' +
        'books that measure this entity. This form carries no rate and no FX evidence, and ' +
        'converting the cost of an asset acquired before the cutover would need the ' +
        'transaction-date rate (CPC 02 (R2) §21). Register the asset in the book’s functional ' +
        'currency, or bring it in through a path that carries an evidenced conversion basis.',
      FIXED_ASSET_REGISTRATION_NO_MEASURING_BOOK:
        'No active book measures this entity, so the asset has no functional currency and no ' +
        'rule to freeze. Configure a book that measures it before registering the asset.',
      FIXED_ASSET_IDEMPOTENCY_KEY_REQUIRED:
        'This registration was sent without an idempotency key (the Idempotency-Key header), ' +
        'so the server could not tell a resubmission from a second asset and refused the ' +
        'request. No asset was created: reload the page and register again.',
    },
    success: 'Asset {{code}} registered.',

    footer: {
      costSummary: 'Acquisition cost: {{cost}}',
      saveDraft: 'Save draft',
      createAndCapitalize: 'Create and capitalise',
    },

    edit: {
      pageTitle: 'Edit draft {{code}}',
      breadcrumb: 'Edit draft',
      save: 'Save changes',
      cancel: 'Cancel',
      saveFailed: 'The draft could not be saved',
      loadFailed: 'The draft could not be loaded',
      notEditableTitle: 'This asset cannot be edited here',
      success: 'Draft {{code}} updated.',
      notEditable:
        'Asset {{code}} is not a manually registered draft, so it cannot be edited here.',
      backToAsset: 'Back to the asset',
      loading: 'Loading the draft',
    },
  },
};
